import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { paymentTermOptions, withStoredOption } from '../utils/paymentTerms';
import Layout from './Layout';
import './TradeRecap.css';
// Bunkers Phase 1: the paired-recap banner reuses the
// ``.fef-next-step--info`` shape defined in FuelEnquiryForm.css. CSS
// imports merge into one global stylesheet at build time so this is
// safe whether or not the FE-form bundle has been loaded already.
import './FuelEnquiries/FuelEnquiryForm.css';
import withAuth from '../utils/withAuth';
import { Link } from 'react-router-dom';
import ChatWindow from './ChatWindow';
import { fetchHMSEntities } from '../services/hmsEntityService';
import { fetchOperationsManagers } from '../services/operationsManagerService';
import PortsDropdown from './PortsDropdown';
import ErrorPopup from './ErrorPopup';
import { OfferContext } from '../OfferContext';
import TradeRecapBackModal from './TradeRecapBackModal';
import DateRangePicker from './ui/DateRangePicker';
import DatePicker from './ui/DatePicker';
import InvoiceDatePicker from './ui/InvoiceDatePicker';
import { fetchTradeRecapDropdowns } from '../services/tradeRecapService';
import { fetchDeliveryDropdowns } from '../services/deliveryService';
import { useFlashMessage, DEFAULT_DURATION } from './FlashMessage';
import { recapRefusedFields, recapSaveRefusal } from '../utils/recapSaveRefusal';
import { canonicalizeAmount } from '../utils/amountFormat';
import AmountInput from './ui/AmountInput';
import { fetchTraderList, traderBoxNote } from '../utils/traderList';
import TraderOptions from './shared/TraderOptions';
import PledgeBankField, { pledgeOf, pledgeFieldsFor } from './shared/PledgeBankField';
import { debounce } from 'lodash'; // Add lodash import for debounce
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import VesselSearchAutocomplete from './VesselSearchAutocomplete';
import { matchVesselTypeId } from './FuelEnquiries/FuelEnquiryForm';
import logo from '../assets/images/IMT_logo.png';
import Footer from './ui/Footer';
import DealDocumentsPanel from './DealDocumentsPanel';
import RecapContractBlock from './Legal/RecapContractBlock';
import RecapInvoicesPanel from './RecapInvoicesPanel';
import RecapBDNsPanel from './RecapBDNsPanel';
import RecapCustomerConfirmationPanel from './RecapCustomerConfirmationPanel';
import RecordLoadProblem, { describeLoadFailure } from './ui/RecordLoadProblem';
import ListLoadFailed from './ui/ListLoadFailed';
import { goToTradeOps, useTradeOpsDesk } from '../utils/tradeOpsDesk';
import { isRestrictedCustomerTrader } from '../utils/customerTraderAccess';
import { UserContext } from '../contexts/UserContext';
import { dashboardService } from '../services/dashboardService';
// Bunkers Phase 1 (May 2026): the recap FAB opens a right-side
// slide-out that wraps DealDocumentsPanel. We reuse the same overlay
// + panel CSS that powers the delivery screen's DocumentPanel so the
// two surfaces look identical.
import './DocumentPanel.css';
import HMSFloatingActionMenu from './ui/HMSFloatingActionMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faExchangeAlt, faSave,
  faUpload, faTimes, faTruck,
} from '@fortawesome/free-solid-svg-icons';
import RecapProductSearch from './RecapProductSearch';
import DealProductLines from './DealProductLines';
import HMSButton from './ui/HMSButton';
import {
  adoptSavedIds, cargoRecapLinesForSave, lineProductLabel, missingOnLines, recapFieldsWithLines, recapLineKeys,
  recapLinesFromHeader,
} from '../utils/dealProductLines';
// Sprint D — inline "+ Create product" flow when search has no hits
import InlineProductCreator from './Stock/InlineProductCreator';
import InlineCrmCreator from './CRM/InlineCrmCreator';
import RecapPartnerSearch from './RecapPartnerSearch';
import systemDataService from '../services/systemDataService';
import { CURRENCY_OPTIONS } from '../utils/constants';
// Sprint 7A: Stock-picker autofill on the recap form (fuels only).
// On variant pick we resolve the catalogue's slug-based spec_attributes
// onto the four FuelType / FuelGrade / SulfurLimit / ISO8217Year FK ids
// and hydrate Product 1 of the FuelsSpecBlock automatically.
import { fuelEnquiryService } from '../services/fuelEnquiryService';
import {
  FUELS_VARIANT_FILTER,
  autofillSpecFromVariant,
} from '../data/mixedFuelsSpec';
// Sprint 2C: Fuels-only blocks. Mounted conditionally via
// `commodityType === 'fuels'`. Solids users never see them.
import FuelsSpecBlock, { blankProduct as blankFuelsProduct } from './TradeRecap/FuelsSpecBlock';
// Bunkers/Lubes toggle -> product catalogue category. Shared with the
// enquiry form so the two screens cannot drift on where a new lube is filed.
import { categoryForProductKind } from '../data/productTaxonomy';
import FuelsFinancialSummaryPanel from './TradeRecap/FuelsFinancialSummaryPanel';
import FuelsFundingPanel from './TradeRecap/FuelsFundingPanel';
// Sprint 7C: shared Section/Field wrappers ported from the FuelEnquiry
// form so the fuels recap cards (Deal Details, Contract & Partners,
// Commercial Terms, Deal Makers) match the enquiry/offer screens
// visually. Solids recaps keep the legacy ``trade-recap-section-card``
// layout untouched (commodityType !== 'fuels' branches in the render).
import Section from './ui/Section';
import Field from './ui/Field';
// Sprint 7C: shared header + stepper styles for the fuels recap.
// Both stylesheets are scoped under .hms-form-screen / .hms-stepper-*
// so they don't bleed into the Solids recap or other screens.
import './ui/HMSScreenHeader.css';
import HMSStepper from './ui/HMSStepper';
import { PRICE_DECIMALS, QUANTITY_DECIMALS, formatFigure } from '../utils/precision';
import PinToDeskButton from './mydesk/PinToDeskButton';
import CreatedByLine from './ui/CreatedByLine';
import HeaderNameEditor from './ui/HeaderNameEditor';

// The contract price and quantity on the recap header are stored as TEXT on
// the server, which refuses a new value that is not a number
// (offer.serializers.clean_recap_contract_figure). So the two boxes are number
// boxes whose typing this screen owns (AmountInput): a comma is read as a
// thousands separator while typing, a 7th decimal is refused at the keystroke,
// nothing is rounded when the trader clicks away (card:
// six-decimals-across-the-chain, AC3 + AC4), and the plain number is what is
// sent. The caps come from utils/precision.js, the one place the chain reads.
//
// A figure saved before the boxes were number boxes may be text that is not a
// number ("TBC"). It is shown as it is and sent back untouched until the
// trader types a number over it -- the server never refuses a value that did
// not change, so an old recap still saves. Text that did NOT come from the
// saved recap (a chat fill, say) would be refused, and the note says so.
export const ContractFigureInput = ({ value, savedValue, placeholder, ...rest }) => {
  const raw = value === null || value === undefined ? '' : String(value);
  // Read the way the server reads it: commas and spaces are thousands
  // separators ("300 000" and "1,000.5" are numbers).
  const readable = canonicalizeAmount(raw.replace(/\s/g, ''));
  const legacyText = raw.trim() !== '' && readable === '' ? raw : null;
  const isSavedText = legacyText !== null && String(savedValue ?? '').trim() === raw.trim();
  return (
    <>
      <AmountInput {...rest} value={readable} placeholder={legacyText || placeholder} />
      {legacyText && (
        <small className="field-description">
          {isSavedText
            ? `Saved as "${legacyText}", which is not a number. It stays as it is until you type a number here.`
            : `"${legacyText}" is not a number. Type the figure as a number to save it.`}
        </small>
      )}
    </>
  );
};

// The header contract quantity as a number for the tolerance checks, or NaN.
// Read like ContractFigureInput reads it, so a saved "1,000.5" is 1000.5 here
// -- parseFloat stopped at the comma and read it as 1.
const contractQuantityNumber = (value) => {
  const canon = canonicalizeAmount(String(value ?? '').replace(/\s/g, ''));
  return canon === '' ? NaN : Number(canon);
};

// Optional dates on the recap. The screen holds a blank date as '', which the
// server's date fields refuse ("Date has wrong format"), so each is sent as
// null when blank. The Save button and the autosave both build their payload
// from this one list: they used to carry a copy each, and both copies missed
// the delivery window, so every save of a recap without one was refused.
export const OPTIONAL_RECAP_DATES = [
  'sale_date', 'purchase_date', 'expected_delivery_start', 'expected_delivery_end',
];
export const blankRecapDatesAsNull = (fields) => Object.fromEntries(
  OPTIONAL_RECAP_DATES.map((name) => [name, fields[name] || null]),
);

// A refused save is worded by the one shared helper (utils/recapSaveRefusal.js);
// re-exported because this screen's checks import it from here.
export { recapSaveRefusal };

// The ONE payload the recap is saved with -- by the Save button and by the
// autosave alike. They used to build it separately and the copies drifted:
// the Save button cleaned the product lines and rolled them up into the
// recap's own quantity and price, the autosave did neither, so the autosave of
// a bunkers recap with a blank product number sent '' and was refused on every
// tick. The only differences left are the caller's: ``isAutosave`` and, for
// the Save button only, the Draft/Finalized ``status`` (an autosave never
// moves it, so it leaves the key out).
export const buildRecapSavePayload = (fields, {
  commodityType, recapType, customRecapName, units, isAutosave, status,
}) => {
  const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
    const lower = lowerValue ? `${lowerValue}${lowerUnit}` : '';
    const upper = upperValue ? `${upperValue}${upperUnit}` : '';

    if (lower && upper) {
      return `${lower} to ${upper}`;
    } else if (lower) {
      return lower;
    } else if (upper) {
      return upper;
    }
    return '';
  };

  const payload = {
    ...fields,
    commodity_type: commodityType,
    recap_type: recapType.toUpperCase(),
    current_status: fields.current_status || 'ACTIVE',
    // Only the trader's OWN text is ever stored ('' = automatic). The
    // automatic name is the server's (offer/deal_names.py), so it tracks
    // later edits to the recap instead of freezing whatever the fields said.
    recap_display_name: customRecapName || null,

    // Format tolerance fields
    sale_shipping_tolerance: formatToleranceField(
      fields.sale_shipping_tolerance_lower_value,
      fields.sale_shipping_tolerance_lower_unit,
      fields.sale_shipping_tolerance_upper_value,
      fields.sale_shipping_tolerance_upper_unit
    ),
    purchase_shipping_tolerance: formatToleranceField(
      fields.purchase_shipping_tolerance_lower_value,
      fields.purchase_shipping_tolerance_lower_unit,
      fields.purchase_shipping_tolerance_upper_value,
      fields.purchase_shipping_tolerance_upper_unit
    ),

    // Format datetime fields
    sale_deal_confirmation_date: fields.sale_deal_confirmation_date
      ? `${fields.sale_deal_confirmation_date}T00:00:00Z`
      : null,
    purchase_deal_confirmation_date: fields.purchase_deal_confirmation_date
      ? `${fields.purchase_deal_confirmation_date}T00:00:00Z`
      : null,
    recap_and_calculation_checked_on: fields.recap_and_calculation_checked_on
      ? `${fields.recap_and_calculation_checked_on}T00:00:00Z`
      : null,
    email_deal_recaps_sent_on: fields.email_deal_recaps_sent_on
      ? `${fields.email_deal_recaps_sent_on}T00:00:00Z`
      : null,
    ...blankRecapDatesAsNull(fields),
    is_autosave: isAutosave,
  };
  if (status !== undefined) payload.status = status;

  // Sprint 7B: scrub the per-product ``products`` array before send.
  //   - Drop UI-only ``stock_item_variant_display`` (DRF rejects
  //     unknown fields only when a serializer is strict, but we strip
  //     defensively + to keep the wire payload tight).
  //   - Coerce empty-string numeric fields to ``null`` so DRF accepts
  //     them on PATCH (CharField('') is fine, FK/Decimal('') is not).
  //   - Drop ``id: null`` on synthesised legacy rows so the backend
  //     CREATEs them rather than trying to UPDATE id=null.
  // Cargo (Oct 2026): a deal with several products sends its lines (two or
  // more, else none: one product lives in the header); the server works out
  // the header from them (backend/offer/product_lines.py).
  if (commodityType !== 'fuels') {
    payload.products = cargoRecapLinesForSave(payload.products);
  }
  if (commodityType === 'fuels' && Array.isArray(payload.products)) {
    payload.products = payload.products.map((row) => {
      // ``supplier_name`` is a read-only display field shipped by
      // TradeRecapProductSerializer so FuelsSpecBlock can render
      // saved suppliers that fall outside the curated dropdown.
      // Strip on save -- read-only on the serializer makes it a
      // no-op either way, but keeping the wire payload tight.
      const { stock_item_variant_display, supplier_name, ...copy } = row;
      Object.keys(copy).forEach((k) => {
        if (copy[k] === '') copy[k] = null;
      });
      if (copy.id == null) delete copy.id;
      return copy;
    });

    // Mirror products[0] specs onto parent scalars so legacy readers
    // (deals board cards, fuel_contract_shorthand property,
    // dashboards) see the head row's spec on the parent. This keeps
    // back-compat with code that hasn't been migrated to read
    // ``recap.products[0]`` yet (e.g. delivery graph, list views).
    const head = payload.products[0];
    if (head) {
      payload.fuel_type = head.fuel_type ?? null;
      payload.fuel_grade = head.fuel_grade ?? null;
      payload.sulfur_limit = head.sulfur_limit ?? null;
      payload.iso_8217_year = head.iso_8217_year ?? null;
      payload.measuring_unit = head.measuring_unit ?? null;
      payload.stock_item_variant = head.stock_item_variant ?? null;
      // Sprint 7C: per-product supplier (Sprint 7B) -> parent
      // ``supplier`` / ``supplier_credit_days`` snapshot. The
      // backend's funding_days / interest / ROCE properties read
      // these scalars; without the mirror they go stale once the
      // trader edits the per-product supplier card.
      payload.supplier = head.supplier ?? null;
      payload.supplier_credit_days = head.supplier_credit_days ?? null;
    }

    // Sprint 7C: roll per-product quantity + price up to the parent
    // leg scalars. This is the back-compat bridge for every reader
    // that still pulls ``sale_contract_quantity`` / ``buy_price_per_ton``
    // directly off the recap row (FuelsFinancialSummaryPanel,
    // partial-allocation properties on the model, deals-board
    // cards). After Sprint 7C the trader no
    // longer edits these on the Commercial Terms card; the source
    // of truth is the Products card, and we derive the parent
    // values here on every save.
    //
    //   - Quantity: simple sum of ``products[].sale_quantity``.
    //     The per-product field is named ``sale_quantity`` for
    //     historical reasons but represents "this product's leg
    //     quantity" on both sale and purchase recaps (see
    //     ``hydrateRecapProducts``).
    //   - Price: qty-weighted average. ``Σ(price_i * qty_i) /
    //     Σ(qty_i)``. Algebraically: ``avg_price * total_qty ==
    //     Σ(price_i * qty_i)`` so the dollar-value aggregations
    //     downstream (interest_amount = buy * qty * rate) stay
    //     correct.
    //   - Unit: copied from products[0].measuring_unit -> Unit.code.
    //     The parent stores a CharField unit code; the per-product
    //     field is an FK. We resolve the FK to the code via the
    //     loaded ``units`` lookup so partial-allocation strings
    //     ("100 MT of 200 MT" etc.) keep formatting cleanly.
    const products = payload.products;
    const parseNum = (v) => {
      if (v === null || v === undefined || v === '') return NaN;
      const n = parseFloat(String(v).replace(/\s/g, ''));
      return Number.isFinite(n) ? n : NaN;
    };
    const totalQty = products.reduce((acc, p) => {
      const q = parseNum(p.sale_quantity);
      return acc + (Number.isFinite(q) ? q : 0);
    }, 0);
    const weightedAvg = (priceKey, decimals = PRICE_DECIMALS) => {
      let num = 0;
      let den = 0;
      products.forEach((p) => {
        const q = parseNum(p.sale_quantity);
        const v = parseNum(p[priceKey]);
        if (Number.isFinite(q) && Number.isFinite(v) && q > 0) {
          num += v * q;
          den += q;
        }
      });
      if (den <= 0) return null;
      // Round to a price's precision (utils/precision.js) so the
      // averaged value never overflows buy_price_per_ton or the price
      // CharFields (max_length 15); trailing zeros beyond two are
      // dropped so the text columns don't store phantom decimals.
      return formatFigure(num / den, 2, decimals);
    };

    const unitCodeForId = (unitId) => {
      if (unitId == null) return null;
      const u = (units || []).find((row) => String(row.id) === String(unitId));
      return u ? (u.code || u.name || null) : null;
    };
    const headUnitCode = head ? unitCodeForId(head.measuring_unit) : null;

    // Rounded to a quantity's precision so a float sum's noise digits
    // never reach the 15-character quantity text columns.
    const totalQtyStr = totalQty > 0 ? formatFigure(totalQty, 0, QUANTITY_DECIMALS) : null;
    const avgSale = weightedAvg('sale_price');
    const avgBuy = weightedAvg('buy_price');

    if (recapType === 'Sale') {
      payload.sale_contract_quantity = totalQtyStr;
      if (headUnitCode) payload.sale_contract_quantity_unit = headUnitCode;
      if (avgSale != null) payload.sale_contract_price_value = avgSale;
    } else {
      payload.purchase_contract_quantity = totalQtyStr;
      if (headUnitCode) payload.purchase_contract_quantity_unit = headUnitCode;
      // On a purchase recap the trader-entered "price" is the buy
      // price; mirror buy_price into purchase_contract_price_value
      // so deals-board / invoicing readers stay consistent.
      if (avgBuy != null) payload.purchase_contract_price_value = avgBuy;
    }
    // ``buy_price_per_ton`` is the snapshot used by ROCE / interest
    // calcs on both legs (Sprint 4B). Always mirror it from the
    // per-product buy_price weighted average.
    if (avgBuy != null) payload.buy_price_per_ton = avgBuy;
  }

  // Bunkers/fuels recaps have no tolerance UI; the value is derived by the
  // enquiry->recap conversion. Omit the tolerance fields so the backend
  // preserves them (the legacy split-field round-trip would corrupt the
  // derived "-x/+0 MT" string and overflow the 5-char unit column).
  if (commodityType === 'fuels') {
    ['sale_shipping_tolerance', 'purchase_shipping_tolerance',
     'sale_shipping_tolerance_lower_value', 'sale_shipping_tolerance_lower_unit',
     'sale_shipping_tolerance_upper_value', 'sale_shipping_tolerance_upper_unit',
     'purchase_shipping_tolerance_lower_value', 'purchase_shipping_tolerance_lower_unit',
     'purchase_shipping_tolerance_upper_value', 'purchase_shipping_tolerance_upper_unit',
    ].forEach((k) => delete payload[k]);
  }

  return payload;
};

// Dropdown options in this file come in two shapes: plain strings
// (currencies, CV basis) and {value, label} objects (payment terms,
// incoterms). Two of the six select renderers below assumed strings and
// rendered the object straight into JSX, which React refuses -- the
// whole SOLIDS recap screen died with "Objects are not valid as a React
// child" the moment Payment Terms became a catalogue. One helper so a
// renderer can never make that assumption again.
const optionValue = (option) =>
  (option && typeof option === 'object' ? option.value : option) ?? '';
const optionLabel = (option) =>
  (option && typeof option === 'object' ? option.label : option) ?? '';

// Bunkers Phase 1: Fuels-stage human labels for the header badge.
// Mirrors ``offer.models.TradeRecap.FUELS_STAGE_CHOICES`` (backend
// source of truth). Add new stages here when the backend choice
// list grows -- the fallback in the badge renderer is the raw enum
// value so a missing key just degrades to e.g. ``contract_posting``.
const FUELS_STAGE_LABELS = {
  contract_posting: 'Contract Posting',
  active_deal: 'Active Deal',
  finalised_deal: 'Finalised Deal',
  awaiting_final_documentation: 'Awaiting Final Documentation',
  awaiting_customer_invoicing: 'Awaiting Customer Invoicing',
  awaiting_invoice_to_be_sent: 'Awaiting Invoice To Be Sent',
  payment_phase: 'Payment Phase',
  completed: 'Completed',
};

// Sprint 7B: hydrate ``formFields.products`` from a recap detail
// payload. Returns the FULL product list (Product 1 ... Product N).
//
// Three cases:
//   1. ``existingRecap.products`` has rows -> map each to UI shape (cache
//      ``stock_item_variant_display`` from the nested
//      ``stock_item_variant_data.display_name``).
//   2. ``existingRecap.commodity_type === 'fuels'`` but no children
//      (legacy single-product recap saved before Sprint 7B) -> synthesise
//      one Product 1 row from the parent inline scalars so the trader
//      sees their existing data in the new card UI.
//   3. Cargo with one product: returns ``[]`` -- one product lives in the
//      header. A cargo recap with several products (Oct 2026) has rows and
//      takes case 1; Solids never renders FuelsSpecBlock.
function hydrateRecapProducts(existingRecap) {
  if (!existingRecap) return [];
  const incoming = Array.isArray(existingRecap.products) ? existingRecap.products : [];
  if (incoming.length > 0) {
    return incoming.map((p) => {
      const data = p.stock_item_variant_data;
      let display = '';
      if (data?.display_name) {
        display = data.display_name.split(' (Sale:')[0].split(' (Purchase:')[0];
      } else if (data?.shortname) {
        display = data.shortname;
      }
      return { ...p, stock_item_variant_display: display };
    });
  }
  if (existingRecap.commodity_type !== 'fuels') return [];
  // Legacy fuels: synthesise one row from parent scalars so the trader
  // sees their saved data in the new card UI. The ``id: null`` flag
  // tells _sync_nested_products to CREATE this row on the next save.
  const head = blankFuelsProduct();
  head.fuel_type = existingRecap.fuel_type ?? null;
  head.fuel_grade = existingRecap.fuel_grade ?? null;
  head.sulfur_limit = existingRecap.sulfur_limit ?? null;
  head.sulfur_limit_other = existingRecap.sulfur_limit_other || '';
  head.iso_8217_year = existingRecap.iso_8217_year ?? null;
  head.measuring_unit = existingRecap.measuring_unit ?? null;
  head.stock_item_variant = existingRecap.stock_item_variant ?? null;
  // Best-effort scalar -> per-product price/qty mirror. Pre-Sprint-7B
  // recaps stored these on the parent (sale_contract_quantity,
  // sale_contract_price_value); Sprint 7B moves them to per-product.
  // For sale leg recaps surface sale numbers; purchase leg recaps
  // surface purchase numbers. ``recap_type`` is 'SALE' or 'PURCHASE'.
  if ((existingRecap.recap_type || '').toUpperCase() === 'PURCHASE') {
    head.sale_quantity = existingRecap.purchase_contract_quantity || '';
    head.sale_price = existingRecap.purchase_contract_price_value || '';
  } else {
    head.sale_quantity = existingRecap.sale_contract_quantity || '';
    head.sale_price = existingRecap.sale_contract_price_value || '';
  }
  head.buy_price = existingRecap.buy_price_per_ton || '';
  head.supplier = existingRecap.supplier ?? null;
  head.supplier_credit_days = existingRecap.supplier_credit_days || '';
  return [head];
}

function TradeRecap() {
  // Customer traders get the sales recap and its approved invoices, and no
  // deal documents or delivery (ClickUp Fuels flow whiteboard, 2026-09-17).
  // The server refuses both regardless; this only stops the screen offering
  // doors that answer "not found". Read from the shared user info when it has
  // loaded, otherwise fetched once -- a recap opened by direct link has no
  // Trade Ops screen behind it to have loaded it.
  // useContext, not useUserInfo: the recap renders outside the provider in its
  // own screen checks, and a missing provider must mean "fetch it", not a crash.
  const sharedUserInfo = useContext(UserContext)?.userInfo;
  const [viewer, setViewer] = useState(null);
  useEffect(() => {
    if (sharedUserInfo && 'is_customer_trader' in sharedUserInfo) {
      setViewer(sharedUserInfo);
      return undefined;
    }
    let cancelled = false;
    dashboardService.getUserInfo()
      .then((info) => { if (!cancelled) setViewer(info); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sharedUserInfo]);
  const hideDealDocsAndDelivery = isRestrictedCustomerTrader(viewer);
  const { offerId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { offerResponse } = useContext(OfferContext);
  // Trade Ops flavour. Historically driven only by ``location.state``
  // from the parent layout, which broke any navigation source that
  // didn't pass it (relationship viewer, deep link, copy-pasted URL,
  // notification action_url). Symptom: a fuels recap rendered as a
  // solids/cargo recap because the default fell through to 'solids'.
  // Fix: seed from location.state if available (preserves existing
  // happy path), then let the API response override after the recap
  // loads -- the server is the source of truth on commodity_type.
  const initialCommodityType = (
    location.state?.commodityType === 'fuels' ? 'fuels' : 'solids'
  );
  const [commodityType, setCommodityType] = useState(initialCommodityType);
  const [tradeRecap, setTradeRecap] = useState(null);
  // Row 125: why an existing recap didn't load (not found / no access / other).
  // While set, the screen shows RecordLoadProblem INSTEAD of the form, so a
  // recap that never loaded can't be edited or saved as a new one.
  const [recapLoadProblem, setRecapLoadProblem] = useState(null);
  const [recapLoadAttempt, setRecapLoadAttempt] = useState(0);
  // Derive the back-nav target from the recap's own ``commodity_type``
  // when the API has resolved, falling back to the React state. Fixes
  // a race where Back was clicked before ``setCommodityType`` had
  // propagated, sending Bunkers users to the Cargo Ops dashboard.
  // Row 127: the shared rule (record, else hint, else Back asks which desk
  // this person can open).
  const tradeOpsDesk = useTradeOpsDesk({
    recordCommodity: tradeRecap?.commodity_type,
    hintCommodity: location.state?.commodityType,
  });
  const effectiveCommodityType = tradeOpsDesk.commodity;
  const [hmsEntities, setHMSEntities] = useState([]);
  const [operationsManagers, setOperationsManagers] = useState([]);
  // Bunkers Phase 1 (May 2026): lookups feeding the new IMT + Ship
  // Details sections. ``employeesAsTraders`` is a separate state
  // from the legacy ``dropdownOptions.traders`` (which is keyed by
  // trader NAME for the deal-maker dropdowns) -- here we need
  // integer Employee FKs for the customer_trader / supplier_trader
  // FK columns. ``branches`` and ``vesselTypes`` reuse the same
  // ``fuel_enquiries`` taxonomy tables the enquiry form uses.
  // ``bunkerPorts`` reuses ``/api/offer/ports/?bunker_only=true``.
  const [branches, setBranches] = useState([]);
  const [vesselTypes, setVesselTypes] = useState([]);
  const [bunkerPorts, setBunkerPorts] = useState([]);
  const [employeesAsTraders, setEmployeesAsTraders] = useState([]);
  // Row 134: the "nobody to pick" note only speaks once the list has loaded.
  const [tradersLoaded, setTradersLoaded] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({
    traders: [],
    all_employees: [],
    countries: []
  });
  // Row 128: kept so the Deal Makers note can ask for the staff list again.
  const loadTradeRecapDropdownsRef = useRef(async () => {});
  const retryTradeRecapDropdowns = () => loadTradeRecapDropdownsRef.current();
  const [units, setUnits] = useState([]);
  const { showError, showSuccess } = useFlashMessage();
  const [recapType, setRecapType] = useState(new URLSearchParams(location.search).get('type') || 'Sale');
  const [paymentTermsPresets, setPaymentTermsPresets] = useState([]);
  // Only the TYPED recap name lives here ('' = automatic). The automatic name
  // is the server's (offer/deal_names.py), refreshed from every answer.
  const [customRecapName, setCustomRecapName] = useState('');
  const [automaticRecapName, setAutomaticRecapName] = useState('');
  // Sprint 7C: active step id for the fuels-mode sticky stepper.
  // Default to the first section so the bar lights up on mount.
  // Bunkers Phase 1 (May 2026): the fuels stepper now starts at 'hms'
  // (the first section in the new IMT / Products / Ship Details /
  // Summary layout). Solids recaps still default to 'deal-details'
  // because the scroll-spy effect overwrites this on mount with the
  // first anchored section for the active commodity.
  const [activeStep, setActiveStep] = useState('hms');
  // Hamster Builder Log on the recap side - mirrors OfferGenerator's behaviour.
  const [hamsterBuilderLog, setHamsterBuilderLog] = useState([]);
  
  // Autosave state variables
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(Date.now());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [autosaveStatus, setAutosaveStatus] = useState('idle');
  // Why the last automatic save was refused, shown in the header until a save
  // goes through. It used to fail silently, every tick, for as long as the
  // recap was open -- a refusal nobody could see is a refusal nobody fixes.
  const [autosaveProblem, setAutosaveProblem] = useState('');
  // A quantity's unit box the server refused (ledger 165: a Solids quantity
  // needs its unit), {field: message}: marked red with the server's own words
  // until a unit is picked or a save goes through.
  const [refusedUnits, setRefusedUnits] = useState({});
  const noteRefusedUnits = (error) => setRefusedUnits(Object.fromEntries(
    Object.entries(recapRefusedFields(error))
      .filter(([name]) => name.endsWith('_contract_quantity_unit'))
      .map(([name, problems]) => [name, Array.isArray(problems) ? String(problems[0]) : String(problems)]),
  ));

  // Sprint D — InlineProductCreator state for the empty-state "+ Create product"
  // path. Trade recap covers both sale and purchase legs; we don't have a
  // single "deal type" to drive the price field, so we pre-fill BOTH price
  // fields the same way handleRecapProductChange does for an existing variant.
  //
  // ``target`` records WHICH product card opened the modal. One modal serves
  // every card on the screen, so without a target the render site can only
  // hard-code a category -- which is how a Lubes line's new product got filed
  // as Oil / Gas (wrong category, wrong naming rule, wrong place in Product
  // Management). Nothing opens the creator directly: both call sites go
  // through the per-site openers below, each of which supplies its own target.
  const [productCreatorState, setProductCreatorState] = useState({
    isOpen: false,
    searchTerm: '',
    target: null,
  });

  // Which catalogue category the shared "+ Create product" modal opens on.
  // Read off the card the target points at -- never a literal at the render
  // site, and never a second copy of the kind -> category mapping (that lives
  // in productTaxonomy.categoryForProductKind, shared with the enquiry form).
  // A card with no ``product_kind`` at all keeps the commodity-type default
  // rather than guessing Lubrication.
  const categoryForCreatorTarget = (target) => {
    const commodityDefault = commodityType === 'fuels' ? 'oil_gas' : 'coal';
    if (commodityType !== 'fuels') return commodityDefault;
    if (!target || target.type !== 'fuelsProduct') return commodityDefault;
    const row = (Array.isArray(formFields.products) ? formFields.products : [])[target.idx];
    if (!row || !row.product_kind) return commodityDefault;
    return categoryForProductKind(row.product_kind);
  };

  // Per-site openers. There is deliberately no generic "open the creator"
  // helper: each call site can only reach the modal through the opener that
  // already knows which card it belongs to, so a new call site cannot forget
  // to say who it is opening for.
  const openProductCreatorForFuelsProduct = (searchTerm, target) => {
    setProductCreatorState({
      isOpen: true,
      searchTerm: searchTerm || '',
      target: target && target.type === 'fuelsProduct' ? target : null,
    });
  };

  const openProductCreatorForSolidsProduct = (searchTerm) => {
    setProductCreatorState({
      isOpen: true,
      searchTerm: searchTerm || '',
      target: { type: 'solidsProduct' },
    });
  };

  const closeProductCreator = () => {
    setProductCreatorState({ isOpen: false, searchTerm: '', target: null });
  };

  // Inline CRM partner creator state. Only enabled while authoring a NEW
  // recap (offerId === 'new' or no offerId). When editing an existing
  // recap the create CTA is hidden so historical artifacts can't mutate
  // the CRM catalog. ``targetField`` carries the {name, nameId} pair of
  // the RecapPartnerSearch instance that triggered the flow so we can
  // preselect the new profile on success.
  const [crmCreatorState, setCrmCreatorState] = useState({
    isOpen: false,
    searchTerm: '',
    targetFieldName: null,
    targetFieldNameId: null,
    role: 'seller',
  });
  const [pendingCreatedPartners, setPendingCreatedPartners] = useState([]);
  
  // Add flag to track if content was saved by autosave but still needs confirmation
  const [autoSavedNeedsConfirm, setAutoSavedNeedsConfirm] = useState(false);
  
  // Reference to store original state for potential rollback
  const [originalState, setOriginalState] = useState(null);
  const originalDataRef = useRef(null);
  
  // Reference to store debounced function
  const debouncedSaveRef = useRef(null);
  
  // Autosave configuration constants
  const AUTOSAVE_INTERVAL = 10000; // 10 seconds interval as in guide
  const AUTOSAVE_DEBOUNCE = 30000; // 30 seconds debounce as in guide
  
  const [formFields, setFormFields] = useState({
    entity: '',
    product: '',
    stock_item_variant: null, // Stock system integration
    origin: '',
    laycan: '',
    laycan_comments: '',
    // Sprint 2C: Fuels-only spec block. FK ids; null when unset. Solids
    // recaps simply ignore these — the serializer strips them on read.
    fuel_type: null,
    fuel_grade: null,
    sulfur_limit: null,
    iso_8217_year: null,
    // Sprint 6 multi-product: TradeRecapProduct child rows beyond the
    // first. Empty for single-product fuels recaps and all Solids
    // recaps. Populated from existingRecap.products on hydration.
    products: [],
    // Sale Fields
    sale_contract_number: '',
    sale_buyer: '',
    sale_buyer_crm_id: null,
    // Bunkers Key Contact (moved from the Fuel Enquiry screen). Editable
    // mirror of the SALE buyer's CRM primary contact -- write-only on the
    // backend (synced to CRM via key_contact_sync_service), seeded from
    // the read-only ``key_contact_*_value`` mirrors on load.
    key_contact_name: '',
    key_contact_email: '',
    sale_chain_in_between: '',
    sale_chain_in_between_crm_id: null,
    sale_chain_in_between_2: '',
    sale_chain_in_between_2_crm_id: null,
    sale_enduser: '',
    sale_enduser_crm_id: null,
    sale_contract_quantity: '',
    sale_contract_quantity_unit: '',
    // Replace single tolerance with upper and lower values and units
    sale_shipping_tolerance_lower_value: '',
    sale_shipping_tolerance_lower_unit: '%',
    sale_shipping_tolerance_upper_value: '',
    sale_shipping_tolerance_upper_unit: '%',
    sale_shipping_tolerance: '', // Keep for backward compatibility
    sale_incoterm: '',
    sale_delivery_point: '',
    sale_contract_price_currency: '',
    sale_contract_price_value: '',
    sale_price_formula: '',
    sale_cv_basis_option: '',
    sale_cv_basis_value: '',
    sale_payment_terms: '',
    ...pledgeFieldsFor(pledgeOf(null), 'sale_'),
    sale_commissions: '',
    sale_deal_makers: '',
    // New deal maker fields
    sale_deal_maker_1: '',
    sale_deal_maker_1_percentage: '',
    sale_deal_maker_2: '',
    sale_deal_maker_2_percentage: '',
    sale_deal_confirmation_date: '',
    sale_additional_comments: '',
    // Purchase Fields
    purchase_contract_number: '',
    purchase_producer: '',
    purchase_producer_crm_id: null,
    purchase_chain_in_between: '',
    purchase_chain_in_between_crm_id: null,
    purchase_chain_in_between_2: '',
    purchase_chain_in_between_2_crm_id: null,
    purchase_seller: '',
    purchase_seller_crm_id: null,
    purchase_contract_quantity: '',
    purchase_contract_quantity_unit: '',
    // Replace single tolerance with upper and lower values and units
    purchase_shipping_tolerance_lower_value: '',
    purchase_shipping_tolerance_lower_unit: '%',
    purchase_shipping_tolerance_upper_value: '',
    purchase_shipping_tolerance_upper_unit: '%',
    purchase_shipping_tolerance: '', // Keep for backward compatibility
    purchase_incoterm: '',
    purchase_delivery_point: '',
    purchase_contract_price_currency: '',
    purchase_contract_price_value: '',
    purchase_price_formula: '',
    purchase_price_basis_option: '',
    purchase_price_basis_value: '',
    purchase_payment_terms: '',
    ...pledgeFieldsFor(pledgeOf(null), 'purchase_'),
    purchase_commissions: '',
    purchase_deal_makers: '',
    // New deal maker fields
    purchase_deal_maker_1: '',
    purchase_deal_maker_1_percentage: '',
    purchase_deal_maker_2: '',
    purchase_deal_maker_2_percentage: '',
    purchase_deal_confirmation_date: '',
    purchase_additional_comments: '',
    // Status Fields
    recap_type: '',
    current_status: '',
  });
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  // Bunkers Phase 1 (May 2026): FAB-driven "Upload Docs" flow on the
  // fuels recap. Opens a right-side slide-out drawer that mirrors the
  // delivery screen's DocumentPanel and renders the existing
  // DealDocumentsPanel as its body.
  const [docsDrawerOpen, setDocsDrawerOpen] = useState(false);

  // Section completion tracking state
  const [sectionCompletion, setSectionCompletion] = useState({
    dealDetails: { filled: 0, total: 0 },
    contractPartners: { filled: 0, total: 0 },
    commercialTerms: { filled: 0, total: 0 },
    dealMakersComments: { filled: 0, total: 0 }
  });

  // Field groups by section for tracking completion - dynamically based on recap type
  const getSectionFields = useCallback((type) => {
    const prefix = type === 'Sale' ? 'sale' : 'purchase';
    const cvBasisOptionField = type === 'Sale' ? 'sale_cv_basis_option' : 'purchase_price_basis_option';
    const cvBasisValueField = type === 'Sale' ? 'sale_cv_basis_value' : 'purchase_price_basis_value';
    
    return {
      dealDetails: [
        'entity',
        'product',
        'origin',
        'laycan',
        'laycan_comments'
      ],
      contractPartners: [
        `${prefix}_contract_number`,
        type === 'Sale' ? `${prefix}_buyer` : `${prefix}_producer`,
        `${prefix}_chain_in_between`,
        type === 'Sale' ? `${prefix}_enduser` : `${prefix}_seller`
      ],
      commercialTerms: [
        `${prefix}_contract_quantity`,
        `${prefix}_contract_quantity_unit`,
        `${prefix}_shipping_tolerance_lower_value`,
        `${prefix}_shipping_tolerance_lower_unit`,
        `${prefix}_shipping_tolerance_upper_value`,
        `${prefix}_shipping_tolerance_upper_unit`,
        `${prefix}_incoterm`,
        `${prefix}_delivery_point`,
        `${prefix}_contract_price_value`,
        `${prefix}_contract_price_currency`,
        `${prefix}_price_formula`,
        cvBasisOptionField,
        cvBasisValueField,
        `${prefix}_payment_terms`,
        `${prefix}_commissions`,
        `${prefix}_deal_confirmation_date`
      ],
      dealMakersComments: [
        `${prefix}_deal_maker_1`,
        `${prefix}_deal_maker_1_percentage`,
        `${prefix}_deal_maker_2`,
        `${prefix}_deal_maker_2_percentage`,
        `${prefix}_additional_comments`
      ]
    };
  }, []);

  // Product options are now fetched via RecapProductSearch component (stock system)
  // Origin options are now fetched via fetchDeliveryDropdowns (API)

  // Add CV Basis options
  const cvBasisOptions = ["NCV(ARB)", "GCV(ARB)", "GCV(ADB)", "GCV(DB)"];

  // Payment terms options come from the ONE merged catalogue served by
  // /api/offer/payment_presets/ -- the day-count terms that drive invoice due
  // dates (Net 7 ... Net 90, COD, CIA, BL30 ...) PLUS the four contract
  // clauses (LC, LC 90/10%, Open Terms, CAD) that used to be a separate
  // table. Every desk picks from the same list, and the stored value is the
  // TERM CODE so the invoice can resolve it.
  //
  // Bunkers Phase 1 (May 2026): "Fixed"/"Floating" are NO LONGER payment-term
  // options -- they moved to a dedicated per-product pricing-type toggle on the
  // FuelsSpecBlock Product card (``TradeRecapProduct.pricing_type``).
  const paymentTermsOptions = useMemo(
    () => paymentTermOptions(paymentTermsPresets),
    [paymentTermsPresets]
  );

  // Add incoterm options
  const incotermOptions = ["FOB", "CFR", "CIF", "DAP"];


  // Add cleanup effect for body style
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Add isActive helper for navigation
  const isActive = (path) => {
    return location.pathname === path ? "active" : "";
  };

  // Parse tolerance string into separate lower and upper components
  const parseToleranceString = (toleranceString) => {
    if (!toleranceString) {
      return {
        lowerValue: '',
        lowerUnit: '%',
        upperValue: '',
        upperUnit: '%'
      };
    }

    // Check if tolerance has a range format like "5% to 10%" or "5min to 10max"
    if (toleranceString.includes('to')) {
      const [lower, upper] = toleranceString.split('to').map(part => part.trim());
      
      // Extract value and unit for lower
      const lowerMatch = lower.match(/^(\d*\.?\d*)(.*)$/);
      const lowerValue = lowerMatch ? lowerMatch[1] : '';
      const lowerUnit = (lowerMatch && lowerMatch[2]) ? lowerMatch[2] : '%';
      
      // Extract value and unit for upper
      const upperMatch = upper.match(/^(\d*\.?\d*)(.*)$/);
      const upperValue = upperMatch ? upperMatch[1] : '';
      const upperUnit = (upperMatch && upperMatch[2]) ? upperMatch[2] : '%';
      
      return {
        lowerValue,
        lowerUnit,
        upperValue,
        upperUnit
      };
    } else {
      // Single value - place in upper tolerance (matches OfferGenerator behavior)
      const match = toleranceString.match(/^(\d*\.?\d*)(.*)$/);
      if (match) {
        const value = match[1];
        const unit = match[2] || '%';
        return {
          lowerValue: '',
          lowerUnit: '%',
          upperValue: value,
          upperUnit: unit
        };
      }
    }
    
    // Default fallback
    return {
      lowerValue: '',
      lowerUnit: '%',
      upperValue: '',
      upperUnit: '%'
    };
  };

  const isDashboardActive = () => {
    return location.pathname.startsWith('/dashboard') ? "active" : "";
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    history.push('/login');
  };

  // Add scroll to top effect
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Calculate section completion based on formFields
  useEffect(() => {
    const sectionFields = getSectionFields(recapType);
    const newSectionCompletion = {};
    
    Object.keys(sectionFields).forEach(sectionKey => {
      const fields = sectionFields[sectionKey];
      let filled = 0;
      let total = fields.length;
      
      fields.forEach(field => {
        const value = formFields[field];
        // Check if field has a non-empty value
        if (value !== null && value !== undefined && value !== '') {
          filled++;
        }
      });
      
      newSectionCompletion[sectionKey] = { filled, total };
    });
    
    setSectionCompletion(newSectionCompletion);
  }, [formFields, recapType, getSectionFields]);

  // Sprint 7C/D: scroll-spy for the recap stepper. Watches the
  // ``hms-anchor-*`` Section anchors and updates ``activeStep`` to
  // whichever is closest to the viewport top. The fuels and solids
  // step lists differ slightly (solids has no Products step), so we
  // pick the right id set per commodity.
  useEffect(() => {
    // Bunkers Phase 1: fuels recap stepper anchors are leg-aware.
    // SALE: IMT / Products / Ship Details / Summary.
    // PURCHASE: IMT / Products / Summary / Funding. Solids unchanged.
    let ids;
    if (commodityType === 'fuels') {
      ids = recapType === 'Sale'
        ? ['hms', 'products', 'ship-details', 'summary']
        : ['hms', 'products', 'summary', 'funding'];
    } else {
      ids = ['deal-details', 'contract-partners',
             'commercial-terms', 'deal-makers'];
    }
    const handler = () => {
      let bestId = ids[0];
      let bestDist = Infinity;
      ids.forEach((id) => {
        const el = document.getElementById(`hms-anchor-${id}`);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        // Prefer the section whose top is just above the stepper bar
        // (top < 120 means it's already scrolled past the sticky pill).
        const dist = top >= 120 ? top - 120 : 120 - top;
        if (dist < bestDist) {
          bestDist = dist;
          bestId = id;
        }
      });
      setActiveStep((curr) => (curr === bestId ? curr : bestId));
    };
    handler();
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, [commodityType, recapType]);

  useEffect(() => {
    // Fetch existing Trade Recap if it exists
    setRecapLoadProblem(null);
    const fetchTradeRecap = async () => {
      try {
        if (offerId === 'new') {
          // Handle new trade recap
          const type = new URLSearchParams(location.search).get('type') || 'Sale';
          setRecapType(type);
          
          // For new recaps, set default values for the CRM dropdown fields
          setFormFields(prevFields => ({
            ...prevFields,
            recap_type: type,
            sale_contract_number: type === 'Sale' ? '' : '',
            purchase_contract_number: type === 'Purchase' ? '' : '',
            // CRM partner fields - empty by default, user selects from CRM search
            sale_buyer: '',
            sale_buyer_crm_id: null,
            sale_chain_in_between: '',
            sale_chain_in_between_crm_id: null,
            sale_chain_in_between_2: '',
            sale_chain_in_between_2_crm_id: null,
            sale_enduser: '',
            sale_enduser_crm_id: null,
            purchase_producer: '',
            purchase_producer_crm_id: null,
            purchase_chain_in_between: '',
            purchase_chain_in_between_crm_id: null,
            purchase_chain_in_between_2: '',
            purchase_chain_in_between_2_crm_id: null,
            purchase_seller: '',
            purchase_seller_crm_id: null
          }));
          return;
        }

        if (!offerId) {
          // If no offerId is provided, we're creating a new recap
          const type = new URLSearchParams(location.search).get('type') || 'Sale';
          setRecapType(type);
          
          // For new recaps, set default values for the CRM dropdown fields
          setFormFields(prevFields => ({
            ...prevFields,
            recap_type: type,
            sale_contract_number: type === 'Sale' ? '' : '',
            purchase_contract_number: type === 'Purchase' ? '' : '',
            // CRM partner fields - empty by default, user selects from CRM search
            sale_buyer: '',
            sale_buyer_crm_id: null,
            sale_chain_in_between: '',
            sale_chain_in_between_crm_id: null,
            sale_chain_in_between_2: '',
            sale_chain_in_between_2_crm_id: null,
            sale_enduser: '',
            sale_enduser_crm_id: null,
            purchase_producer: '',
            purchase_producer_crm_id: null,
            purchase_chain_in_between: '',
            purchase_chain_in_between_crm_id: null,
            purchase_chain_in_between_2: '',
            purchase_chain_in_between_2_crm_id: null,
            purchase_seller: '',
            purchase_seller_crm_id: null
          }));
          return;
        }

        // Existing logic for fetching trade recap by offer ID
        const response = await axiosInstance.get(`/api/offer/trade-recaps/${offerId}/`);
        const existingRecap = response.data;
        
        // Debug specifically for additional comments
        console.log('API Response - Additional Comments:', {
          sale_additional_comments: existingRecap.sale_additional_comments,
          purchase_additional_comments: existingRecap.purchase_additional_comments,
          apiKeys: Object.keys(existingRecap)
        });
        
        // The server is the source of truth on commodity_type. Re-sync
        // here so any navigation source (relationship viewer, deep
        // link, etc.) that didn't pass ``location.state.commodityType``
        // still renders the recap with the correct flavour. Falls back
        // to the current value when the API didn't return it.
        if (existingRecap.commodity_type
            && existingRecap.commodity_type !== commodityType) {
          setCommodityType(existingRecap.commodity_type);
        }

        setTradeRecap(existingRecap);
        
        // Set custom recap name
        setCustomRecapName(existingRecap.recap_display_name || '');
        setAutomaticRecapName(existingRecap.automatic_name);
        
        // Log the deal makers and additional comments from the response
        console.log('TradeRecap - Deal maker data loaded:', {
          sale_deal_maker_1: existingRecap.sale_deal_maker_1,
          sale_deal_maker_1_percentage: existingRecap.sale_deal_maker_1_percentage,
          sale_deal_maker_2: existingRecap.sale_deal_maker_2,
          sale_deal_maker_2_percentage: existingRecap.sale_deal_maker_2_percentage,
          sale_additional_comments: existingRecap.sale_additional_comments,
          purchase_deal_maker_1: existingRecap.purchase_deal_maker_1,
          purchase_deal_maker_1_percentage: existingRecap.purchase_deal_maker_1_percentage,
          purchase_deal_maker_2: existingRecap.purchase_deal_maker_2,
          purchase_deal_maker_2_percentage: existingRecap.purchase_deal_maker_2_percentage,
          purchase_additional_comments: existingRecap.purchase_additional_comments
        });
        
        // Set recap type based on the recap_type field
        const recapTypeFromData = existingRecap.recap_type || 'SALE';
        setRecapType(recapTypeFromData === 'SALE' ? 'Sale' : 'Purchase');

        // Parse tolerance values
        const saleToleranceParsed = parseToleranceString(existingRecap.sale_shipping_tolerance);
        const purchaseToleranceParsed = parseToleranceString(existingRecap.purchase_shipping_tolerance);
        
        setFormFields({
          entity: existingRecap.entity || '',
          product: existingRecap.product || '',
          // The deal's catalogue product as a line names it (server), for
          // "+ Add another product".
          product_label: existingRecap.product_label || '',
          stock_item_variant: existingRecap.stock_item_variant || null,
          origin: (existingRecap.origin || '').toLowerCase(), // Normalize to lowercase to match dropdown options
          laycan: existingRecap.laycan || '',
          laycan_comments: existingRecap.laycan_comments || '',
          // Bunkers Phase 1 (May 2026): the new Ship Details section
          // edits vessel_name + trade_recap_imo_number directly via
          // formFields, but those keys were never hydrated from the
          // detail payload before -- the legacy form had no input
          // bound to them, so they lived only as inline displays.
          // Load them here so the new inputs show the saved values
          // instead of rendering blank over data the conversion
          // service has already mirrored from the FuelEnquiry.
          vessel_name: existingRecap.vessel_name || '',
          trade_recap_imo_number: existingRecap.trade_recap_imo_number || '',
          // Sprint 2C: Fuels-only spec block. Solids recaps strip these
          // keys server-side, so `existingRecap.fuel_*` is undefined for
          // them — the `?? null` keeps the field's shape consistent.
          fuel_type: existingRecap.fuel_type ?? null,
          fuel_grade: existingRecap.fuel_grade ?? null,
          sulfur_limit: existingRecap.sulfur_limit ?? null,
          iso_8217_year: existingRecap.iso_8217_year ?? null,
          // Sprint 7B multi-product: ``products`` is now the FULL list of
          // TradeRecapProduct rows (Product 1 ... Product N). Pre-Sprint-7B
          // it was extras-only and Product 1 lived in parent inline
          // scalars. The new FuelsSpecBlock renders every product as an
          // identical card so we hydrate the full array here.
          //
          // Cargo: ``products`` is empty for one product (it lives in the
          // header) and holds the lines of a recap with several products
          // (Oct 2026, backend/offer/product_lines.py).
          //
          // Legacy fuels recaps (saved before Sprint 7B) may have an
          // empty ``products`` array but populated parent inline
          // scalars. ``synthesiseHeadProductFromParent`` builds a
          // single Product 1 row from those scalars so the trader sees
          // their existing data in the new card UI.
          //
          // ``stock_item_variant_display`` is the picker's UI cache —
          // strip the trailing pricing block from the serializer's
          // ``stock_item_variant_data.display_name`` so a saved
          // selection round-trips visually identical to a fresh pick.
          products: hydrateRecapProducts(existingRecap),
          // Add current_status field
          current_status: existingRecap.current_status || '',
          // Sale Fields
          // Phase 3 (bunkers_contracts): the recap Contract No. field now mirrors
          // the real C-number minted by ``legal.services.contract_service``.
          // Until a C-number exists, leave the field blank — do NOT fall back
          // to the recap's transaction_number (which the header badge already
          // shows). See feature_planning/bunkers_contracts/03_recap_field_rebind.md.
          sale_contract_number: existingRecap.sale_contract_number || '',
          // CRM partner fields with IDs
          sale_buyer: existingRecap.sale_buyer || '',
          sale_buyer_crm_id: existingRecap.sale_buyer_crm_id || null,
          // Bunkers Key Contact: seed the editable inputs from the
          // read-only mirrors derived from the SALE buyer's CRM primary
          // contact. Blank for non-fuels / non-SALE recaps (stripped
          // server-side). On save these flow back to CRM.
          key_contact_name: existingRecap.key_contact_name_value || '',
          key_contact_email: existingRecap.key_contact_email_value || '',
          sale_chain_in_between: existingRecap.sale_chain_in_between || '',
          sale_chain_in_between_crm_id: existingRecap.sale_chain_in_between_crm_id || null,
          sale_chain_in_between_2: existingRecap.sale_chain_in_between_2 || '',
          sale_chain_in_between_2_crm_id: existingRecap.sale_chain_in_between_2_crm_id || null,
          sale_enduser: existingRecap.sale_enduser || '',
          sale_enduser_crm_id: existingRecap.sale_enduser_crm_id || null,
          sale_contract_quantity: existingRecap.sale_contract_quantity || '',
          sale_contract_quantity_unit: existingRecap.sale_contract_quantity_unit || '',
          // Add parsed tolerance fields
          sale_shipping_tolerance: existingRecap.sale_shipping_tolerance || '',
          sale_shipping_tolerance_lower_value: saleToleranceParsed.lowerValue,
          sale_shipping_tolerance_lower_unit: saleToleranceParsed.lowerUnit,
          sale_shipping_tolerance_upper_value: saleToleranceParsed.upperValue,
          sale_shipping_tolerance_upper_unit: saleToleranceParsed.upperUnit,
          sale_incoterm: existingRecap.sale_incoterm || '',
          sale_delivery_point: existingRecap.sale_delivery_point || '',
          sale_contract_price_currency: existingRecap.sale_contract_price_currency || '',
          sale_contract_price_value: existingRecap.sale_contract_price_value || '',
          sale_price_formula: existingRecap.sale_price_formula || '',
          sale_cv_basis_option: existingRecap.sale_cv_basis_option || '',
          sale_cv_basis_value: existingRecap.sale_cv_basis_value || '',
          sale_payment_terms: existingRecap.sale_payment_terms || '',
          ...pledgeFieldsFor(pledgeOf(existingRecap, 'sale_'), 'sale_'),
          sale_commissions: existingRecap.sale_commissions || '',
          sale_deal_makers: existingRecap.sale_deal_makers || '',
          // Add individual deal maker fields
          sale_deal_maker_1: existingRecap.sale_deal_maker_1 || '',
          sale_deal_maker_1_percentage: existingRecap.sale_deal_maker_1_percentage || '',
          sale_deal_maker_2: existingRecap.sale_deal_maker_2 || '',
          sale_deal_maker_2_percentage: existingRecap.sale_deal_maker_2_percentage || '',
          sale_deal_confirmation_date: existingRecap.sale_deal_confirmation_date ? 
            existingRecap.sale_deal_confirmation_date.split('T')[0] : '',
          sale_additional_comments: existingRecap.sale_additional_comments || '',
          // Purchase Fields
          // Phase 3 (bunkers_contracts) rebind — see Sale field above.
          purchase_contract_number: existingRecap.purchase_contract_number || '',
          // CRM partner fields with IDs
          purchase_producer: existingRecap.purchase_producer || '',
          purchase_producer_crm_id: existingRecap.purchase_producer_crm_id || null,
          purchase_chain_in_between: existingRecap.purchase_chain_in_between || '',
          purchase_chain_in_between_crm_id: existingRecap.purchase_chain_in_between_crm_id || null,
          purchase_chain_in_between_2: existingRecap.purchase_chain_in_between_2 || '',
          purchase_chain_in_between_2_crm_id: existingRecap.purchase_chain_in_between_2_crm_id || null,
          purchase_seller: existingRecap.purchase_seller || '',
          purchase_seller_crm_id: existingRecap.purchase_seller_crm_id || null,
          purchase_contract_quantity: existingRecap.purchase_contract_quantity || '',
          purchase_contract_quantity_unit: existingRecap.purchase_contract_quantity_unit || '',
          // Add parsed tolerance fields
          purchase_shipping_tolerance: existingRecap.purchase_shipping_tolerance || '',
          purchase_shipping_tolerance_lower_value: purchaseToleranceParsed.lowerValue,
          purchase_shipping_tolerance_lower_unit: purchaseToleranceParsed.lowerUnit,
          purchase_shipping_tolerance_upper_value: purchaseToleranceParsed.upperValue,
          purchase_shipping_tolerance_upper_unit: purchaseToleranceParsed.upperUnit,
          purchase_incoterm: existingRecap.purchase_incoterm || '',
          purchase_delivery_point: existingRecap.purchase_delivery_point || '',
          purchase_contract_price_currency: existingRecap.purchase_contract_price_currency || '',
          purchase_contract_price_value: existingRecap.purchase_contract_price_value || '',
          purchase_price_formula: existingRecap.purchase_price_formula || '',
          purchase_price_basis_option: existingRecap.purchase_price_basis_option || '',
          purchase_price_basis_value: existingRecap.purchase_price_basis_value || '',
          purchase_payment_terms: existingRecap.purchase_payment_terms || '',
          ...pledgeFieldsFor(pledgeOf(existingRecap, 'purchase_'), 'purchase_'),
          purchase_commissions: existingRecap.purchase_commissions || '',
          purchase_deal_makers: existingRecap.purchase_deal_makers || '',
          // Add individual purchase deal maker fields
          purchase_deal_maker_1: existingRecap.purchase_deal_maker_1 || '',
          purchase_deal_maker_1_percentage: existingRecap.purchase_deal_maker_1_percentage || '',
          purchase_deal_maker_2: existingRecap.purchase_deal_maker_2 || '',
          purchase_deal_maker_2_percentage: existingRecap.purchase_deal_maker_2_percentage || '',
          purchase_deal_confirmation_date: existingRecap.purchase_deal_confirmation_date ? 
            existingRecap.purchase_deal_confirmation_date.split('T')[0] : '',
          purchase_additional_comments: existingRecap.purchase_additional_comments || '',
          // Bunkers FUNDING section (Phase 1, May 2026). Editable on
          // PURCHASE recaps only; load as null when missing so the
          // input renders empty rather than '0'. ``funding_days_override``
          // is the trader-authored override for the Funding Days input;
          // null means "use the derived effective_funding_days".
          funding_amount: existingRecap.funding_amount ?? null,
          interest_rate_pct: existingRecap.interest_rate_pct ?? null,
          funding_days_override: existingRecap.funding_days_override ?? null,
          // Bunkers IMT + Ship Details sections (Phase 1, May 2026).
          // FKs come back as integer ids on the serializer; cast to
          // Number() when present so `<select value={...}>`'s strict
          // string compare against `<option value={int}>` lines up.
          customer_trader: existingRecap.customer_trader ?? null,
          supplier_trader: existingRecap.supplier_trader ?? null,
          sale_date: existingRecap.sale_date
            ? existingRecap.sale_date.split('T')[0] : '',
          purchase_date: existingRecap.purchase_date
            ? existingRecap.purchase_date.split('T')[0] : '',
          imt_branch: existingRecap.imt_branch ?? null,
          hms_sell_company: existingRecap.hms_sell_company ?? null,
          // Purchase-leg IMT entity. Legacy recaps predate the column, so
          // fall back to the sell company (what the purchase leg wrongly
          // displayed before) — self-heals on the next save.
          hms_buy_company: existingRecap.hms_buy_company
            ?? existingRecap.hms_sell_company ?? null,
          vessel_type: existingRecap.vessel_type ?? null,
          port: existingRecap.port ?? null,
          country: existingRecap.country || '',
          expected_delivery_start: existingRecap.expected_delivery_start
            ? existingRecap.expected_delivery_start.split('T')[0] : '',
          expected_delivery_end: existingRecap.expected_delivery_end
            ? existingRecap.expected_delivery_end.split('T')[0] : '',
          // Status Fields
          recap_and_calculation_checked: existingRecap.recap_and_calculation_checked || false,
          email_deal_recaps_sent: existingRecap.email_deal_recaps_sent || false,
          recap_and_calculation_checked_on: existingRecap.recap_and_calculation_checked_on ? 
            existingRecap.recap_and_calculation_checked_on.split('T')[0] : '',
          email_deal_recaps_sent_on: existingRecap.email_deal_recaps_sent_on ? 
            existingRecap.email_deal_recaps_sent_on.split('T')[0] : '',
          recap_type: existingRecap.recap_type || '',
        });
      } catch (error) {
        console.error('Error fetching Trade Recap:', error);
        // Keep the answer: a refused, missing or half-loaded recap must not
        // read as "still loading", nor fall through to an empty form.
        if (offerId && offerId !== 'new') {
          setRecapLoadProblem(describeLoadFailure(error));
        }
      }
    };

    fetchTradeRecap();
  }, [offerId, location.search, recapLoadAttempt]);

  useEffect(() => {
    const loadHMSEntities = async () => {
      try {
        const entities = await fetchHMSEntities();
        console.log('Fetched IMT entities:', entities);
        setHMSEntities(entities);
      } catch (error) {
        console.error('Error fetching IMT entities:', error);
      }
    };

    const loadOperationsManagers = async () => {
      try {
        const managers = await fetchOperationsManagers();
        setOperationsManagers(managers);
      } catch (error) {
        console.error('Error fetching operations managers:', error);
      }
    };

    loadTradeRecapDropdownsRef.current = async () => {
      try {
        const options = await fetchTradeRecapDropdowns();
        // Also fetch delivery dropdowns for country options
        const deliveryOptions = await fetchDeliveryDropdowns();
        setDropdownOptions({
          ...options,
          countries: deliveryOptions.countries || []
        });
      } catch (error) {
        console.error('Error fetching trade recap dropdowns:', error);
      }
    };

    const loadUnits = async () => {
      try {
        const unitsData = await systemDataService.getUnits();
        const activeUnits = unitsData.filter(unit => unit.is_active);
        setUnits(activeUnits);
      } catch (error) {
        console.error('Error fetching units:', error);
      }
    };

    // Bunkers Phase 1 (May 2026): lookups for the new IMT + Ship
    // Details sections. Same endpoints the FuelEnquiry form uses --
    // any shape changes there propagate here for free. All four are
    // fail-soft: a 4xx/5xx leaves the state as ``[]`` and the
    // corresponding dropdowns degrade to an empty list rather than
    // crashing the whole recap render.
    const loadBunkersHMSLookups = async () => {
      try {
        const [
          branchList,
          vesselTypesList,
          portsResp,
          tradersPage,
        ] = await Promise.all([
          fuelEnquiryService.branches.list(),
          fuelEnquiryService.lookups.vesselTypes(),
          axiosInstance.get('/api/offer/ports/', { params: { bunker_only: 'true' } }),
          // The trader dropdowns are plain <select>s with no search box, so a
          // trader missing from the list cannot be reached AT ALL. The shared
          // loader pulls every page (it used to stop at 100 here).
          fetchTraderList(),
        ]);
        setBranches(Array.isArray(branchList) ? branchList : []);
        setVesselTypes(Array.isArray(vesselTypesList) ? vesselTypesList : []);
        const portsList = Array.isArray(portsResp.data)
          ? portsResp.data
          : (portsResp.data?.results || []);
        setBunkerPorts(portsList);
        setEmployeesAsTraders(tradersPage.rows);
        setTradersLoaded(true);
      } catch (e) {
        console.error('Error loading bunkers IMT lookups:', e);
      }
    };

    loadHMSEntities();
    loadOperationsManagers();
    loadTradeRecapDropdownsRef.current();
    loadUnits();
    loadBunkersHMSLookups();
  }, []);

  useEffect(() => {
    const fetchPaymentPresets = async () => {
      try {
        const response = await axiosInstance.get('/api/offer/payment_presets/');
        setPaymentTermsPresets(response.data);
      } catch (error) {
        console.error('Error fetching payment presets:', error);
      }
    };

    fetchPaymentPresets();
  }, []);

  // Update the formatDateForInput function to handle dd/mm/yyyy format
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // Handle different date formats
    let date;
    
    // Check if it's already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Handle dd/mm/yyyy format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    }
    
    // Handle date strings like "3 March 2025" or similar
    if (isNaN(Date.parse(dateString))) {
      // Try to parse date string with custom format
      const parts = dateString.split(' ');
      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        date = new Date(`${month} ${day}, ${year}`);
      } else {
        return '';
      }
    } else {
      // Parse ISO date string or other standard formats
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return ''; // Invalid date
    
    // Format as YYYY-MM-DD for HTML input
    return date.toISOString().split('T')[0];
  };

  // Add function to format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Format as dd/mm/yyyy
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // NavAPI vessel search on the Ship Details vessel name (same component
  // as the enquiry + delivery forms). Picking a result auto-fills the IMO
  // and best-effort vessel type; typing commits as free text so values
  // like "TBC" or vessels outside the AIS DB still save.
  const handleVesselSelected = (vessel) => {
    if (!vessel) return;
    setFormFields((prev) => ({
      ...prev,
      vessel_name: vessel.ship_name || prev.vessel_name,
      trade_recap_imo_number: vessel.imo || prev.trade_recap_imo_number,
      vessel_type: matchVesselTypeId(vessel.ship_type, vesselTypes) ?? prev.vessel_type,
    }));
    setHasUnsavedChanges(true);
  };

  const handleVesselNameTyped = (value) => {
    setFormFields((prev) => ({ ...prev, vessel_name: value }));
    setHasUnsavedChanges(true);
  };

  // Update the handleChange function to handle date conversions
  // An edit to what the recap saves, from a child block that writes the form
  // itself (the bunkers product cards): stores it AND marks the recap as
  // changed -- the same one "unsaved change" flag the header fields set, which
  // arms the autosave and the leave-the-page warning. The product cards used
  // to get the bare setter, so a product edit on its own armed neither and a
  // typed price was lost if the trader left without pressing Save.
  const editFormFields = useCallback((update) => {
    setFormFields(update);
    setHasUnsavedChanges(true);
  }, []);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    if (type === 'date') {
      // Convert from yyyy-mm-dd to dd/mm/yyyy for storage
      const formattedDate = value ? formatDateForDisplay(value) : '';
      setFormFields((prevFields) => ({
        ...prevFields,
        [name]: formattedDate,
      }));
    } else {
      setFormFields((prevFields) => ({
        ...prevFields,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
    
    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);
    console.log('[AUTOSAVE] hasUnsavedChanges set to true');
  };

  const PERCENTAGE_COMPLEMENT = { '100': '', '70': '30', '50': '50', '30': '70' };

  const handleDealMakerPercentageChange = (e) => {
    const { name, value } = e.target;
    const match = name.match(/^(sale|purchase)_deal_maker_(\d)_percentage$/);
    if (!match) return;

    const [, side, slot] = match;
    const otherSlot = slot === '1' ? '2' : '1';
    const otherField = `${side}_deal_maker_${otherSlot}_percentage`;
    const complement = PERCENTAGE_COMPLEMENT[value] || '';

    setFormFields((prev) => ({
      ...prev,
      [name]: value,
      [otherField]: value ? complement : '',
    }));

    setHasUnsavedChanges(true);
  };

  // Add port validation states
  const [portValidation, setPortValidation] = useState({
    sale_delivery_point: true,
    purchase_delivery_point: true
  });

  // Add handler for port validation
  const handlePortValidationChange = (field, isValid) => {
    setPortValidation(prev => ({
      ...prev,
      [field]: isValid
    }));
  };

  // Function to save autosave to sessionStorage.
  // Audit 2026-05-17: switched from localStorage so trade-recap form data
  // (contains commercial deal terms) cannot be exfiltrated by XSS on another
  // tab and dies with the tab close.
  const saveToLocalStorage = (data) => {
    try {
      const key = `autosave_traderecap_${offerId || 'new'}`;
      const dataWithTimestamp = {
        timestamp: Date.now(),
        recap_type: recapType,
        data: data
      };
      sessionStorage.setItem(key, JSON.stringify(dataWithTimestamp));
      console.log(`[AUTOSAVE] Saved to sessionStorage with key: ${key}`);
      setAutosaveStatus('localStorage');
    } catch (error) {
      console.error('[AUTOSAVE] Failed to save autosave to sessionStorage:', error);
      setAutosaveStatus('failed');
    }
  };

  // Function for server saving
  const saveToServer = async (data) => {
    try {
      // The same payload the Save button sends (buildRecapSavePayload), with
      // the autosave flag; the trader's Draft/Finalized status is left alone.
      const submissionData = buildRecapSavePayload(data, {
        commodityType, recapType, customRecapName, units, isAutosave: true,
      });

      console.log("[AUTOSAVE] Submitting data to server:", submissionData);

      let response;
      if (tradeRecap) {
        // Update existing recap
        response = await axiosInstance.put(
          `/api/offer/trade-recaps/${tradeRecap.id}/`,
          submissionData
        );
      } else if (offerId && offerId !== 'new') {
        // Handle case for existing offer but no tradeRecap yet
        response = await axiosInstance.put(
          `/api/offer/trade-recaps/${offerId}/`,
          submissionData
        );
      } else {
        // Avoid creating new recaps through autosave - just use localStorage
        throw new Error("Cannot autosave new recap to server");
      }
      
      console.log("[AUTOSAVE] Success response:", response.data);
      // Lines added since the last save take the ids the server gave them, so
      // the next save updates them instead of making new ones (a remade line
      // would take the delivery rows pinned to it with it).
      setFormFields((prev) => ({ ...prev, products: adoptSavedIds(prev.products, response.data?.products) }));
      // The automatic name follows what was just saved.
      setAutomaticRecapName(response.data.automatic_name);
      setAutosaveStatus('success');
      setAutosaveProblem('');
      setRefusedUnits({});
      return response.data;
    } catch (error) {
      console.error("[AUTOSAVE] Error saving to server:", error);
      setAutosaveStatus('failed');
      setAutosaveProblem(recapSaveRefusal(error, 'The last automatic save did not go through.'));
      noteRefusedUnits(error);
      throw error;
    }
  };

  // Core autosave function
  const performAutosave = useCallback(async () => {
    console.log('[AUTOSAVE] Autosave check', { 
      hasUnsavedChanges, 
      isDataInDatabase: !!tradeRecap, 
      dataId: offerId 
    });
    
    // Check conditions before proceeding
    if (!hasUnsavedChanges) {
      console.log('[AUTOSAVE] Skipping - No unsaved changes');
      return;
    }
    
    if (!tradeRecap) {
      console.log('[AUTOSAVE] Skipping - Data not in database yet');
      // For new forms, save to sessionStorage (audit 2026-05-17 hardening).
      try {
        const emergencyBackup = {
          formData: formFields,
          additionalValues: {
            recapType,
            customRecapName
          },
          timestamp: Date.now()
        };
        sessionStorage.setItem('formAutosave_new', JSON.stringify(emergencyBackup));
        console.log('[AUTOSAVE] Saved to sessionStorage for new form');
      } catch (error) {
        console.error('[AUTOSAVE] sessionStorage backup failed', error);
      }
      return;
    }
    
    if (!offerId) {
      console.log('[AUTOSAVE] Skipping - No data ID available');
      return;
    }
    
    // All conditions passed, proceed with autosave
    try {
      console.log('[AUTOSAVE] Starting autosave operation');
      
      // Check if ports are valid before attempting to save
      const portsValid = Object.values(portValidation).every(valid => valid);
      if (!portsValid) {
        console.log('[AUTOSAVE] Ports validation failed, using localStorage fallback');
        saveToLocalStorage(formFields);
        return;
      }
      
      // Attempt to save to server if online
      if (isOnline) {
        console.log('[AUTOSAVE] Saving changes to server');
        await saveToServer(formFields);
        
        // Update timestamp but DO NOT reset hasUnsavedChanges
        // This ensures back button confirmation still works after autosave
        // setHasUnsavedChanges(false); <- This line is commented out
        setLastSaveTime(Date.now());
        
        // Set flag to indicate content was saved but still needs confirmation for navigation
        setAutoSavedNeedsConfirm(true);
        
        // Add a flag to indicate saved state but needs confirmation for navigation
        console.log('[AUTOSAVE] Completed successfully - content saved but still needs navigation confirmation');
      } else {
        // Save to localStorage if offline
        console.log('[AUTOSAVE] Offline, saving to localStorage');
        saveToLocalStorage(formFields);
      }
    } catch (error) {
      console.error('[AUTOSAVE] Failed:', error);
      // Store emergency backup in sessionStorage (audit 2026-05-17 hardening).
      try {
        const emergencyBackup = {
          formData: formFields,
          additionalValues: {
            recapType,
            customRecapName
          },
          timestamp: Date.now()
        };
        sessionStorage.setItem(`formAutosave_${offerId || 'new'}`, JSON.stringify(emergencyBackup));
        console.log('[AUTOSAVE] Emergency backup saved to sessionStorage');
      } catch (backupError) {
        console.error('[AUTOSAVE] Failed to create emergency backup:', backupError);
      }
    }
  }, [hasUnsavedChanges, tradeRecap, offerId, formFields, recapType, customRecapName, isOnline, portValidation]);

  // Set up debounced save function
  useEffect(() => {
    debouncedSaveRef.current = debounce(performAutosave, AUTOSAVE_DEBOUNCE);
    
    // Cleanup
    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, [performAutosave, AUTOSAVE_DEBOUNCE]);

  // Add trigger effect for autosave when changes are detected
  useEffect(() => {
    if (hasUnsavedChanges && debouncedSaveRef.current) {
      console.log('[AUTOSAVE] Changes detected, triggering debounced save');
      debouncedSaveRef.current();
    }
  }, [hasUnsavedChanges]);

  // Add interval-based checking
  useEffect(() => {
    console.log('[AUTOSAVE] Setting up autosave interval');
    
    const intervalId = setInterval(() => {
      const timeSinceLastSave = Date.now() - lastSaveTime;
      console.log('[AUTOSAVE] Interval check', { 
        hasUnsavedChanges, 
        tradeRecap: !!tradeRecap, 
        offerId,
        timeSinceLastSave
      });
      
      if (hasUnsavedChanges && timeSinceLastSave >= AUTOSAVE_INTERVAL) {
        console.log('[AUTOSAVE] Interval-based autosave triggered');
        performAutosave();
      }
    }, 30000); // Check every 30 seconds
    
    return () => {
      console.log('[AUTOSAVE] Clearing autosave interval');
      clearInterval(intervalId);
    };
  }, [hasUnsavedChanges, lastSaveTime, offerId, performAutosave, AUTOSAVE_INTERVAL]);

  // Add beforeunload handler for navigation warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Add online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      console.log('[AUTOSAVE] Network back online');
      setIsOnline(true);
      
      // Optionally attempt to sync any unsaved changes
      if (hasUnsavedChanges) {
        performAutosave();
      }
    };
    
    const handleOffline = () => {
      console.log('[AUTOSAVE] Network offline, will use localStorage');
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [hasUnsavedChanges, performAutosave]);

  // Add recovery on mount
  useEffect(() => {
    const recoverAutosavedData = () => {
      if (offerId) {
        try {
          // sessionStorage (audit 2026-05-17): recovers an autosave from the
          // same tab only. Pre-migration localStorage autosaves are
          // intentionally not picked up - commercial deal data must not
          // survive a tab close.
          const savedData = sessionStorage.getItem(`formAutosave_${offerId}`);
          if (savedData) {
            const parsedData = JSON.parse(savedData);

            // Check if the backup is recent enough (within last 24 hours)
            const backupAge = Date.now() - parsedData.timestamp;
            const MAX_BACKUP_AGE = 24 * 60 * 60 * 1000; // 24 hours

            if (backupAge <= MAX_BACKUP_AGE) {
              console.log('[AUTOSAVE] Found emergency backup data. Recovering...');

              // Confirm with user before restoring
              if (window.confirm('We found unsaved changes from your previous session. Would you like to restore them?')) {
                setFormFields(parsedData.formData);

                // Restore additional values if available
                if (parsedData.additionalValues) {
                  if (parsedData.additionalValues.recapType) {
                    setRecapType(parsedData.additionalValues.recapType);
                  }
                  if (parsedData.additionalValues.customRecapName) {
                    setCustomRecapName(parsedData.additionalValues.customRecapName);
                  }
                }

                showSuccess('Successfully restored your previous unsaved changes', 3000);
              }
            }

            // Clear the backup data after recovery
            sessionStorage.removeItem(`formAutosave_${offerId}`);
          }
        } catch (error) {
          console.error('[AUTOSAVE] Error recovering autosaved data:', error);
        }
      }
    };
    
    // Only try to recover if we don't already have data
    if (!tradeRecap) {
      recoverAutosavedData();
    }
  }, [offerId, showSuccess, tradeRecap]);

  // Add rollback functionality
  const rollbackChanges = async () => {
    if (!originalDataRef.current || !offerId) {
      console.log('[AUTOSAVE] Rollback not possible - missing original data or ID');
      return false;
    }
    
    const originalData = originalDataRef.current;
    
    // Skip if still loading
    if (originalData.loading || originalData.error) {
      console.log('[AUTOSAVE] Cannot rollback - original data not fully loaded');
      return false;
    }
    
    console.log('[AUTOSAVE] Rolling back to original state');
    
    try {
      // Send original API data back to server
      if (tradeRecap && originalData.apiResponse) {
        // Send API request to restore original data. A restore, not a save:
        // a half-typed product line in the original must not stop it (only an
        // explicit save insists on whole lines).
        await axiosInstance.put(`/api/offer/trade-recaps/${offerId}/`, { ...originalData.apiResponse, is_autosave: true });
        
        // Restore UI state to match server data
        setFormFields(originalData.formData);
        
        if (originalState && originalState.additionalValues) {
          if (originalState.additionalValues.recapType) {
            setRecapType(originalState.additionalValues.recapType);
          }
          if (originalState.additionalValues.customRecapName) {
            setCustomRecapName(originalState.additionalValues.customRecapName);
          }
        }
        
        setHasUnsavedChanges(false);
        console.log('[AUTOSAVE] Successfully rolled back to original state');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[AUTOSAVE] Rollback failed:', error);
      return false;
    }
  };

  // Clean up old autosaves (runs once when component mounts).
  // Audit 2026-05-17: sweeps both sessionStorage (new home) and localStorage
  // (where pre-migration backups may still sit) to drain stale snapshots.
  useEffect(() => {
    const isAutosaveKey = (key) => (
      key.startsWith('autosave_traderecap_') || key.startsWith('formAutosave_')
    );

    const sweep = (store) => {
      Object.keys(store).forEach((key) => {
        if (!isAutosaveKey(key)) return;
        try {
          const data = JSON.parse(store.getItem(key));
          const age = Date.now() - data.timestamp;
          if (age > 7 * 24 * 60 * 60 * 1000) {
            store.removeItem(key);
            console.log(`[AUTOSAVE] Removed old autosave: ${key}`);
          }
        } catch (e) {
          store.removeItem(key);
        }
      });
    };

    sweep(sessionStorage);
    sweep(localStorage);
  }, []);

  // Modify the saveFormData function used by submit to handle both manual and automatic saves
  const saveFormData = async (finalizeData = false, isAutosave = false) => {
    try {
      // Check for port validation first, but only for manual saves
      if (!isAutosave) {
        const portsValid = Object.values(portValidation).every(valid => valid);
        if (!portsValid) {
          showError("Please select valid ports from the dropdown", DEFAULT_DURATION);
          return false;
        }
      }
      
      // Tolerance validation - only for manual saves, absolute units, and Solids.
      // Bunkers/fuels have no tolerance UI; tolerance is derived, not edited.
      if (!isAutosave && commodityType !== 'fuels') {
        const validateTolerance = (quantity, lowerValue, lowerUnit, upperValue, upperUnit, side) => {
          const quantityNum = contractQuantityNumber(quantity);
          if (Number.isNaN(quantityNum)) {
            return null; // Skip validation if no quantity is set
          }
          const errors = [];
          
          // Validate lower tolerance - ONLY for absolute units (not %)
          if (lowerValue && lowerValue !== '' && lowerUnit !== '%') {
            const lowerNum = parseFloat(lowerValue.replace(/\s/g, ''));
            if (isNaN(lowerNum)) {
              errors.push(`${side} lower tolerance must be a valid number`);
            } else if (lowerNum > quantityNum) {
              errors.push(`${side} minimum tolerance (${lowerValue}${lowerUnit}) cannot be higher than the contract quantity (${quantity})`);
            }
          }
          
          // Validate upper tolerance - ONLY for absolute units (not %)
          if (upperValue && upperValue !== '' && upperUnit !== '%') {
            const upperNum = parseFloat(upperValue.replace(/\s/g, ''));
            if (isNaN(upperNum)) {
              errors.push(`${side} upper tolerance must be a valid number`);
            } else if (upperNum < quantityNum) {
              errors.push(`${side} maximum tolerance (${upperValue}${upperUnit}) cannot be lower than the contract quantity (${quantity})`);
            }
          }
          
          return errors.length > 0 ? errors : null;
        };
        
        // Validate sale tolerance
        const saleErrors = validateTolerance(
          formFields.sale_contract_quantity,
          formFields.sale_shipping_tolerance_lower_value,
          formFields.sale_shipping_tolerance_lower_unit,
          formFields.sale_shipping_tolerance_upper_value,
          formFields.sale_shipping_tolerance_upper_unit,
          'Sale'
        );
        
        if (saleErrors) {
          showError(saleErrors.join('. '), DEFAULT_DURATION);
          return false;
        }
        
        // Validate purchase tolerance
        const purchaseErrors = validateTolerance(
          formFields.purchase_contract_quantity,
          formFields.purchase_shipping_tolerance_lower_value,
          formFields.purchase_shipping_tolerance_lower_unit,
          formFields.purchase_shipping_tolerance_upper_value,
          formFields.purchase_shipping_tolerance_upper_unit,
          'Purchase'
        );

        if (purchaseErrors) {
          showError(purchaseErrors.join('. '), DEFAULT_DURATION);
          return false;
        }

        // Recap minimum info (Jonas feedback 19.08): product, price, incoterms,
        // quantity, tolerance, laycan and delivery point are required to save.
        // Manual solids saves only — fuels recaps derive tolerance and carry
        // their own spec block, and autosaves stay lenient so work in progress
        // is never lost.
        const prefix = recapType === 'Sale' ? 'sale' : 'purchase';
        const missing = [];
        // Several products: each line needs its product, quantity and price.
        if (hasRecapLines) {
          missing.push(...missingOnLines(formFields.products, recapLineKeys(recapType), {
            priceRequired: !formFields[`${prefix}_price_formula`],
          }));
        }
        else if (!formFields.product) missing.push('Product');
        // A price is either a fixed value or a formula
        if (!hasRecapLines && !formFields[`${prefix}_contract_price_value`] && !formFields[`${prefix}_price_formula`]) {
          missing.push('Price');
        }
        if (!formFields[`${prefix}_incoterm`]) missing.push('Incoterms');
        if (!hasRecapLines && !formFields[`${prefix}_contract_quantity`]) missing.push('Quantity');
        if (
          !formFields[`${prefix}_shipping_tolerance_lower_value`] &&
          !formFields[`${prefix}_shipping_tolerance_upper_value`]
        ) {
          missing.push('Tolerance');
        }
        if (!formFields.laycan) missing.push('Laycan');
        if (!formFields[`${prefix}_delivery_point`]) missing.push('Delivery point');

        if (missing.length > 0) {
          showError(
            `Required recap information missing: ${missing.join(', ')}`,
            DEFAULT_DURATION
          );
          return false;
        }
      }
      
      const payload = buildRecapSavePayload(formFields, {
        commodityType, recapType, customRecapName, units, isAutosave,
        status: finalizeData ? 'Finalized' : 'Draft',
      });

      console.log(isAutosave ? 'Autosaving form data...' : 'Saving form data...', payload);

      let savedDataId;
      let responseData;

      if (tradeRecap) {
        // Update existing recap
        const response = await axiosInstance.put(
          `/api/offer/trade-recaps/${tradeRecap.id}/`,
          payload
        );
        savedDataId = tradeRecap.id;
        responseData = response.data;
        // New lines take their ids (see the autosave above).
        setFormFields((prev) => ({ ...prev, products: adoptSavedIds(prev.products, responseData?.products) }));
        // The automatic name follows what was just saved.
        setAutomaticRecapName(responseData.automatic_name);
        
        // Update customRecapName with the saved display name from response
        if (responseData.recap_display_name && !isAutosave) {
          setCustomRecapName(responseData.recap_display_name);
        }
        
        // Only show success message if not an autosave
        if (!isAutosave) {
          // Show success message with sync notification if applicable
          if (tradeRecap.associated_offer_or_bid) {
            showSuccess('Trade recap updated successfully. Associated deal has been updated with sync values.', 5000);
          } else {
            showSuccess('Trade recap updated successfully.', 3000);
          }
        }
      } else {
        // Create new recap -- only from the "new recap" screen. With a recap
        // number in the address the recap simply hasn't loaded, and posting
        // here would make a second, blank recap instead of saving that one.
        if (offerId && offerId !== 'new') {
          showError("This recap hasn't loaded, so it can't be saved. Reload the page and try again.", DEFAULT_DURATION);
          return finalizeData ? null : false;
        }
        const response = await axiosInstance.post(
          '/api/offer/trade-recaps/',
          payload
        );
        savedDataId = response.data.id;
        responseData = response.data;
        // The screen now holds the recap it just made: its number, Created-by
        // line and Create Contract show, and the next save updates it rather
        // than posting a second recap. The address follows, so a reload opens it.
        setTradeRecap(responseData);
        setFormFields((prev) => ({ ...prev, products: adoptSavedIds(prev.products, responseData?.products) }));
        history.replace(`/trade-recap/${savedDataId}`, location.state);
        setAutomaticRecapName(responseData.automatic_name);
        
        // Update customRecapName with the saved display name from response
        if (responseData.recap_display_name && !isAutosave) {
          setCustomRecapName(responseData.recap_display_name);
        }
        
        // Update the contract number with the generated transaction number
        if (!isAutosave) {
          // Phase 3 (bunkers_contracts): the post-save Contract No. backfill
          // that copied the recap's transaction_number into
          // sale_contract_number / purchase_contract_number has been removed.
          // C-numbers are now minted by the Contract Creator Modal flow and
          // arrive on the recap row directly. See planning doc 03.

          // Show success message with sync notification if applicable
          if (payload.associated_offer_or_bid) {
            showSuccess('New trade recap created successfully. Associated deal has been updated with sync values.', 5000);
          } else {
            showSuccess('New trade recap created successfully.', 3000);
          }
        }
      }
      
      // If this was a successful save, clear the unsaved changes flag
      if (!isAutosave) {
        setHasUnsavedChanges(false);
        setLastSaveTime(Date.now());
        setAutosaveProblem('');
      }
      setRefusedUnits({});
      
      return finalizeData ? savedDataId : responseData || true;
    } catch (error) {
      console.error('Error details:', error);
      noteRefusedUnits(error);
      
      // Only show error message if not an autosave
      if (!isAutosave) {
        showError(recapSaveRefusal(error, 'Failed to save trade recap. Please try again.'), DEFAULT_DURATION);
      }
      
      return finalizeData ? null : false;
    }
  };

  // Modify the handleSubmit function to use saveFormData
  const handleSubmit = async (e) => {
    // Bunkers Phase 1 (May 2026): the FAB Save Draft action calls this
    // without an event, so guard the form-only preventDefault.
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    try {
      const success = await saveFormData(false, false);
      
      if (success) {
        // Clear any session/localStorage backup since we've saved successfully.
        // Audit 2026-05-17: also clear pre-migration localStorage entries.
        const key = `autosave_traderecap_${offerId || 'new'}`;
        sessionStorage.removeItem(key);
        sessionStorage.removeItem(`formAutosave_${offerId || 'new'}`);
        localStorage.removeItem(key);
        localStorage.removeItem(`formAutosave_${offerId || 'new'}`);

        // Clear autosave confirmation flag after explicit save
        setAutoSavedNeedsConfirm(false);

        // Success message already shown by saveFormData function
        // Stay on the page - do NOT navigate away
      }
    } catch (error) {
      console.error("Full error object:", error);
      console.error("Error response data:", error.response?.data);
      
      // Create a readable error message
      showError(recapSaveRefusal(error, 'Failed to save trade recap. Please review your entries.'), 5000);
    }
  };

  const handleKeyDown = (e) => {
    // Only prevent form submission on Enter if not in a textarea
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  const formatQuantityValue = (value) => {
    // Return value as is - no formatting needed
    return value;
  };

  // Add validation function for tolerance input
  const validateTolerance = (value) => {
    if (!value) return true;
    return /^\d*\.?\d*$/.test(value);
  };

  // Add validation function for kcal/kg input
  const validateKcalKg = (value) => {
    if (!value) return true;
    // Only allow positive numbers with optional decimals
    return /^\d*\.?\d*$/.test(value) && (!value || parseFloat(value) > 0);
  };

  // Render completion indicator component for each section
  const renderCompletionIndicator = (sectionKey) => {
    const { filled, total } = sectionCompletion[sectionKey] || { filled: 0, total: 0 };
    
    if (total === 0) return null;
    
    const progressPercentage = Math.floor((filled / total) * 100);
    
    return (
      <div className="trade-recap-completion-indicator">
        <div className="trade-recap-completion-progress">
          <div 
            className="trade-recap-completion-progress-bar" 
            style={{ 
              width: `${progressPercentage}%`,
              backgroundColor: 'var(--hms-primary-blue)'
            }}
          />
        </div>
        <span className="trade-recap-completion-counter">{filled}/{total}</span>
      </div>
    );
  };

  const renderInputField = (label, name, type = 'text', description = '', rows = 2, placeholder = '') => {

    // Special handling for delivery point fields
    if (name === 'sale_delivery_point' || name === 'purchase_delivery_point') {
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <PortsDropdown
            value={formFields[name] || ''}
            onChange={(value) => {
              console.log(`${name} selection: `, value);
              setFormFields(prev => ({
                ...prev,
                [name]: value
              }));
            }}
            className="trade-recap-ports-dropdown"
            placeholder="Enter country name or port name"
            isHighlighted={false}
            onValidationChange={(isValid) => handlePortValidationChange(name, isValid)}
          />
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Special handling for laycan field
    if (name === 'laycan') {
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <DateRangePicker
            id={name}
            name={name}
            value={formFields[name] || ''}
            onChange={(e) => {
              handleChange(e);
              setFormFields(prev => ({
                ...prev,
                _laycanManuallySet: true
              }));
            }}
            className="trade-recap-date-range"
            format="YYYY-MM-DD"
            placeholder="Select laycan date range"
          />
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Special handling for laycan_comments field
    if (name === 'laycan_comments') {
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <textarea
            id={name}
            name={name}
            value={formFields[name] || ''}
            onChange={handleChange}
            rows="3"
            placeholder="Enter additional laycan details or comments"
            className="trade-recap-textarea"
          />
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Special handling for contract number fields
    if (name === 'sale_contract_number' || name === 'purchase_contract_number') {
      const numberField = (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <input
            type="text"
            id={name}
            name={name}
            value={formFields[name]}
            readOnly
            placeholder="Auto Generated"
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
          {description && <small className="field-description">{description}</small>}
        </div>
      );
      // Cargo recaps: the leg's own contract (Create Contract, status, Send
      // to Legal) sits under its number. A new C-number goes straight into
      // the form so a later save sends it back rather than the loaded blank;
      // it does not arm the unsaved-changes prompt (the server already has it).
      if (commodityType !== 'solids') return numberField;
      return (
        <React.Fragment key={name}>
          {numberField}
          <RecapContractBlock
            recapId={tradeRecap?.id}
            summary={tradeRecap?.legal_contract_summary || null}
            onContractNumber={(number) => setFormFields((prev) => ({ ...prev, [name]: number }))}
          />
        </React.Fragment>
      );
    }

    // Special handling for quantity fields with unit dropdown (matching DeliveryForm)
    if (name === 'sale_contract_quantity' || name === 'purchase_contract_quantity') {
      const unitFieldName = name === 'sale_contract_quantity' 
        ? 'sale_contract_quantity_unit' 
        : 'purchase_contract_quantity_unit';
      
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <div className="trade-recap-paired-fields">
            <ContractFigureInput
              id={name}
              name={name}
              disabled={hasRecapLines}
              maxDecimals={QUANTITY_DECIMALS}
              savedValue={tradeRecap?.[name]}
              onChange={(value) => {
                setFormFields(prev => ({
                  ...prev,
                  [name]: value
                }));
                setHasUnsavedChanges(true);
              }}
              value={formFields[name]}
              placeholder="0.00"
            />
            <select
              name={unitFieldName}
              value={formFields[unitFieldName] || ''}
              onChange={(e) => {
                setFormFields(prev => ({
                  ...prev,
                  [unitFieldName]: e.target.value
                }));
                setRefusedUnits(({ [unitFieldName]: _picked, ...rest }) => rest);
                setHasUnsavedChanges(true);
              }}
              className="trade-recap-select"
              aria-invalid={refusedUnits[unitFieldName] ? 'true' : undefined}
              style={{
                minWidth: '120px',
                flex: 1,
                ...(refusedUnits[unitFieldName] ? { borderColor: '#dc2626', boxShadow: '0 0 0 1px #dc2626' } : {}),
              }}
            >
              <option value="">Unit</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.code}>
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
          {hasRecapLines && <small className="field-description">Adds up the product lines</small>}
          {refusedUnits[unitFieldName] && (
            <small className="field-error" role="alert" style={{ color: '#dc2626' }}>
              {refusedUnits[unitFieldName]}
            </small>
          )}
        </div>
      );
    }

    // Special handling for tolerance fields
    if (name === 'sale_shipping_tolerance' || name === 'purchase_shipping_tolerance') {
      const prefix = name.startsWith('sale_') ? 'sale_shipping_tolerance' : 'purchase_shipping_tolerance';
      
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={`${prefix}_lower_value`}>{label}</label>}
          <div className="tolerance-inputs-container">
            <div className="tolerance-input-group">
              <div className="tolerance-input-with-dropdown">
                <input
                  type="text"
                  id={`${prefix}_lower_value`}
                  name={`${prefix}_lower_value`}
                  onChange={(e) => {
                    const value = e.target.value;
                    const unit = formFields[`${prefix}_lower_unit`];
                    const baseQty = contractQuantityNumber(formFields[`${prefix}_contract_quantity`]) || 0;
                    
                    if (!validateTolerance(value)) {
                      showError("Please enter a valid number for shipping tolerance", DEFAULT_DURATION);
                      return;
                    }
                    
                    // Validate min value must be <= base quantity
                    if (unit === 'min' && value && baseQty && parseFloat(value) > baseQty) {
                      showError(`Lower min (${value}) cannot exceed base quantity (${baseQty})`, DEFAULT_DURATION);
                      return;
                    }
                    
                    console.log('✅ TOLERANCE LOWER:', { value, unit, baseQty, valid: true });
                    setFormFields(prev => ({
                      ...prev,
                      [`${prefix}_lower_value`]: value
                    }));
                  }}
                  value={formFields[`${prefix}_lower_value`]}
                  placeholder="0"
                  inputMode="decimal"
                />
                <select
                  name={`${prefix}_lower_unit`}
                  value={formFields[`${prefix}_lower_unit`]}
                  onChange={(e) => {
                    console.log('🔄 TOLERANCE LOWER UNIT CHANGED:', e.target.value);
                    handleChange(e);
                  }}
                  className="tolerance-unit-dropdown"
                >
                  <option value="%">%</option>
                  <option value="min">min</option>
                </select>
              </div>
              <small className="field-description">Lower</small>
            </div>
            
            <div className="tolerance-input-group">
              <div className="tolerance-input-with-dropdown">
                <input
                  type="text"
                  id={`${prefix}_upper_value`}
                  name={`${prefix}_upper_value`}
                  onChange={(e) => {
                    const value = e.target.value;
                    const unit = formFields[`${prefix}_upper_unit`];
                    const baseQty = contractQuantityNumber(formFields[`${prefix}_contract_quantity`]) || 0;
                    
                    if (!validateTolerance(value)) {
                      showError("Please enter a valid number for shipping tolerance", DEFAULT_DURATION);
                      return;
                    }
                    
                    // Validate max value must be >= base quantity
                    if (unit === 'max' && value && baseQty && parseFloat(value) < baseQty) {
                      showError(`Upper max (${value}) cannot be less than base quantity (${baseQty})`, DEFAULT_DURATION);
                      return;
                    }
                    
                    console.log('✅ TOLERANCE UPPER:', { value, unit, baseQty, valid: true });
                    setFormFields(prev => ({
                      ...prev,
                      [`${prefix}_upper_value`]: value
                    }));
                  }}
                  value={formFields[`${prefix}_upper_value`]}
                  placeholder="0"
                  inputMode="decimal"
                />
                <select
                  name={`${prefix}_upper_unit`}
                  value={formFields[`${prefix}_upper_unit`]}
                  onChange={(e) => {
                    console.log('🔄 TOLERANCE UPPER UNIT CHANGED:', e.target.value);
                    handleChange(e);
                  }}
                  className="tolerance-unit-dropdown"
                >
                  <option value="%">%</option>
                  <option value="max">max</option>
                </select>
              </div>
              <small className="field-description">Upper</small>
            </div>
          </div>
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Special handling for price currency fields with paired value (like quantity)
    if (name === 'sale_contract_price_currency' || name === 'purchase_contract_price_currency') {
      const valueFieldName = name === 'sale_contract_price_currency' 
        ? 'sale_contract_price_value' 
        : 'purchase_contract_price_value';
      
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={valueFieldName}>{label}</label>}
          <div className="trade-recap-paired-fields">
            <ContractFigureInput
              id={valueFieldName}
              name={valueFieldName}
              disabled={hasRecapLines}
              maxDecimals={PRICE_DECIMALS}
              allowNegative
              savedValue={tradeRecap?.[valueFieldName]}
              onChange={(value) => {
                setFormFields(prev => ({
                  ...prev,
                  [valueFieldName]: value
                }));
              }}
              value={formFields[valueFieldName]}
              placeholder="0.00"
            />
            <select
              name={name}
              value={formFields[name] || ''}
              onChange={(e) => {
                setFormFields(prev => ({
                  ...prev,
                  [name]: e.target.value
                }));
                setHasUnsavedChanges(true);
              }}
              className="trade-recap-select"
              style={{ minWidth: '120px', flex: 1 }}
            >
              <option value="">Currency</option>
              {CURRENCY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          {hasRecapLines
            ? <small className="field-description">Average of the product lines</small>
            : description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Special handling for CV Basis / Price Basis option fields with paired value (like quantity)
    if (name === 'sale_cv_basis_option' || name === 'purchase_price_basis_option') {
      const valueFieldName = name === 'sale_cv_basis_option' 
        ? 'sale_cv_basis_value' 
        : 'purchase_price_basis_value';
      
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={valueFieldName}>{label}</label>}
          <div className="trade-recap-paired-fields">
            <input
              type="text"
              id={valueFieldName}
              name={valueFieldName}
              onChange={(e) => {
                const value = e.target.value;
                if (validateKcalKg(value)) {
                  setFormFields(prev => ({
                    ...prev,
                    [valueFieldName]: value
                  }));
                } else {
                  showError("Please enter a valid positive number for the value field", DEFAULT_DURATION);
                }
              }}
              value={formFields[valueFieldName] || ''}
              placeholder="kcal/kg"
              inputMode="decimal"
            />
            <select
              name={name}
              value={formFields[name] || ''}
              onChange={(e) => {
                setFormFields(prev => ({
                  ...prev,
                  [name]: e.target.value
                }));
                setHasUnsavedChanges(true);
              }}
              className="trade-recap-select"
              style={{ minWidth: '120px', flex: 1 }}
            >
              <option value="">Basis</option>
              {cvBasisOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Special handling for price fields
    if (name === 'sale_contract_price_value' || name === 'purchase_contract_price_value') {
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <ContractFigureInput
            id={name}
            name={name}
            maxDecimals={PRICE_DECIMALS}
            allowNegative
            savedValue={tradeRecap?.[name]}
            onChange={(value) => {
              setFormFields(prev => ({
                ...prev,
                [name]: value
              }));
            }}
            value={formFields[name]}
            placeholder="0.00"
          />
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Special handling for CV Basis Value and Price Basis Value fields
    if (name === 'sale_cv_basis_value' || name === 'purchase_price_basis_value') {
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <input
            type="text"
            id={name}
            name={name}
            onChange={(e) => {
              const value = e.target.value;
              if (validateKcalKg(value)) {
                setFormFields(prev => ({
                  ...prev,
                  [name]: value
                }));
              } else {
                showError("Please enter a valid positive number for the value field", DEFAULT_DURATION);
              }
            }}
            value={formFields[name]}
            placeholder="kcal/kg"
            inputMode="decimal"
          />
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Special handling for textarea fields
    if (type === 'textarea') {
      console.log(`Rendering textarea for ${name} with value:`, formFields[name]);
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <textarea
            id={name}
            name={name}
            value={formFields[name] || ''}
            onChange={(e) => {
              console.log(`Textarea ${name} changed to:`, e.target.value);
              handleChange(e);
            }}
            rows={rows}
            placeholder={label || ''}
            className="trade-recap-textarea"
          />
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    if (type === 'date') {
      // Use InvoiceDatePicker for deal confirmation dates (same as DeliveryForm)
      if (name.includes('deal_confirmation_date')) {
        return (
          <div className="trade-recap-form-group-vertical" key={name}>
            {label && <label htmlFor={name}>{label}</label>}
            <InvoiceDatePicker
              id={name}
              name={name}
              value={formFields[name] || ''}
              onChange={(e) => {
                handleChange(e);
                setHasUnsavedChanges(true);
              }}
              placeholder="Select date"
            />
            {description && <small className="field-description">{description}</small>}
          </div>
        );
      }
      
      // Use regular DatePicker for other date fields
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <DatePicker
            id={name}
            name={name}
            value={formFields[name] || ''}
            onChange={handleChange}
            className="trade-recap-date"
            format="YYYY-MM-DD"
            placeholder="Select date"
          />
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    // Handle dropdown type fields (used for currency, cv_basis, etc.)
    if (type === 'dropdown') {
      // Get options from the saleFields or purchaseFields definition
      let options = [];
      if (name.includes('currency')) {
        options = CURRENCY_OPTIONS;
      } else if (name.includes('cv_basis_option') || name.includes('price_basis_option')) {
        options = cvBasisOptions;
      } else if (name.includes('incoterm')) {
        options = incotermOptions;
      } else if (name.includes('payment_terms')) {
        // A recap saved before the catalogue merge holds a display name, not
        // a term code. Show it rather than rendering an empty select.
        options = withStoredOption(paymentTermsOptions, formFields[name]);
      }
      
      return (
        <div className="trade-recap-form-group-vertical" key={name}>
          {label && <label htmlFor={name}>{label}</label>}
          <select
            id={name}
            name={name}
            value={formFields[name] || ''}
            onChange={(e) => {
              handleChange(e);
              setHasUnsavedChanges(true);
            }}
            className="trade-recap-select"
          >
            <option value="">Select {label || 'option'}</option>
            {options.map(option => {
              // Handle both array of strings and array of objects with value/label
              const optionValue = typeof option === 'string' ? option : option.value;
              const optionLabel = typeof option === 'string' ? option : option.label;
              return (
                <option key={optionValue} value={optionValue}>{optionLabel}</option>
              );
            })}
          </select>
          {description && <small className="field-description">{description}</small>}
        </div>
      );
    }

    return (
        <div className="trade-recap-form-group-vertical" key={name}>
            {label && <label htmlFor={name}>{label}</label>}
            <input
                type={type}
                id={name}
                name={name}
                value={formFields[name]}
                onChange={handleChange}
                placeholder={placeholder || label || ''}
            />
            {description && <small className="field-description">{description}</small>}
        </div>
    );
  };

  // New helper function to render fields horizontally (side by side)
  const renderHorizontalFields = (fields) => (
    <div className="horizontal-fields">
      {fields.map((field) => {
        if (field.name.toLowerCase().includes('currency')) {
          return (
            <div className="trade-recap-form-group-vertical" key={field.name} style={{ flex: 1, minWidth: 0 }}>
              <label htmlFor={field.name}>{field.label}</label>
              <select
                id={field.name}
                name={field.name}
                value={formFields[field.name] || ''}
                onChange={(e) => {
                  handleChange(e);
                  setHasUnsavedChanges(true);
                }}
                className="trade-recap-select"
              >
                <option value="">Select Currency</option>
                {CURRENCY_OPTIONS.map(currency => (
                  <option key={currency.value} value={currency.value}>
                    {currency.label}
                  </option>
                ))}
              </select>
              {field.description && <small className="field-description">{field.description}</small>}
            </div>
          );
        }
        if (field.type === 'crm_search') {
          return (
            <div className="trade-recap-form-group-vertical" key={field.name} style={{ flex: 1, minWidth: 0 }}>
              <RecapPartnerSearch
                label={field.label}
                fieldName={field.name}
                fieldNameId={field.nameId}
                value={formFields[field.name]}
                crmId={formFields[field.nameId]}
                onChange={(partnerId, partnerName) => {
                  setFormFields(prev => ({
                    ...prev,
                    [field.name]: partnerName,
                    [field.nameId]: partnerId
                  }));
                  setHasUnsavedChanges(true);
                }}
                onCreatePartner={isNewRecap ? (searchTerm) =>
                  openCrmCreator(field.name, field.nameId, inferPartnerRole(field.name), searchTerm)
                  : undefined}
                extraVisibleProfiles={pendingCreatedPartners}
              />
            </div>
          );
        }
        if (field.type === 'dropdown') {
          return (
            <div key={field.name} style={{ flex: 1, minWidth: 0 }}>
              {renderDropdownField(field.label, field.name, field.options, field.description)}
            </div>
          );
        }
        return (
          <div key={field.name} style={{ flex: 1, minWidth: 0 }}>
            {renderInputField(field.label, field.name, field.type, field.description)}
          </div>
        );
      })}
    </div>
  );

  const renderDropdownField = (label, name, options, description = '') => {
    console.log(`Rendering dropdown for ${name}:`, options);
    return (
    <div className="trade-recap-form-group-vertical" key={name}>
      {label && <label>{label}</label>}
      <select
        name={name}
        value={formFields[name]}
        onChange={handleChange}
        className="trade-recap-select"
      >
        <option value="">Select {label || 'option'}</option>
        {options && options.length > 0 ? options.map((option) => (
          <option key={optionValue(option)} value={optionValue(option)}>
            {optionLabel(option)}
          </option>
        )) : <option value="" disabled>No options available</option>}
      </select>
      {description && <small className="field-description">{description}</small>}
    </div>
    );
  };

  // Sprint 7C: fuels-mode renderer. Builds the four post-Products
  // sections (Deal Details, Contract & Partners, Commercial Terms,
  // Deal Makers & Comments) using the shared ``Section`` + ``Field``
  // wrappers so the recap matches the FuelEnquiryForm and
  // FuelOfferGenerator visuals byte-for-byte. Solids recaps still
  // use the legacy ``renderAdditionalFields`` + inline Deal Details
  // JSX below this function -- nothing about Solids changes.
  //
  // Sprint 7C also drops the parent qty/price inputs from the
  // Commercial Terms card on fuels: the Products card carries the
  // per-product quantity / sale_price / buy_price now, and the
  // parent scalars are derived on save (see save-time roll-up).
  // Currency stays at deal level (no per-product currency).
  // Pledge (go-live checklist), right after a leg's payment terms. The list is
  // the banks this leg's company holds accounts at; invoices raised from the
  // deal inherit the pledge and are locked to that bank's accounts.
  const renderPledgeField = (prefix, entity) => (
    <PledgeBankField
      idPrefix={`${prefix}recap`}
      entity={entity}
      value={pledgeOf(formFields, prefix)}
      onChange={(next) => {
        setFormFields((prev) => ({ ...prev, ...pledgeFieldsFor(next, prefix) }));
        setHasUnsavedChanges(true);
      }}
    />
  );

  const renderFuelsLayout = () => {
    const isSale = recapType === 'Sale';
    const leg = isSale ? 'sale' : 'purchase';

    // Bunkers Phase 1 trim: the per-leg tolerance row was removed from
    // the recap screens per the PM's screenshot ground truth. Columns
    // (sale_shipping_tolerance_*, purchase_shipping_tolerance_*) stay
    // on the model + serializer so legacy populated rows keep their
    // data; only the FE renderer is gone. ``renderToleranceField`` and
    // its ``tolerancePrefix`` helper were deleted with the same change.

    const partnerField = (label, fieldName, fieldNameId) => (
      <Field label={label}>
        <RecapPartnerSearch
          label=""
          fieldName={fieldName}
          fieldNameId={fieldNameId}
          value={formFields[fieldName]}
          crmId={formFields[fieldNameId]}
          onChange={(partnerId, partnerName) => {
            setFormFields((prev) => ({
              ...prev,
              [fieldName]: partnerName,
              [fieldNameId]: partnerId,
              // Changing the SALE buyer invalidates the seeded Key Contact
              // (it belonged to the previous buyer). Clear it so we never
              // write a stale contact onto the new buyer's CRM profile --
              // it repopulates from CRM on the next load.
              ...(fieldName === 'sale_buyer'
                ? { key_contact_name: '', key_contact_email: '' }
                : {}),
            }));
            setHasUnsavedChanges(true);
          }}
          onCreatePartner={isNewRecap ? (searchTerm) =>
            openCrmCreator(fieldName, fieldNameId, inferPartnerRole(fieldName), searchTerm)
            : undefined}
          extraVisibleProfiles={pendingCreatedPartners}
        />
      </Field>
    );

    const dropdownField = (label, name, options, placeholder) => (
      <Field label={label}>
        <select
          name={name}
          value={formFields[name] || ''}
          onChange={(e) => {
            handleChange(e);
            setHasUnsavedChanges(true);
          }}
        >
          <option value="">{placeholder || `Select ${label}`}</option>
          {options.map((opt) => {
            const v = typeof opt === 'string' ? opt : opt.value;
            const l = typeof opt === 'string' ? opt : opt.label;
            return <option key={v} value={v}>{l}</option>;
          })}
        </select>
      </Field>
    );

    const textField = (label, name, placeholder, hint) => (
      <Field label={label} hint={hint}>
        <input
          type="text"
          name={name}
          value={formFields[name] || ''}
          onChange={handleChange}
          placeholder={placeholder || ''}
        />
      </Field>
    );

    // Bunkers Phase 1: surface the paired-recap sibling at the top of
    // the fuels recap so the trader can hop between SALE and PURCHASE
    // legs without bouncing through the enquiry. The serializer ships
    // ``paired_purchase_recap`` on SALE rows and
    // ``paired_sale_recap_summary`` on PURCHASE rows; both share the
    // ``{id, transaction_number, recap_type}`` shape. Returns null
    // when no sibling exists (legacy single-leg recaps). The banner
    // reuses the existing ``.fef-next-step--info`` styling from
    // FuelEnquiryForm.css so we have one banner shape system-wide.
    const pairedSibling = isSale
      ? tradeRecap?.paired_purchase_recap
      : tradeRecap?.paired_sale_recap_summary;
    const pairedSiblingLabel = isSale ? 'Purchase' : 'Sale';
    // Bunkers Phase 1 (May 2026): "View Enquiry" button on the recap
    // header banner. Source enquiry id is exposed on the serializer as
    // ``source_fuel_enquiry_id`` -- works for both legs (SALE reaches it
    // via the direct OneToOne, PURCHASE via the paired SALE sibling).
    const sourceEnquiryId = tradeRecap?.source_fuel_enquiry_id;
    // Bunkers Phase 1 (May 2026): "View Contract" button. The
    // ``legal_contract_summary`` field is shipped by TradeRecapSerializer
    // and resolves to ``{id, contract_number}`` only when a LegalContract
    // has been minted for this recap leg (each leg has its own pair-leg
    // contract, so this is leg-specific, not shared across the sibling).
    const legalContract = tradeRecap?.legal_contract_summary;

    return (
      <>
        {pairedSibling?.id && (
          <div className="fef-next-step fef-next-step--info" role="note">
            <FontAwesomeIcon
              icon={faExchangeAlt}
              className="fef-next-step-icon"
            />
            <div className="fef-next-step-body">
              <div className="fef-next-step-title">
                Paired {pairedSiblingLabel} Recap
              </div>
              <div className="fef-next-step-desc">
                {pairedSibling.transaction_number || `#${pairedSibling.id}`}
              </div>
            </div>
            <div className="fef-next-step-actions">
              {sourceEnquiryId && (
                <button
                  type="button"
                  className="fef-next-step-action"
                  onClick={() => history.push(
                    `/dashboard/trades/fuels/fuel-enquiries/${sourceEnquiryId}`,
                  )}
                >
                  View Enquiry{' '}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              )}
              <button
                type="button"
                className="fef-next-step-action"
                onClick={() => history.push(
                  `/trade-recap/${pairedSibling.id}`,
                  { commodityType: 'fuels' },
                )}
              >
                View {pairedSiblingLabel} Recap{' '}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
              {legalContract?.id && (
                <button
                  type="button"
                  className="fef-next-step-action"
                  onClick={() => history.push(
                    `/dashboard/legal/contracts/${legalContract.id}`,
                  )}
                  title={`Open ${legalContract.contract_number} in the Legal module`}
                >
                  View Contract{' '}
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* SECTION 1: IMT ------------------------------------------ */}
        {/* Bunkers Phase 1 (May 2026): the IMT section diverges between
            legs per the PM's recap screenshots.
            -- SALE leg: 12-field card (Customer Trader, Supplier Trader,
               Sales Date, IMT Branch, Branch Manager auto, IMT Sell
               Company, Sales Contract No., Buyer, Incoterms Sales,
               Credit Limit, Credit Balance, Payment Terms).
            -- PURCHASE leg: 5-field card (Supply Trader, IMT Entity,
               Purchase Date, Purchase Contract No., Incoterms Purchase).
            Backing columns are shared on TradeRecap; the conversion
            service mirrors them from FuelEnquiry at convert time so
            both legs auto-populate. */}
        {isSale ? (
        <Section
          title="IMT"
          stepNumber={1}
          anchorId="hms-anchor-hms"
        >
          {/* Row 1: traders + sale date */}
          <div className="hms-grid hms-grid-3">
            <Field
              label="Customer Trader"
              hint={tradersLoaded ? traderBoxNote(employeesAsTraders, 'customer') : undefined}
            >
              <select
                value={formFields.customer_trader ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setFormFields((prev) => ({ ...prev, customer_trader: v }));
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Select a trader…</option>
                <TraderOptions
                  traders={employeesAsTraders}
                  box="customer"
                  saved={tradeRecap?.customer_trader_detail}
                />
              </select>
            </Field>
            <Field
              label="Supplier Trader (optional)"
              hint={tradersLoaded ? traderBoxNote(employeesAsTraders, 'supplier') : undefined}
            >
              <select
                value={formFields.supplier_trader ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setFormFields((prev) => ({ ...prev, supplier_trader: v }));
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Assign later</option>
                <TraderOptions
                  traders={employeesAsTraders}
                  box="supplier"
                  saved={tradeRecap?.supplier_trader_detail}
                />
              </select>
            </Field>
            <Field label="Sales Date">
              <InvoiceDatePicker
                id="sale_date"
                name="sale_date"
                value={formFields.sale_date || ''}
                onChange={(e) => {
                  setFormFields((prev) => ({ ...prev, sale_date: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="Select sales date"
              />
            </Field>
          </div>

          {/* Row 2: branch + branch manager (auto) + sell company */}
          <div className="hms-grid hms-grid-3">
            <Field label="IMT Branch">
              <select
                value={formFields.imt_branch ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setFormFields((prev) => ({ ...prev, imt_branch: v }));
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Select a branch…</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Branch Manager (auto)" hint="Auto-fills from IMT Branch">
              <input
                type="text"
                className="hms-input-readonly"
                value={(() => {
                  // Prefer the server-derived value (always authoritative;
                  // returns the saved branch's manager even if the local
                  // ``branches`` cache is stale or empty). Fall back to
                  // the local lookup when the trader has selected a new
                  // branch but hasn't saved yet.
                  if (tradeRecap?.branch_manager_name) {
                    return tradeRecap.branch_manager_name;
                  }
                  const b = branches.find((br) => br.id === Number(formFields.imt_branch));
                  return b?.branch_manager_name || '';
                })() || '—'}
                readOnly
              />
            </Field>
            <Field label="IMT Sell Company">
              <select
                value={formFields.hms_sell_company ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setFormFields((prev) => ({ ...prev, hms_sell_company: v }));
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Select an entity…</option>
                {hmsEntities.map((entity) => {
                  // hmsEntities can be either CRMProfile-shaped objects or
                  // the legacy {label} list -- match both shapes so we
                  // don't break either consumer.
                  const id = typeof entity === 'object'
                    ? (entity.id ?? entity.value)
                    : null;
                  const name = typeof entity === 'object'
                    ? (entity.hms_entity_name || entity.label || entity.name)
                    : entity;
                  if (id == null) return null;
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
            </Field>
          </div>

          {/* Row 3: contract number (auto) + buyer + incoterm */}
          <div className="hms-grid hms-grid-3">
            <Field label={isSale ? 'Sales Contract No.' : 'Purchase Contract No.'} hint="Auto-generated">
              <input
                type="text"
                value={formFields[`${leg}_contract_number`] || ''}
                readOnly
                placeholder="Auto Generated"
                className="hms-input-readonly"
              />
            </Field>
            {isSale
              ? partnerField('Buyer', 'sale_buyer', 'sale_buyer_crm_id')
              : partnerField('Seller', 'purchase_seller', 'purchase_seller_crm_id')}
            {dropdownField(
              isSale ? 'Incoterms Sales' : 'Incoterms Purchase',
              `${leg}_incoterm`,
              incotermOptions,
            )}
          </div>

          {/* Row 4: credit limit + credit balance (both read-only,
              derived from the SALE buyer's CRMProfile -- on PURCHASE
              recaps the @property returns null so both cells show "--") */}
          <div className="hms-grid hms-grid-3">
            <Field
              label="Credit Limit"
              hint="Effective limit (base + active enquiry uplifts)"
            >
              <input
                type="text"
                className="hms-input-readonly"
                value={
                  tradeRecap?.credit_limit_value != null
                    ? `$${Number(tradeRecap.credit_limit_value).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : '—'
                }
                readOnly
              />
            </Field>
            <Field label="Credit Balance Indicator" hint="Live headroom after open enquiries">
              <input
                type="text"
                className="hms-input-readonly"
                value={
                  tradeRecap?.credit_balance_value != null
                    ? `$${Number(tradeRecap.credit_balance_value).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`
                    : '—'
                }
                readOnly
                style={
                  tradeRecap?.credit_balance_value != null
                    && Number(tradeRecap.credit_balance_value) < 0
                    ? { color: '#b91c1c', fontWeight: 600 }
                    : undefined
                }
              />
            </Field>
            {dropdownField(
              'Payment Terms',
              `${leg}_payment_terms`,
              paymentTermsOptions,
            )}
            {renderPledgeField(`${leg}_`, formFields.hms_sell_company)}
          </div>

          {/* Row 5: Key Contact + Contact Email (moved from the Fuel
              Enquiry screen). Editable mirror of the SALE buyer's CRM
              primary contact -- on save they sync back to CRM via
              key_contact_sync_service. Edits to an approved buyer profile
              by a non-CRM-Approver route through the RFC workflow; the
              hint sets that expectation. */}
          <div className="hms-grid hms-grid-2">
            <Field
              label="Key Contact"
              hint={tradeRecap?.key_contact_approval_status === 'approved'
                ? 'Edits to an approved profile may require CRM Approver review.'
                : 'From buyer\'s CRM profile'}
            >
              <input
                type="text"
                name="key_contact_name"
                value={formFields.key_contact_name || ''}
                onChange={handleChange}
                maxLength={255}
                placeholder="Primary contact name"
              />
            </Field>
            <Field label="Contact Email">
              <input
                type="email"
                name="key_contact_email"
                value={formFields.key_contact_email || ''}
                onChange={handleChange}
                placeholder="primary@example.com"
              />
            </Field>
          </div>
        </Section>
        ) : (
        /* PURCHASE leg IMT card: 5 fields per the PM's purchase-recap
           screenshot. Supplier-side view -- no buyer, no credit
           indicators, no sales date, no branch. */
        <Section
          title="IMT"
          stepNumber={1}
          anchorId="hms-anchor-hms"
        >
          <div className="hms-grid hms-grid-3">
            <Field
              label="Supply Trader"
              hint={tradersLoaded ? traderBoxNote(employeesAsTraders, 'supplier') : undefined}
            >
              <select
                value={formFields.supplier_trader ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setFormFields((prev) => ({ ...prev, supplier_trader: v }));
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Select a trader…</option>
                <TraderOptions
                  traders={employeesAsTraders}
                  box="supplier"
                  saved={tradeRecap?.supplier_trader_detail}
                />
              </select>
            </Field>
            <Field label="IMT Entity">
              {/* PURCHASE leg: the entity BUYING from the supplier
                  (hms_buy_company) — before July 2026 this wrongly bound
                  the sell company. */}
              <select
                value={formFields.hms_buy_company ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setFormFields((prev) => ({ ...prev, hms_buy_company: v }));
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Select an entity…</option>
                {hmsEntities.map((entity) => {
                  const id = typeof entity === 'object'
                    ? (entity.id ?? entity.value)
                    : null;
                  const name = typeof entity === 'object'
                    ? (entity.hms_entity_name || entity.label || entity.name)
                    : entity;
                  if (id == null) return null;
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
            </Field>
            <Field label="Purchase Date">
              <InvoiceDatePicker
                id="purchase_date"
                name="purchase_date"
                value={formFields.purchase_date || ''}
                onChange={(e) => {
                  setFormFields((prev) => ({ ...prev, purchase_date: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="Select purchase date"
              />
            </Field>
          </div>

          <div className="hms-grid hms-grid-2">
            <Field label="Purchase Contract No." hint="Auto-generated">
              <input
                type="text"
                value={formFields.purchase_contract_number || ''}
                readOnly
                placeholder="Auto Generated"
                className="hms-input-readonly"
              />
            </Field>
            {dropdownField(
              'Incoterms Purchase',
              'purchase_incoterm',
              incotermOptions,
            )}
          </div>
        </Section>
        )}

        {/* SECTION 2: PRODUCTS (FuelsSpecBlock) --------------------- */}
        <Section
          title="Products"
          stepNumber={2}
          anchorId="hms-anchor-products"
        >
          <FuelsSpecBlock
            commodityType={commodityType}
            formFields={formFields}
            setFormFields={editFormFields}
            onCreateProduct={openProductCreatorForFuelsProduct}
            recapType={recapType === 'Sale' ? 'SALE' : 'PURCHASE'}
            paymentTermsOptions={paymentTermsOptions}
          />
        </Section>

        {/* SECTION 3: SHIP DETAILS (SALE-only) --------------------- */}
        {/* Bunkers Phase 1: 7-field Ship Details section per the PM's
            recap screenshots. PURCHASE recaps skip this section -- ship
            logistics live on the SALE leg only; the PURCHASE leg goes
            straight from Products to the Bunkers Financial Summary +
            Funding sections.
            ``country`` auto-fills from the picked port (matching the
            FuelEnquiry behaviour); the trader can override with any
            value from the bunker-ports dataset. */}
        {isSale && (
        <Section
          title="Ship Details"
          stepNumber={3}
          anchorId="hms-anchor-ship-details"
        >
          <div className="hms-grid hms-grid-2">
            <Field label="Vessel Name">
              <VesselSearchAutocomplete
                initialValue={formFields.vessel_name || ''}
                onSelect={handleVesselSelected}
                onQueryChange={handleVesselNameTyped}
                placeholder="Search vessel by Name, IMO, MMSI or Call Sign..."
              />
            </Field>
            <Field label="Vessel Type">
              <select
                value={formFields.vessel_type ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setFormFields((prev) => ({ ...prev, vessel_type: v }));
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Select vessel type…</option>
                {vesselTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="hms-grid hms-grid-2">
            <Field label="IMO Number">
              <input
                type="text"
                name="trade_recap_imo_number"
                value={formFields.trade_recap_imo_number || ''}
                onChange={handleChange}
                placeholder="e.g. 9876543 (or TBC / PENDING)"
                maxLength={50}
              />
            </Field>
            <Field label="Port" hint="Bunker ports only">
              <select
                value={formFields.port ?? ''}
                onChange={(e) => {
                  const newId = e.target.value ? Number(e.target.value) : null;
                  const matched = bunkerPorts.find((p) => p.id === newId);
                  setFormFields((prev) => {
                    const next = { ...prev, port: newId };
                    // Auto-fill country from the picked port, but only
                    // when the trader hasn't typed an override -- if
                    // ``country`` is empty OR matches the previously
                    // selected port's country, overwrite it. Mirrors
                    // the FuelEnquiry form's port handler.
                    const priorMatched = bunkerPorts.find((p) => p.id === prev.port);
                    const priorCountry = priorMatched?.country_name || '';
                    if (matched && (!prev.country || prev.country === priorCountry)) {
                      next.country = matched.country_name || '';
                    }
                    return next;
                  });
                  setHasUnsavedChanges(true);
                }}
              >
                <option value="">Select port…</option>
                {[...bunkerPorts]
                  .sort((a, b) => {
                    const ca = (a.country_name || '').localeCompare(b.country_name || '');
                    if (ca !== 0) return ca;
                    return (a.port_name || '').localeCompare(b.port_name || '');
                  })
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.country_name} — {p.port_name}
                    </option>
                  ))}
              </select>
            </Field>
          </div>

          <div className="hms-grid hms-grid-2">
            <Field label="Country" hint="Auto-fills from Port — override by typing">
              <input
                type="text"
                name="country"
                value={formFields.country || ''}
                onChange={(e) => {
                  setFormFields((prev) => ({ ...prev, country: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
                placeholder="e.g. SINGAPORE"
              />
            </Field>
            <Field label="Expected Delivery Window">
              <DateRangePicker
                startDateName="expected_delivery_start"
                endDateName="expected_delivery_end"
                startDateValue={formFields.expected_delivery_start || ''}
                endDateValue={formFields.expected_delivery_end || ''}
                onChange={(e) => {
                  handleChange(e);
                  setHasUnsavedChanges(true);
                }}
                placeholder="Select delivery window…"
              />
            </Field>
          </div>

          <Field label="Laycan Comments">
            <textarea
              id="laycan_comments"
              name="laycan_comments"
              value={formFields.laycan_comments || ''}
              onChange={handleChange}
              placeholder="Enter additional laycan / delivery comments"
              rows={3}
            />
          </Field>
        </Section>
        )}
      </>
    );
  };

  const renderAdditionalFields = () => {
    // CRM partner fields now use RecapPartnerSearch component

    // Sprint 2C: Fuels recaps hide the Solids CV-basis fields (file 11
    // §3.4 hide column). The fields stay in `formFields` and on the
    // backend — we only skip rendering them. Solids users see them
    // exactly as before.
    const FUELS_HIDDEN_FIELDS = new Set([
      'sale_cv_basis_option',
      'sale_cv_basis_value',
      'purchase_price_basis_option',
      'purchase_price_basis_value',
    ]);
    const filterForCommodity = (fields) => (
      commodityType === 'fuels'
        ? fields.filter((f) => !FUELS_HIDDEN_FIELDS.has(f.name))
        : fields
    );

    const saleFields = filterForCommodity([
      { label: 'Sales Contract No.', name: 'sale_contract_number' },
      { label: 'Buyer', name: 'sale_buyer', nameId: 'sale_buyer_crm_id', type: 'crm_search' },
      // Group chain in between fields to display horizontally
      { label: 'Sales - Chain in Between 1', name: 'sale_chain_in_between', nameId: 'sale_chain_in_between_crm_id', type: 'crm_search', group: 'chain_in_between_sale' },
      { label: 'Sales - Chain in Between 2', name: 'sale_chain_in_between_2', nameId: 'sale_chain_in_between_2_crm_id', type: 'crm_search', group: 'chain_in_between_sale' },
      { label: 'End User', name: 'sale_enduser', nameId: 'sale_enduser_crm_id', type: 'crm_search' },
      { label: 'Sales Quantity', name: 'sale_contract_quantity', description: 'Metric tons' },
      { label: 'Tolerance', name: 'sale_shipping_tolerance' },
      { label: 'Incoterms Sales', name: 'sale_incoterm', type: 'dropdown', options: incotermOptions },
      { label: 'Delivery Point', name: 'sale_delivery_point' },
      { label: 'Sales Price per Unit (contract) Currency', name: 'sale_contract_price_currency', type: 'dropdown', options: CURRENCY_OPTIONS },
      { label: 'Sales Price per Unit (contract) Value', name: 'sale_contract_price_value', description: 'If index price, enter estimated fix price per metric ton' },
      { label: 'Price Formula', name: 'sale_price_formula', description: 'Price formula if applicable', placeholder: 'e.g., Platts + $5' },
      { label: 'Price Basis Sales (Quality)', name: 'sale_cv_basis_option', type: 'dropdown', options: cvBasisOptions },
      { label: 'Price Basis Sales Value', name: 'sale_cv_basis_value', description: 'kcal/kg' },
      { label: 'Payment Terms', name: 'sale_payment_terms', type: 'dropdown', options: paymentTermsOptions },
      { label: 'Commissions', name: 'sale_commissions', placeholder: 'e.g., $2.50 per MT' },
      { label: 'Sales Confirmation Date', name: 'sale_deal_confirmation_date', type: 'date' },
    ]);

    const purchaseFields = filterForCommodity([
      { label: 'Purchase Contract No.', name: 'purchase_contract_number' },
      { label: 'Producer', name: 'purchase_producer', nameId: 'purchase_producer_crm_id', type: 'crm_search' },
      // Group chain in between fields to display horizontally
      { label: 'Purchase - Chain in Between 1', name: 'purchase_chain_in_between', nameId: 'purchase_chain_in_between_crm_id', type: 'crm_search', group: 'chain_in_between_purchase' },
      { label: 'Purchase - Chain in Between 2', name: 'purchase_chain_in_between_2', nameId: 'purchase_chain_in_between_2_crm_id', type: 'crm_search', group: 'chain_in_between_purchase' },
      { label: 'Seller', name: 'purchase_seller', nameId: 'purchase_seller_crm_id', type: 'crm_search' },
      { label: 'Purchase Quantity', name: 'purchase_contract_quantity', description: 'Metric tons' },
      { label: 'Tolerance', name: 'purchase_shipping_tolerance' },
      { label: 'Incoterms Purchase', name: 'purchase_incoterm', type: 'dropdown', options: incotermOptions },
      { label: 'Delivery Point', name: 'purchase_delivery_point' },
      { label: 'Purchase Price per Unit (contract) Currency', name: 'purchase_contract_price_currency', type: 'dropdown', options: CURRENCY_OPTIONS },
      { label: 'Purchase Price per Unit (contract) Value', name: 'purchase_contract_price_value', description: 'If index price, enter estimated fix price per metric ton' },
      { label: 'Price Formula', name: 'purchase_price_formula', description: 'Price formula if applicable', placeholder: 'e.g., Platts + $5' },
      { label: 'Price Basis Purchase (Quality)', name: 'purchase_price_basis_option', type: 'dropdown', options: cvBasisOptions },
      { label: 'Price Basis Purchase Value', name: 'purchase_price_basis_value', description: 'kcal/kg' },
      { label: 'Payment Terms', name: 'purchase_payment_terms', type: 'dropdown', options: paymentTermsOptions },
      { label: 'Commissions', name: 'purchase_commissions', placeholder: 'e.g., $2.50 per MT' },
      { label: 'Purchase Confirmation Date', name: 'purchase_deal_confirmation_date', type: 'date' },
    ]);

    const saleGroupedFieldsMap = {
      sale_contract_price_currency: 'sale_contract_price_value',
      sale_cv_basis_option: 'sale_cv_basis_value',
    };

    const purchaseGroupedFieldsMap = {
      purchase_contract_price_currency: 'purchase_contract_price_value',
      purchase_price_basis_option: 'purchase_price_basis_value',
    };

    const renderFieldSet = (fields, groupedFieldsMap, title) => {
      // Track which fields are part of groups we've already rendered
      const renderedGroups = new Set();
      
      // Separate fields into partner and commercial groups
      const isSale = title.toLowerCase() === 'sale';
      
      // Fields for Contract & Partners section
      const partnerFields = fields.filter(f => 
        f.name.includes('contract_number') ||
        f.name.includes('buyer') || 
        f.name.includes('seller') || 
        f.name.includes('chain_in_between') ||
        f.name.includes('enduser') ||
        f.name.includes('producer')
      );
      
      // Fields for Commercial Terms section
      const commercialFields = fields.filter(f => 
        !partnerFields.includes(f)
      );
      
      // Split commercial fields into left and right columns
      const commercialFieldsLeft = [];
      const commercialFieldsRight = [];
      let leftCount = 0;
      let rightCount = 0;
      
      commercialFields.forEach((field) => {
        // Skip paired value fields (they'll be rendered with their currency/option pair)
        if (Object.values(groupedFieldsMap).includes(field.name)) {
          return;
        }
        
        // Alternate distribution, keeping paired fields together
        if (leftCount <= rightCount) {
          commercialFieldsLeft.push(field);
          leftCount++;
          // If this field has a paired field, add it too
          if (groupedFieldsMap[field.name]) {
            const pairedField = commercialFields.find(f => f.name === groupedFieldsMap[field.name]);
            if (pairedField) commercialFieldsLeft.push(pairedField);
          }
        } else {
          commercialFieldsRight.push(field);
          rightCount++;
          if (groupedFieldsMap[field.name]) {
            const pairedField = commercialFields.find(f => f.name === groupedFieldsMap[field.name]);
            if (pairedField) commercialFieldsRight.push(pairedField);
          }
        }
      });
      
      // Sprint 7D — Field rendering helper. Each field is now wrapped
      // in the shared <Field> component (.hms-field) instead of the
      // legacy `.trade-recap-field-with-bullet` div, so the Cargo
      // recap inherits the same uppercase micro-label + 6px input
      // styling as the Fuels recap and Fuel Enquiry screens. State
      // keys, handlers and validation are untouched.
      const renderFieldWithBulletLabel = (field) => {
        if (field.type === 'crm_search') {
          return (
            <Field key={field.name} label={field.label}>
              <RecapPartnerSearch
                label=""
                fieldName={field.name}
                fieldNameId={field.nameId}
                value={formFields[field.name]}
                crmId={formFields[field.nameId]}
                onChange={(partnerId, partnerName) => {
                  setFormFields(prev => ({
                    ...prev,
                    [field.name]: partnerName,
                    [field.nameId]: partnerId
                  }));
                  setHasUnsavedChanges(true);
                }}
                onCreatePartner={isNewRecap ? (searchTerm) =>
                  openCrmCreator(field.name, field.nameId, inferPartnerRole(field.name), searchTerm)
                  : undefined}
                extraVisibleProfiles={pendingCreatedPartners}
              />
            </Field>
          );
        }

        if (field.type === 'dropdown') {
          const select = (
            <Field key={field.name} label={field.label} hint={field.description}>
              <select
                name={field.name}
                value={formFields[field.name] || ''}
                onChange={handleChange}
              >
                <option value="">Select {field.label}</option>
                {field.options && field.options.map((option) => (
                  <option key={optionValue(option)} value={optionValue(option)}>
                    {optionLabel(option)}
                  </option>
                ))}
              </select>
            </Field>
          );
          // The Pledge tick sits right after the leg's payment terms.
          if (field.name.endsWith('_payment_terms')) {
            return (
              <React.Fragment key={field.name}>
                {select}
                {renderPledgeField(field.name.replace('payment_terms', ''), formFields.entity)}
              </React.Fragment>
            );
          }
          return select;
        }

        // `renderInputField` still owns the per-field-name special
        // cases (delivery point, laycan, paired qty+unit, tolerance,
        // readonly contract no., etc.). We pass label='' so its inner
        // `<label>` is suppressed and the surrounding <Field> provides
        // the label instead — same pattern the legacy bullet wrapper
        // used.
        return (
          <Field key={field.name} label={field.label} hint={field.description}>
            {renderInputField('', field.name, field.type, '', 2, field.placeholder)}
          </Field>
        );
      };
      
      // Split partner fields: Left = contract no, buyer/producer, enduser/seller; Right = chain in between
      const leftPartnerFields = partnerFields.filter(f => 
        f.name.includes('contract_number') ||
        f.name.includes('buyer') ||
        f.name.includes('enduser') ||
        f.name.includes('producer') ||
        f.name.includes('seller')
      ).filter(f => !f.name.includes('chain_in_between'));
      
      const rightPartnerFields = partnerFields.filter(f => 
        f.name.includes('chain_in_between')
      );
      
      // Sprint 7D — wrap a paired (currency + value) commercial field
      // in <Field>. The inner renderInputField call still owns the
      // unit/dropdown markup so all existing handlers and validations
      // stay intact.
      const renderPairedCommercialField = (field) => (
        <Field key={field.name} label={field.label} hint={field.description}>
          {renderInputField('', field.name, field.type, '', 2, field.placeholder)}
        </Field>
      );

      return (
        <>
          {/* ============================================== */}
          {/* CONTRACT & PARTNERS SECTION (Sprint 7D)         */}
          {/* ============================================== */}
          <Section
            title="Contract & Partners"
            stepNumber={2}
            anchorId="hms-anchor-contract-partners"
            headerAction={renderCompletionIndicator('contractPartners')}
          >
            <div className="hms-grid hms-grid-2">
              <div>
                {/* Left: Contract No, Buyer/Producer, End User/Seller */}
                {leftPartnerFields.map((field) => renderFieldWithBulletLabel(field))}
              </div>

              <div>
                {/* Right: Chain in Between fields */}
                {rightPartnerFields.map((field) => {
                  if (field.group && !renderedGroups.has(field.group)) {
                    const groupFields = rightPartnerFields.filter(f => f.group === field.group);
                    renderedGroups.add(field.group);

                    return (
                      <div className="field-group" key={field.group}>
                        {groupFields.map(gf => renderFieldWithBulletLabel(gf))}
                      </div>
                    );
                  }

                  if (field.group && renderedGroups.has(field.group)) {
                    return null;
                  }

                  return renderFieldWithBulletLabel(field);
                })}
              </div>
            </div>
          </Section>

          {/* ============================================== */}
          {/* COMMERCIAL TERMS SECTION (Sprint 7D)           */}
          {/* ============================================== */}
          <Section
            title="Commercial Terms"
            stepNumber={3}
            anchorId="hms-anchor-commercial-terms"
            headerAction={renderCompletionIndicator('commercialTerms')}
          >
            <div className="hms-grid hms-grid-2">
              <div>
                {commercialFieldsLeft.map((field) => {
                  if (Object.values(groupedFieldsMap).includes(field.name)) {
                    return null;
                  }
                  if (groupedFieldsMap[field.name]) {
                    return renderPairedCommercialField(field);
                  }
                  return renderFieldWithBulletLabel(field);
                })}
              </div>

              <div>
                {commercialFieldsRight.map((field) => {
                  if (Object.values(groupedFieldsMap).includes(field.name)) {
                    return null;
                  }
                  if (groupedFieldsMap[field.name]) {
                    return renderPairedCommercialField(field);
                  }
                  return renderFieldWithBulletLabel(field);
                })}
              </div>
            </div>
          </Section>

          {/* ============================================== */}
          {/* DEAL MAKERS & COMMENTS SECTION (Sprint 7D)     */}
          {/* ============================================== */}
          <Section
            title="Deal Makers & Comments"
            stepNumber={4}
            anchorId="hms-anchor-deal-makers"
            headerAction={renderCompletionIndicator('dealMakersComments')}
          >
            <div className="hms-grid hms-grid-2">
              <Field label="Deal Makers">
                {renderDealMakerFields(title.toLowerCase())}
              </Field>

              <Field label="Additional Comments">
                <textarea
                  id={`${title.toLowerCase()}_additional_comments`}
                  name={`${title.toLowerCase()}_additional_comments`}
                  value={formFields[`${title.toLowerCase()}_additional_comments`] || ''}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Enter additional comments"
                />
              </Field>
            </div>
          </Section>
        </>
      );
    };

    return (
      <div className="deal-legs-container">
        {recapType === 'Sale' ? (
          renderFieldSet(saleFields, saleGroupedFieldsMap, 'Sale')
        ) : (
          renderFieldSet(purchaseFields, purchaseGroupedFieldsMap, 'Purchase')
        )}
      </div>
    );
  };

  const handleBackClick = () => {
    console.log('[DEBUG] Back button clicked. hasUnsavedChanges:', hasUnsavedChanges, 'autoSavedNeedsConfirm:', autoSavedNeedsConfirm);
    
    // Show confirmation if either we have unsaved changes OR content was autosaved but needs confirmation
    if (hasUnsavedChanges || autoSavedNeedsConfirm) {
      console.log('[DEBUG] Setting showBackConfirmation to true');
      setShowBackConfirmation(true);
      // Verify state was updated in next render cycle
      setTimeout(() => {
        console.log('[DEBUG] Confirmation state after update:', showBackConfirmation);
      }, 0);
    } else {
      console.log('[DEBUG] No unsaved changes or autosaved content, navigating directly');
      // If no unsaved changes, go back directly and restore previous state
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'recaps',
      });
    }
  };

  const handleConfirmBack = async () => {
    try {
      // Cancel any pending autosave
      if (debouncedSaveRef.current && debouncedSaveRef.current.cancel) {
        debouncedSaveRef.current.cancel();
        console.log('[AUTOSAVE] Canceled pending autosave on confirm back');
      }
      
      // Always roll back changes for existing records if we have the original data
      if (tradeRecap && offerId && originalDataRef.current) {
        console.log('[AUTOSAVE] Rolling back changes for existing trade recap');
        await rollbackChanges();
      }
      
      setShowBackConfirmation(false);
      setAutoSavedNeedsConfirm(false); // Reset the autosave confirmation flag
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'recaps',
      });
    } catch (error) {
      console.error('[AUTOSAVE] Error handling back button:', error);
      setShowBackConfirmation(false);
      setAutoSavedNeedsConfirm(false); // Reset the flag even on error
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'recaps',
      }); // Navigate even if rollback fails
    }
  };

  const handleCancelBack = () => {
    setShowBackConfirmation(false);
    // Note: We intentionally don't reset autoSavedNeedsConfirm here
    // This ensures that if they try to navigate away again, they'll still get the confirmation
  };

  const handleDraftAndLeave = async () => {
    try {
      // Save as draft using existing save function
      const success = await saveFormData(false, false);
      
      if (success) {
        // Clear any session/localStorage backup since we've saved successfully.
        // Audit 2026-05-17: also clear pre-migration localStorage entries.
        const key = `autosave_traderecap_${offerId || 'new'}`;
        sessionStorage.removeItem(key);
        sessionStorage.removeItem(`formAutosave_${offerId || 'new'}`);
        localStorage.removeItem(key);
        localStorage.removeItem(`formAutosave_${offerId || 'new'}`);

        // The saveFormData function properly resets change tracking flags
        // Close modal and navigate away
        setShowBackConfirmation(false);
        
        goToTradeOps(history, tradeOpsDesk, {
          selectedDate: location.state?.selectedDate,
          activeTab: location.state?.activeTab || 'recaps',
        });
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      showError('Failed to save draft. Please try again.');
    }
  };

  const handleViewDelivery = async (deliveryNumber) => {
    try {
      // First fetch the delivery ID using the delivery number
      const response = await axiosInstance.get(`/api/deliveries/by_number/${deliveryNumber}/`);
      const deliveryId = response.data.id;
      history.push(`/deliveries/${deliveryId}`);
    } catch (error) {
      console.error('Error fetching delivery:', error);
      showError("Failed to find the associated delivery", DEFAULT_DURATION);
    }
  };

  // Hydrate recap-side Hamster Builder log when an existing recap is loaded.
  useEffect(() => {
    if (tradeRecap?.hamster_builder_log) {
      setHamsterBuilderLog(tradeRecap.hamster_builder_log);
    }
  }, [tradeRecap?.id]);

  // Update the useEffect hook that handles offerResponse
  useEffect(() => {
    if (offerResponse) {
      console.log('Received trade recap data from ChatWindow:', offerResponse);

      // Append the AI extraction (with its source input/file) to the recap-side
      // Hamster Builder log. Persist via PATCH if we already have a recap row.
      const newLogEntry = {
        timestamp: new Date().toISOString(),
        extraction: offerResponse,
        source: {
          input: offerResponse._source_input || null,
          file_name: offerResponse._source_file_name || null,
          file_text: offerResponse._source_file_text || null,
        },
      };
      setHamsterBuilderLog(prev => {
        const updated = [...prev, newLogEntry];
        if (tradeRecap?.id) {
          axiosInstance.patch(`/api/offer/trade-recaps/${tradeRecap.id}/`, {
            hamster_builder_log: updated,
          }).catch(err => {
            console.warn('Failed to persist recap hamster_builder_log:', err);
          });
        }
        return updated;
      });
      // Add debug log to verify laycan is being transferred
      console.log('Checking if laycan is in offerResponse:', {
        offerResponseHasLaycan: 'laycan' in offerResponse,
        laycanValueInOffer: offerResponse.laycan,
        formFieldsLaycanBefore: formFields.laycan
      });
      
      // Check and log shipping tolerance values if present
      if (offerResponse.sale_shipping_tolerance) {
        console.log('Received sale_shipping_tolerance from chat:', offerResponse.sale_shipping_tolerance);
      }
      if (offerResponse.sale_shipping_tolerance_lower) {
        console.log('Received sale_shipping_tolerance_lower from chat:', offerResponse.sale_shipping_tolerance_lower);
      }
      if (offerResponse.sale_shipping_tolerance_upper) {
        console.log('Received sale_shipping_tolerance_upper from chat:', offerResponse.sale_shipping_tolerance_upper);
      }
      if (offerResponse.purchase_shipping_tolerance) {
        console.log('Received purchase_shipping_tolerance from chat:', offerResponse.purchase_shipping_tolerance);
      }
      if (offerResponse.purchase_shipping_tolerance_lower) {
        console.log('Received purchase_shipping_tolerance_lower from chat:', offerResponse.purchase_shipping_tolerance_lower);
      }
      if (offerResponse.purchase_shipping_tolerance_upper) {
        console.log('Received purchase_shipping_tolerance_upper from chat:', offerResponse.purchase_shipping_tolerance_upper);
      }
      
      // Add debug logs for deal confirmation dates
      if (offerResponse.sale_deal_confirmation_date) {
        console.log('Received sale_deal_confirmation_date from chat:', offerResponse.sale_deal_confirmation_date);
      }
      if (offerResponse.purchase_deal_confirmation_date) {
        console.log('Received purchase_deal_confirmation_date from chat:', offerResponse.purchase_deal_confirmation_date);
      }
      
      // Add debug logs for laycan_comments and additional_comments
      if (offerResponse.laycan_comments) {
        console.log('Received laycan_comments from chat:', offerResponse.laycan_comments);
      }
      if (offerResponse.sale_additional_comments) {
        console.log('Received sale_additional_comments from chat:', offerResponse.sale_additional_comments);
      }
      if (offerResponse.purchase_additional_comments) {
        console.log('Received purchase_additional_comments from chat:', offerResponse.purchase_additional_comments);
      }
      
      // Add debug logs for payment terms
      if (offerResponse.sale_payment_terms) {
        console.log('Received sale_payment_terms from chat:', offerResponse.sale_payment_terms);
      }
      if (offerResponse.purchase_payment_terms) {
        console.log('Received purchase_payment_terms from chat:', offerResponse.purchase_payment_terms);
      }
      
      // Add debug logs for delivery points
      if (offerResponse.sale_delivery_point) {
        console.log('Received sale_delivery_point from chat:', offerResponse.sale_delivery_point);
      }
      if (offerResponse.purchase_delivery_point) {
        console.log('Received purchase_delivery_point from chat:', offerResponse.purchase_delivery_point);
      }
      
      // Update form fields based on the response data - ONLY transfer specified fields
      setFormFields(prevFields => {
        // Determine if it's a sale or purchase
        const isSale = (recapType === 'Sale');
        
        // Track if this is a new laycan value from the chat that's different from current
        const isNewLaycanValue = offerResponse.laycan && offerResponse.laycan !== prevFields.laycan;
        
        // Parse sale shipping tolerance if it exists
        let saleTolerance = {};
        // Check first for the separate lower/upper values
        if (offerResponse.sale_shipping_tolerance_lower || offerResponse.sale_shipping_tolerance_upper) {
          // Extract values and units for separate lower/upper tolerance fields
          const lowerMatch = offerResponse.sale_shipping_tolerance_lower ? 
            offerResponse.sale_shipping_tolerance_lower.match(/^(\d*\.?\d*)(.*)$/) : null;
          const upperMatch = offerResponse.sale_shipping_tolerance_upper ? 
            offerResponse.sale_shipping_tolerance_upper.match(/^(\d*\.?\d*)(.*)$/) : null;
          
          // Initialize with current values to avoid clearing unspecified values
          saleTolerance = {
            lowerValue: prevFields.sale_shipping_tolerance_lower_value || '',
            lowerUnit: prevFields.sale_shipping_tolerance_lower_unit || '%',
            upperValue: prevFields.sale_shipping_tolerance_upper_value || '',
            upperUnit: prevFields.sale_shipping_tolerance_upper_unit || '%'
          };
          
          // Only update the specific values that are provided and have numeric values
          if (lowerMatch && lowerMatch[1]) {
            saleTolerance.lowerValue = lowerMatch[1];
            saleTolerance.lowerUnit = lowerMatch[2] || '%';
          } else if (offerResponse.sale_shipping_tolerance_lower === 'min' || 
                    offerResponse.sale_shipping_tolerance_lower === '%') {
            // Keep unit but don't clear existing value
            if (offerResponse.sale_shipping_tolerance_lower === '%') {
              saleTolerance.lowerUnit = '%';
            } else if (offerResponse.sale_shipping_tolerance_lower === 'min') {
              saleTolerance.lowerUnit = 'min';
            }
          }
          
          if (upperMatch && upperMatch[1]) {
            saleTolerance.upperValue = upperMatch[1];
            saleTolerance.upperUnit = upperMatch[2] || '%';
          } else if (offerResponse.sale_shipping_tolerance_upper === '%') {
            saleTolerance.upperUnit = '%';
          } else if (offerResponse.sale_shipping_tolerance_upper === 'max') {
            saleTolerance.upperUnit = 'max';
          }
          
          console.log('Parsed separate sale shipping tolerance values:', saleTolerance);
        }
        // Fall back to the combined sale_shipping_tolerance field
        else if (offerResponse.sale_shipping_tolerance) {
          // Special handling for "min to max" or just "%"
          if (offerResponse.sale_shipping_tolerance === 'min to max' || 
              offerResponse.sale_shipping_tolerance === '%') {
            // Keep existing values but possibly update units
            saleTolerance = {
              lowerValue: prevFields.sale_shipping_tolerance_lower_value || '',
              lowerUnit: offerResponse.sale_shipping_tolerance === 'min to max' ? 'min' : '%',
              upperValue: prevFields.sale_shipping_tolerance_upper_value || '',
              upperUnit: offerResponse.sale_shipping_tolerance === 'min to max' ? 'max' : '%',
            };
          } else {
            // Normal parsing for actual values
            saleTolerance = parseToleranceString(offerResponse.sale_shipping_tolerance);
          }
          console.log('Parsed combined sale shipping tolerance:', saleTolerance);
        }
        
        // Parse purchase shipping tolerance if it exists
        let purchaseTolerance = {};
        // Check first for the separate lower/upper values
        if (offerResponse.purchase_shipping_tolerance_lower || offerResponse.purchase_shipping_tolerance_upper) {
          // Extract values and units for separate lower/upper tolerance fields
          const lowerMatch = offerResponse.purchase_shipping_tolerance_lower ? 
            offerResponse.purchase_shipping_tolerance_lower.match(/^(\d*\.?\d*)(.*)$/) : null;
          const upperMatch = offerResponse.purchase_shipping_tolerance_upper ? 
            offerResponse.purchase_shipping_tolerance_upper.match(/^(\d*\.?\d*)(.*)$/) : null;
          
          // Initialize with current values to avoid clearing unspecified values
          purchaseTolerance = {
            lowerValue: prevFields.purchase_shipping_tolerance_lower_value || '',
            lowerUnit: prevFields.purchase_shipping_tolerance_lower_unit || '%',
            upperValue: prevFields.purchase_shipping_tolerance_upper_value || '',
            upperUnit: prevFields.purchase_shipping_tolerance_upper_unit || '%'
          };
          
          // Only update the specific values that are provided and have numeric values
          if (lowerMatch && lowerMatch[1]) {
            purchaseTolerance.lowerValue = lowerMatch[1];
            purchaseTolerance.lowerUnit = lowerMatch[2] || '%';
          } else if (offerResponse.purchase_shipping_tolerance_lower === 'min' || 
                    offerResponse.purchase_shipping_tolerance_lower === '%') {
            // Keep unit but don't clear existing value
            if (offerResponse.purchase_shipping_tolerance_lower === '%') {
              purchaseTolerance.lowerUnit = '%';
            } else if (offerResponse.purchase_shipping_tolerance_lower === 'min') {
              purchaseTolerance.lowerUnit = 'min';
            }
          }
          
          if (upperMatch && upperMatch[1]) {
            purchaseTolerance.upperValue = upperMatch[1];
            purchaseTolerance.upperUnit = upperMatch[2] || '%';
          } else if (offerResponse.purchase_shipping_tolerance_upper === '%') {
            purchaseTolerance.upperUnit = '%';
          } else if (offerResponse.purchase_shipping_tolerance_upper === 'max') {
            purchaseTolerance.upperUnit = 'max';
          }
          
          console.log('Parsed separate purchase shipping tolerance values:', purchaseTolerance);
        }
        // Fall back to the combined purchase_shipping_tolerance field
        else if (offerResponse.purchase_shipping_tolerance) {
          // Special handling for "min to max" or just "%"
          if (offerResponse.purchase_shipping_tolerance === 'min to max' || 
              offerResponse.purchase_shipping_tolerance === '%') {
            // Keep existing values but possibly update units
            purchaseTolerance = {
              lowerValue: prevFields.purchase_shipping_tolerance_lower_value || '',
              lowerUnit: offerResponse.purchase_shipping_tolerance === 'min to max' ? 'min' : '%',
              upperValue: prevFields.purchase_shipping_tolerance_upper_value || '',
              upperUnit: offerResponse.purchase_shipping_tolerance === 'min to max' ? 'max' : '%',
            };
          } else {
            // Normal parsing for actual values
            purchaseTolerance = parseToleranceString(offerResponse.purchase_shipping_tolerance);
          }
          console.log('Parsed combined purchase shipping tolerance:', purchaseTolerance);
        }
        
        // If there's a new laycan value from the chat, use it (regardless of previous manual setting)
        // This allows new chat responses to update the laycan even after manual edits
        const updatedFields = {
          ...prevFields,
          // ONLY transfer these fields as specified
          // 1. Entity (IMT entities)
          entity: offerResponse.entity || prevFields.entity,
          
          // 2. Product
          product: offerResponse.product || prevFields.product,
          
          // 3. Origin
          origin: offerResponse.origin || prevFields.origin,
          
          // 4. Laycan - Update if there's a new value from the chat
          laycan: isNewLaycanValue ? offerResponse.laycan : prevFields.laycan,
          // If we get a new value from the chat, we're no longer in manual mode
          _laycanManuallySet: isNewLaycanValue ? false : prevFields._laycanManuallySet,
          
          // 4a. Laycan comments
          laycan_comments: offerResponse.laycan_comments || prevFields.laycan_comments,
          
          // 5. Quantity (based on whether it's sale or purchase)
          sale_contract_quantity: isSale ? (offerResponse.sale_contract_quantity || prevFields.sale_contract_quantity) : prevFields.sale_contract_quantity,
          purchase_contract_quantity: !isSale ? (offerResponse.purchase_contract_quantity || prevFields.purchase_contract_quantity) : prevFields.purchase_contract_quantity,
          
          // 7. Shipping tolerance fields
          // Sale shipping tolerance components - Only update if there's new tolerance data
          sale_shipping_tolerance: (offerResponse.sale_shipping_tolerance || (offerResponse.sale_shipping_tolerance_lower || offerResponse.sale_shipping_tolerance_upper)) 
            ? (offerResponse.sale_shipping_tolerance || '') 
            : prevFields.sale_shipping_tolerance,
          sale_shipping_tolerance_lower_value: Object.keys(saleTolerance).length > 0
            ? saleTolerance.lowerValue
            : prevFields.sale_shipping_tolerance_lower_value,
          sale_shipping_tolerance_lower_unit: Object.keys(saleTolerance).length > 0
            ? saleTolerance.lowerUnit
            : prevFields.sale_shipping_tolerance_lower_unit,
          sale_shipping_tolerance_upper_value: Object.keys(saleTolerance).length > 0
            ? saleTolerance.upperValue
            : prevFields.sale_shipping_tolerance_upper_value,
          sale_shipping_tolerance_upper_unit: Object.keys(saleTolerance).length > 0
            ? saleTolerance.upperUnit
            : prevFields.sale_shipping_tolerance_upper_unit,
          
          // Purchase shipping tolerance components - Only update if there's new tolerance data
          purchase_shipping_tolerance: (offerResponse.purchase_shipping_tolerance || (offerResponse.purchase_shipping_tolerance_lower || offerResponse.purchase_shipping_tolerance_upper))
            ? (offerResponse.purchase_shipping_tolerance || '')
            : prevFields.purchase_shipping_tolerance,
          purchase_shipping_tolerance_lower_value: Object.keys(purchaseTolerance).length > 0
            ? purchaseTolerance.lowerValue
            : prevFields.purchase_shipping_tolerance_lower_value,
          purchase_shipping_tolerance_lower_unit: Object.keys(purchaseTolerance).length > 0
            ? purchaseTolerance.lowerUnit
            : prevFields.purchase_shipping_tolerance_lower_unit,
          purchase_shipping_tolerance_upper_value: Object.keys(purchaseTolerance).length > 0
            ? purchaseTolerance.upperValue
            : prevFields.purchase_shipping_tolerance_upper_value,
          purchase_shipping_tolerance_upper_unit: Object.keys(purchaseTolerance).length > 0
            ? purchaseTolerance.upperUnit
            : prevFields.purchase_shipping_tolerance_upper_unit,
          
          // 8. Incoterms (based on whether it's sale or purchase)
          sale_incoterm: isSale ? (offerResponse.sale_incoterm || prevFields.sale_incoterm) : prevFields.sale_incoterm,
          purchase_incoterm: !isSale ? (offerResponse.purchase_incoterm || prevFields.purchase_incoterm) : prevFields.purchase_incoterm,
          
          // 8a. Delivery Point (based on whether it's sale or purchase)
          sale_delivery_point: isSale ? (offerResponse.sale_delivery_point || prevFields.sale_delivery_point) : prevFields.sale_delivery_point,
          purchase_delivery_point: !isSale ? (offerResponse.purchase_delivery_point || prevFields.purchase_delivery_point) : prevFields.purchase_delivery_point,
          
          // 9. Contract currency (based on whether it's sale or purchase)
          sale_contract_price_currency: isSale ? (offerResponse.sale_contract_price_currency || prevFields.sale_contract_price_currency) : prevFields.sale_contract_price_currency,
          purchase_contract_price_currency: !isSale ? (offerResponse.purchase_contract_price_currency || prevFields.purchase_contract_price_currency) : prevFields.purchase_contract_price_currency,
          
          // 10. Contract price value (based on whether it's sale or purchase)
          sale_contract_price_value: isSale ? (offerResponse.sale_contract_price_value || prevFields.sale_contract_price_value) : prevFields.sale_contract_price_value,
          purchase_contract_price_value: !isSale ? (offerResponse.purchase_contract_price_value || prevFields.purchase_contract_price_value) : prevFields.purchase_contract_price_value,
          
          // 11. CV option (based on whether it's sale or purchase)
          sale_cv_basis_option: isSale ? (offerResponse.sale_cv_basis_option || prevFields.sale_cv_basis_option) : prevFields.sale_cv_basis_option,
          purchase_price_basis_option: !isSale ? (offerResponse.purchase_price_basis_option || prevFields.purchase_price_basis_option) : prevFields.purchase_price_basis_option,
          
          // 12. CV price (based on whether it's sale or purchase)
          sale_cv_basis_value: isSale ? (offerResponse.sale_cv_basis_value || prevFields.sale_cv_basis_value) : prevFields.sale_cv_basis_value,
          purchase_price_basis_value: !isSale ? (offerResponse.purchase_price_basis_value || prevFields.purchase_price_basis_value) : prevFields.purchase_price_basis_value,
          
          // 13. Payment terms (based on whether it's sale or purchase)
          sale_payment_terms: isSale ? (offerResponse.sale_payment_terms || prevFields.sale_payment_terms) : prevFields.sale_payment_terms,
          purchase_payment_terms: !isSale ? (offerResponse.purchase_payment_terms || prevFields.purchase_payment_terms) : prevFields.purchase_payment_terms,
          
          // 14. Deal confirmation date (based on whether it's sale or purchase)
          sale_deal_confirmation_date: isSale && offerResponse.sale_deal_confirmation_date ? 
            formatDateForInput(offerResponse.sale_deal_confirmation_date) : prevFields.sale_deal_confirmation_date,
          purchase_deal_confirmation_date: !isSale && offerResponse.purchase_deal_confirmation_date ? 
            formatDateForInput(offerResponse.purchase_deal_confirmation_date) : prevFields.purchase_deal_confirmation_date,
          
          // 15. Additional comments (based on whether it's sale or purchase)
          sale_additional_comments: isSale && offerResponse.sale_additional_comments ? 
            offerResponse.sale_additional_comments : prevFields.sale_additional_comments,
          purchase_additional_comments: !isSale && offerResponse.purchase_additional_comments ? 
            offerResponse.purchase_additional_comments : prevFields.purchase_additional_comments,
          
          // All other fields remain unchanged
        };
        
        // Add debug log to verify shipping tolerance parsing
        if (offerResponse.sale_shipping_tolerance || offerResponse.sale_shipping_tolerance_lower || offerResponse.sale_shipping_tolerance_upper ||
            offerResponse.purchase_shipping_tolerance || offerResponse.purchase_shipping_tolerance_lower || offerResponse.purchase_shipping_tolerance_upper) {
          console.log('Updated tolerance fields:', {
            sale: {
              original: offerResponse.sale_shipping_tolerance,
              separate: {
                lower: offerResponse.sale_shipping_tolerance_lower,
                upper: offerResponse.sale_shipping_tolerance_upper
              },
              parsed: saleTolerance,
              updated: {
                lowerValue: updatedFields.sale_shipping_tolerance_lower_value,
                lowerUnit: updatedFields.sale_shipping_tolerance_lower_unit,
                upperValue: updatedFields.sale_shipping_tolerance_upper_value,
                upperUnit: updatedFields.sale_shipping_tolerance_upper_unit,
              }
            },
            purchase: {
              original: offerResponse.purchase_shipping_tolerance,
              separate: {
                lower: offerResponse.purchase_shipping_tolerance_lower,
                upper: offerResponse.purchase_shipping_tolerance_upper
              },
              parsed: purchaseTolerance,
              updated: {
                lowerValue: updatedFields.purchase_shipping_tolerance_lower_value,
                lowerUnit: updatedFields.purchase_shipping_tolerance_lower_unit,
                upperValue: updatedFields.purchase_shipping_tolerance_upper_value,
                upperUnit: updatedFields.purchase_shipping_tolerance_upper_unit,
              }
            }
          });
        }
        
        // Add debug log to verify laycan updating behavior
        console.log('After update, laycan value check:', {
          laycanBeforeUpdate: prevFields.laycan,
          laycanAfterUpdate: updatedFields.laycan,
          areEqual: prevFields.laycan === updatedFields.laycan,
          laycanFromResponse: offerResponse.laycan,
          laycanManuallySet: prevFields._laycanManuallySet,
          isNewLaycanValue
        });
        
        // Add debug log to verify deal confirmation date updating behavior
        if (offerResponse.sale_deal_confirmation_date || offerResponse.purchase_deal_confirmation_date) {
          console.log('After update, deal confirmation date check:', {
            sale: {
              beforeUpdate: prevFields.sale_deal_confirmation_date,
              afterUpdate: updatedFields.sale_deal_confirmation_date,
              fromResponse: offerResponse.sale_deal_confirmation_date
            },
            purchase: {
              beforeUpdate: prevFields.purchase_deal_confirmation_date,
              afterUpdate: updatedFields.purchase_deal_confirmation_date,
              fromResponse: offerResponse.purchase_deal_confirmation_date
            }
          });
        }
        
        // Add debug log to verify laycan_comments updating behavior
        if (offerResponse.laycan_comments) {
          console.log('After update, laycan_comments check:', {
            beforeUpdate: prevFields.laycan_comments,
            afterUpdate: updatedFields.laycan_comments,
            fromResponse: offerResponse.laycan_comments
          });
        }
        
        // Add debug log to verify additional comments updating behavior
        if (offerResponse.sale_additional_comments || offerResponse.purchase_additional_comments) {
          console.log('After update, additional comments check:', {
            sale: {
              beforeUpdate: prevFields.sale_additional_comments,
              afterUpdate: updatedFields.sale_additional_comments,
              fromResponse: offerResponse.sale_additional_comments
            },
            purchase: {
              beforeUpdate: prevFields.purchase_additional_comments,
              afterUpdate: updatedFields.purchase_additional_comments,
              fromResponse: offerResponse.purchase_additional_comments
            }
          });
        }
        
        // Add debug log to verify payment terms updating behavior
        if (offerResponse.sale_payment_terms || offerResponse.purchase_payment_terms) {
          console.log('After update, payment terms check:', {
            sale: {
              beforeUpdate: prevFields.sale_payment_terms,
              afterUpdate: updatedFields.sale_payment_terms,
              fromResponse: offerResponse.sale_payment_terms
            },
            purchase: {
              beforeUpdate: prevFields.purchase_payment_terms,
              afterUpdate: updatedFields.purchase_payment_terms,
              fromResponse: offerResponse.purchase_payment_terms
            }
          });
        }
        
        // Add debug log to verify delivery points updating behavior
        if (offerResponse.sale_delivery_point || offerResponse.purchase_delivery_point) {
          console.log('After update, delivery points check:', {
            sale: {
              beforeUpdate: prevFields.sale_delivery_point,
              afterUpdate: updatedFields.sale_delivery_point,
              fromResponse: offerResponse.sale_delivery_point
            },
            purchase: {
              beforeUpdate: prevFields.purchase_delivery_point,
              afterUpdate: updatedFields.purchase_delivery_point,
              fromResponse: offerResponse.purchase_delivery_point
            }
          });
          
          // Update port validation state for ports coming from ChatWindow
          if (offerResponse.sale_delivery_point && isSale) {
            setPortValidation(prev => ({
              ...prev,
              sale_delivery_point: true  // Mark as valid since it came from structured output
            }));
          }
          if (offerResponse.purchase_delivery_point && !isSale) {
            setPortValidation(prev => ({
              ...prev,
              purchase_delivery_point: true  // Mark as valid since it came from structured output
            }));
          }
        }

        // Sprint 7C: on fuels, mirror Hamster chat's qty/price writes
        // into Product 1 of ``products[]`` so traders editing via the
        // chat still flow through to the canonical Products card.
        // Without this mirror, after Sprint 7C hides the parent qty/
        // price inputs, chat-driven updates would land on parent
        // scalars that get OVERWRITTEN by the save-time roll-up
        // (which derives parent values FROM products[]). Net effect
        // would be silent loss of the chat's qty/price values.
        //
        // We only touch Product 1 (the head row) and only the fields
        // the chat actually carries. ``sale_quantity`` is the per-
        // product field for both legs (overloaded; see
        // hydrateRecapProducts). For purchase recaps the chat's
        // ``purchase_contract_price_value`` is the buy price, so we
        // mirror it into Product 1's ``buy_price``; for sale recaps
        // the chat's ``sale_contract_price_value`` is the sell price,
        // so it mirrors into ``sale_price``.
        if (commodityType === 'fuels') {
          const incomingQty = isSale
            ? offerResponse.sale_contract_quantity
            : offerResponse.purchase_contract_quantity;
          const incomingPrice = isSale
            ? offerResponse.sale_contract_price_value
            : offerResponse.purchase_contract_price_value;
          const priceTargetKey = isSale ? 'sale_price' : 'buy_price';
          const hasChatQty = incomingQty !== undefined && incomingQty !== null && incomingQty !== '';
          const hasChatPrice = incomingPrice !== undefined && incomingPrice !== null && incomingPrice !== '';
          if (hasChatQty || hasChatPrice) {
            const existing = Array.isArray(updatedFields.products) ? updatedFields.products : [];
            const head = existing[0] ? { ...existing[0] } : blankFuelsProduct();
            if (hasChatQty) head.sale_quantity = String(incomingQty);
            if (hasChatPrice) head[priceTargetKey] = String(incomingPrice);
            const next = [...existing];
            next[0] = head;
            updatedFields.products = next;
          }
        }

        return updatedFields;
      });
    }
  }, [offerResponse, recapType, commodityType]);  // Keep dependencies minimal

  // Add this new render function for deal maker dropdowns
  const renderDealMakerFields = (type) => {
    const key = type.toLowerCase(); // 'sale' or 'purchase'
    
    return (
      <div className="deal-makers-container">
        {dropdownOptions.staffListFailed && (
          // Row 128: the staff list failed; the pickers below are empty for that
          // reason, not because there are no deal makers.
          <ListLoadFailed what="the list of staff for Deal Makers" onRetry={retryTradeRecapDropdowns} />
        )}
        <div className="form-group mb-3">
          <div className="deal-maker-select-group">
            <select
              name={`${key}_deal_maker_1`}
              value={formFields[`${key}_deal_maker_1`] || ''}
              onChange={handleChange}
              className="deal-maker-select"
            >
              <option value="">Select Deal Maker 1</option>
              {dropdownOptions.traders?.map((trader) => (
                <option key={trader.value} value={trader.value}>
                  {trader.label}
                </option>
              ))}
            </select>
            <select
              name={`${key}_deal_maker_1_percentage`}
              value={formFields[`${key}_deal_maker_1_percentage`] || ''}
              onChange={handleDealMakerPercentageChange}
              className="deal-maker-percentage-select"
            >
              <option value="">%</option>
              <option value="100">100%</option>
              <option value="70">70%</option>
              <option value="50">50%</option>
              <option value="30">30%</option>
            </select>
          </div>
        </div>
        <div className="form-group mb-3">
          <div className="deal-maker-select-group">
            <select
              name={`${key}_deal_maker_2`}
              value={formFields[`${key}_deal_maker_2`] || ''}
              onChange={handleChange}
              className="deal-maker-select"
            >
              <option value="">Select Deal Maker 2</option>
              {dropdownOptions.traders?.map((trader) => (
                <option key={trader.value} value={trader.value}>
                  {trader.label}
                </option>
              ))}
            </select>
            <select
              name={`${key}_deal_maker_2_percentage`}
              value={formFields[`${key}_deal_maker_2_percentage`] || ''}
              onChange={handleDealMakerPercentageChange}
              className="deal-maker-percentage-select"
            >
              <option value="">%</option>
              <option value="100">100%</option>
              <option value="70">70%</option>
              <option value="50">50%</option>
              <option value="30">30%</option>
            </select>
          </div>
        </div>
      </div>
    );
  };

  // Add CSS styles for the new deal maker fields
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .deal-makers-container {
        margin-top: 15px;
      }
      
      .deal-maker-select-group {
        display: flex;
        gap: 8px;
        width: 100%;
      }
      
      .deal-maker-select {
        flex: 1;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
      }
      
      .deal-maker-percentage-select {
        width: 100px;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
      }
      
      .subsection-title {
        font-size: 16px;
        font-weight: 600;
        margin-top: 20px;
        margin-bottom: 10px;
        color: #333;
      }
      
      .single-container {
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add a separate useEffect to log formFields whenever they change
  useEffect(() => {
    console.log('FormFields Additional Comments:', {
      sale_additional_comments: formFields.sale_additional_comments,
      purchase_additional_comments: formFields.purchase_additional_comments
    });
  }, [formFields.sale_additional_comments, formFields.purchase_additional_comments]);

  // Store original state for rollback
  useEffect(() => {
    if (tradeRecap && !originalState && formFields && Object.keys(formFields).length > 0) {
      // Deep copy the initial form state for potential rollback
      setOriginalState({
        formData: JSON.parse(JSON.stringify(formFields)),
        additionalValues: {
          recapType,
          customRecapName
        }
      });
      originalDataRef.current = {
        formData: JSON.parse(JSON.stringify(formFields)),
        apiResponse: tradeRecap,
        loading: false,
        error: null
      };
      console.log('[AUTOSAVE] Stored original state for potential rollback');
    }
  }, [tradeRecap, formFields, originalState, recapType, customRecapName]);

  // Add debugging effect for hasUnsavedChanges
  useEffect(() => {
    console.log('[DEBUG] hasUnsavedChanges changed to:', hasUnsavedChanges);
  }, [hasUnsavedChanges]);

  // Add debugging effect for showBackConfirmation
  useEffect(() => {
    console.log('[DEBUG] showBackConfirmation changed to:', showBackConfirmation);
  }, [showBackConfirmation]);

  // Sprint 7A: lazy lookups cache for the fuels autofill on the parent
  // picker. Loaded on first picker change for a fuels recap; subsequent
  // picks reuse the cache. We lazy-load (rather than fetching at mount)
  // because Solids recaps never need these.
  const fuelsLookupsCacheRef = useRef(null);
  const ensureFuelsLookups = async () => {
    if (fuelsLookupsCacheRef.current) return fuelsLookupsCacheRef.current;
    const [fuelTypes, fuelGrades, sulfurLimits, iso8217Years] = await Promise.all([
      fuelEnquiryService.lookups.fuelTypes(),
      fuelEnquiryService.lookups.fuelGrades(),
      fuelEnquiryService.lookups.sulfurLimits(),
      fuelEnquiryService.lookups.iso8217Years(),
    ]);
    fuelsLookupsCacheRef.current = { fuelTypes, fuelGrades, sulfurLimits, iso8217Years };
    return fuelsLookupsCacheRef.current;
  };

  // Sprint D — extracted from the inline RecapProductSearch onChange so the
  // same auto-fill logic runs whether the user (a) selects an existing variant
  // from the search dropdown or (b) creates a brand-new variant via
  // InlineProductCreator at the empty-state "+ Create product" button.
  //
  // Sprint 7A — when commodity_type is 'fuels' AND the picked variant
  // carries a ``spec_attributes`` JSON, also auto-fill the four
  // FuelsSpecBlock FK dropdowns (fuel_type / fuel_grade / sulfur_limit
  // / iso_8217_year) and the measuring_unit. Loose name match via
  // ``autofillSpecFromVariant``; unmatched slugs leave the dropdown
  // alone for manual override (matches the enquiry-form behaviour).
  // Several products on a cargo recap (go-live card 1248h535zfw). Two or
  // more lines fill this leg's Product, Quantity and Price boxes (first
  // product, total, average), as the server stores them; back to one line,
  // the single Product box returns.
  const hasRecapLines = commodityType !== 'fuels'
    && Array.isArray(formFields.products) && formFields.products.length >= 2;
  const applyRecapLines = (next) => {
    setFormFields((prev) => recapFieldsWithLines(prev, next, recapType));
    setHasUnsavedChanges(true);
  };
  const startRecapLines = () => applyRecapLines(recapLinesFromHeader(formFields, recapType));

  const handleRecapProductChange = async (variantId, displayName, variant) => {
    const productName = variant?.product_type_display || (displayName || '').split('|')[0]?.trim() || displayName || '';
    const updates = {
      stock_item_variant: variantId,
      product: productName,
      // The first line's name if the deal grows a second product.
      product_label: variantId ? lineProductLabel(displayName, variant) : '',
    };
    if (variant) {
      if (variant.quantity) {
        updates.sale_contract_quantity = variant.quantity;
        updates.purchase_contract_quantity = variant.quantity;
      }
      if (variant.unit_code) {
        updates.sale_contract_quantity_unit = variant.unit_code;
        updates.purchase_contract_quantity_unit = variant.unit_code;
      }
      if (variant.country_of_origin) {
        updates.origin = variant.country_of_origin.toLowerCase();
      }
      if (variant.price_currency) {
        updates.sale_contract_price_currency = variant.price_currency;
        updates.purchase_contract_price_currency = variant.price_currency;
      }
      if (variant.purchase_price) {
        updates.purchase_contract_price_value = variant.purchase_price;
      }
      if (variant.sale_price) {
        updates.sale_contract_price_value = variant.sale_price;
      }
    }
    setFormFields(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);

    // Sprint 7A: fuels autofill of the four FuelsSpecBlock FKs +
    // measuring_unit. Runs after the synchronous setFormFields above
    // so the spec FKs land in a second batched update -- no race
    // because lookups loading is async and React batches state writes
    // within the same tick.
    if (commodityType === 'fuels' && variant?.spec_attributes) {
      try {
        const lookups = await ensureFuelsLookups();
        const result = autofillSpecFromVariant(variant.spec_attributes, lookups);
        const specUpdates = {};
        ['fuel_type', 'fuel_grade', 'sulfur_limit', 'iso_8217_year'].forEach((k) => {
          if (result[k] != null) specUpdates[k] = result[k];
        });
        if (variant.unit_id != null) specUpdates.measuring_unit = variant.unit_id;
        if (Object.keys(specUpdates).length > 0) {
          setFormFields(prev => ({ ...prev, ...specUpdates }));
          setHasUnsavedChanges(true);
        }
      } catch (err) {
        // Defensive: lookups can fail (network, auth) -- leave the
        // four dropdowns at whatever values they had, the trader can
        // still pick manually. Log so the issue is debuggable.
        console.warn('[Sprint 7A] FuelsSpecBlock autofill skipped:', err);
      }
    }
  };

  const handleInlineProductCreated = (variant) => {
    if (variant) {
      const displayName = variant.display_name || variant.full_description || '';
      handleRecapProductChange(variant.id, displayName, variant);
    }
    closeProductCreator();
  };

  // Inline CRM partner creator helpers. ``isNewRecap`` is the gate the
  // user agreed to: only authoring a brand-new recap may surface the
  // "+ Create CRM partner" CTA — editing an existing recap (any offerId
  // other than 'new') keeps partner pickers search-only so historical
  // artifacts cannot mutate the CRM catalog.
  const isNewRecap = !offerId || offerId === 'new';
  const openCrmCreator = (targetFieldName, targetFieldNameId, role, searchTerm) => {
    setCrmCreatorState({
      isOpen: true,
      searchTerm: searchTerm || '',
      targetFieldName,
      targetFieldNameId,
      role: role || 'seller',
    });
  };
  const closeCrmCreator = () => {
    setCrmCreatorState({
      isOpen: false,
      searchTerm: '',
      targetFieldName: null,
      targetFieldNameId: null,
      role: 'seller',
    });
  };
  const handleInlineCrmPartnerCreated = (profile) => {
    if (!profile) {
      closeCrmCreator();
      return;
    }
    setPendingCreatedPartners((prev) => {
      if (prev.some((p) => p.id === profile.id)) return prev;
      return [...prev, profile];
    });
    const { targetFieldName, targetFieldNameId } = crmCreatorState;
    if (targetFieldName && targetFieldNameId) {
      setFormFields((prev) => ({
        ...prev,
        [targetFieldName]: profile.legal_entity_name,
        [targetFieldNameId]: profile.id,
      }));
      setHasUnsavedChanges(true);
    }
    closeCrmCreator();
  };

  // Heuristic to default the InlineCrmCreator partner_type select based
  // on the field that triggered the flow. Buyer / End User fields default
  // to "Buyer (Debtor)"; Seller / Producer / Owner fields default to
  // "Seller (Creditor)". This is just a default — the user can change it.
  const inferPartnerRole = (fieldName) => {
    const f = (fieldName || '').toLowerCase();
    if (f.includes('buyer') || f.includes('debtor') || f.includes('end_user') || f.includes('consumer')) {
      return 'buyer';
    }
    return 'seller';
  };

  // Sprint 7C: stepper sections for the fuels recap. ``completed``
  // flag drives the green check on the stepper number; we tap into
  // the existing ``sectionCompletion`` scoring (filled === total).
  // The Products step has no completion entry yet so we fall back
  // to "any product has a stock_item_variant" as the heuristic.
  const buildStepperSections = () => {
    const isComplete = (key) => {
      const sec = sectionCompletion[key];
      return sec && sec.total > 0 && sec.filled >= sec.total;
    };
    // Bunkers Phase 1: the fuels stepper checks leg-prefixed fields
    // (sale_/purchase_) so the completion logic stays leg-aware.
    const leg = recapType === 'Sale' ? 'sale' : 'purchase';

    if (commodityType === 'fuels') {
      const productsCount = Array.isArray(formFields.products) ? formFields.products.length : 0;
      const productsComplete = productsCount > 0
        && formFields.products.every((p) => p.stock_item_variant != null && p.sale_quantity);
      // Bunkers Phase 1 (May 2026): 4-step stepper per the PM's recap
      // screenshots, LEG-AWARE.
      // SALE:     IMT / Products / Ship Details / Summary
      // PURCHASE: IMT / Products / Summary / Funding
      // Each step id matches the ``hms-anchor-<id>`` Section anchor
      // below so scroll-to-section keeps working. Completion logic
      // for the editable sections is best-effort (required-fields-set
      // heuristic) since there's no formal validator on these yet.
      const isSale = recapType === 'Sale';
      const hmsComplete = isSale
        ? Boolean(
            formFields.customer_trader && formFields.imt_branch
            && formFields.hms_sell_company && formFields[`${leg}_payment_terms`]
          )
        : Boolean(
            // PURCHASE IMT card is leaner (5 fields). Mark complete
            // when supply trader + IMT entity + purchase date are set.
            // hms_buy_company with a sell-company fallback for legacy
            // recaps that predate the buy-company column.
            formFields.supplier_trader
            && (formFields.hms_buy_company || formFields.hms_sell_company)
            && formFields.purchase_date
          );
      if (isSale) {
        const shipComplete = Boolean(
          formFields.vessel_name && formFields.port
          && formFields.expected_delivery_start && formFields.expected_delivery_end
        );
        return [
          { id: 'hms',           label: 'IMT',                       completed: hmsComplete },
          { id: 'products',      label: 'Products',                  completed: productsComplete },
          { id: 'ship-details',  label: 'Ship Details',              completed: shipComplete },
          { id: 'summary',       label: 'Bunkers Financial Summary', completed: false },
        ];
      }
      // PURCHASE
      const fundingComplete = Boolean(
        formFields.funding_amount && formFields.interest_rate_pct
      );
      return [
        { id: 'hms',      label: 'IMT',                       completed: hmsComplete },
        { id: 'products', label: 'Products',                  completed: productsComplete },
        { id: 'summary',  label: 'Bunkers Financial Summary', completed: false },
        { id: 'funding',  label: 'Funding',                   completed: fundingComplete },
      ];
    }

    // Sprint 7D — solids stepper (Cargo Trade Recap). The four
    // sections map 1:1 onto the existing `sectionCompletion` keys
    // (see getSectionFields()) so the green-tick logic is identical
    // to the legacy `renderCompletionIndicator` widget.
    return [
      { id: 'deal-details',      label: 'Deal Details',         completed: isComplete('dealDetails') },
      { id: 'contract-partners', label: 'Contract & Partners',  completed: isComplete('contractPartners') },
      { id: 'commercial-terms',  label: 'Commercial Terms',     completed: isComplete('commercialTerms') },
      { id: 'deal-makers',       label: 'Deal Makers & Comments', completed: isComplete('dealMakersComments') },
    ];
  };

  // Sprint 7C: editable recap-name display span -- shared between
  // fuels and Solids headers so the click-to-edit logic only lives
  // in one place.
  const renderRecapNameDisplay = (className) => (
    <HeaderNameEditor
      typedName={customRecapName}
      automaticName={automaticRecapName}
      onTypedNameChange={(name) => {
        setCustomRecapName(name);
        setHasUnsavedChanges(true);
      }}
      emptyLabel={`${recapType} Recap`}
      inputClassName={commodityType === 'fuels' ? 'hms-header-name-input' : 'recap-header-name-input'}
      displayClassName={className}
      iconClassName={commodityType === 'fuels' ? 'hms-header-edit-icon' : 'recap-header-edit-icon'}
    />
  );

  // Option B (Chris, 2026-09-09): an enquiry edit rewrites this recap, and a
  // contract document must never change silently. This is the "and says so"
  // half -- without it the value moves and nobody can tell why, which is
  // exactly what the browser walkthrough caught. Read-only, server-written.
  const renderEnquirySyncNote = () => (
    tradeRecap?.enquiry_sync_note ? (
      <div
        className="recap-enquiry-sync-note"
        data-testid="enquiry-sync-note"
        style={{
          margin: '0 auto 16px', padding: '10px 14px', maxWidth: 1200,
          background: 'var(--hms-info-bg, #EEF4FB)',
          border: '1px solid var(--hms-info-border, #C7DAF0)',
          borderRadius: 6, fontSize: 13,
          color: 'var(--hms-info-text, #234E7D)',
        }}
      >
        {/* Source-neutral lead: the chain is two-way until the invoice is
          * final, so this banner can now be reporting a change that came UP
          * from the delivery. Hard-coding "from the enquiry" would name the
          * wrong screen and send a trader looking in the wrong place — the
          * sentence itself always says where it came from. */}
        <strong>Updated automatically.</strong>{' '}
        {tradeRecap.enquiry_sync_note}
      </div>
    ) : null
  );

  // Why the last automatic save was refused, beside the recap number. One
  // line that stays until a save goes through -- not a pop-up per autosave.
  const renderAutosaveProblem = () => (autosaveProblem ? (
    <span
      className="hms-recap-autosave-problem"
      role="status"
      style={{
        fontSize: '0.75rem',
        fontWeight: 600,
        background: '#fdecea',
        color: '#8a1c1c',
        border: '1px solid #f5c2c0',
        borderRadius: '6px',
        padding: '3px 10px',
        whiteSpace: 'pre-line',
      }}
    >
      Not saved automatically: {autosaveProblem}
    </span>
  ) : null);

  // Sprint 7C: fuels header. Mirrors FuelEnquiryForm's IMT Screen
  // Header markup so the visuals are 1:1 once the .hms-form-screen
  // wrapper is on the page.
  const renderFuelsHeader = () => (
    <div className="hms-screen-header">
      <button
        className="hms-back-circle"
        onClick={handleBackClick}
        title="Back to Trade Operations"
        type="button"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
      </button>

      <div className="hms-header-logo">
        <img src={logo} alt="IMT Bergbau AG" className="hms-header-logo-img" />
        <div className="hms-header-title-content">
          <div className="hms-header-name-row">
            {renderRecapNameDisplay('hms-header-name-display')}
          </div>
          <div className="hms-header-info-row">
            <span className="hms-stage-badge">{recapType} Recap</span>
            {tradeRecap?.fuels_stage && (
              <span className="hms-stage-badge">
                {FUELS_STAGE_LABELS[tradeRecap.fuels_stage] || tradeRecap.fuels_stage}
              </span>
            )}
            {tradeRecap?.transaction_number && (
              <span className="hms-recap-no-badge">
                Recap no. {tradeRecap.transaction_number}
              </span>
            )}
            {tradeRecap?.created_at && (
              <CreatedByLine
                className="hms-header-created-by"
                name={tradeRecap.created_by_name}
                when={tradeRecap.created_at}
              />
            )}
            {renderAutosaveProblem()}
          </div>
        </div>
      </div>

      <div className="hms-header-actions">
        <PinToDeskButton entityType="trade_recap" objectId={isNewRecap ? null : tradeRecap?.id} />
        {/* Bunkers Phase 1 (May 2026): Save Draft + Upload Docs + View
            Delivery all live in the floating action menu rendered
            alongside the page (see ``HMSFloatingActionMenu`` near the end
            of the JSX). The fuels header carries no inline action buttons.
            Bunkers no longer surfaces Bid/Offer in the UI — the legacy
            "View Deal" button is intentionally omitted for fuels. */}
      </div>
    </div>
  );

  // Solids header — ported to the shared `.hms-screen-header` markup
  // so the Cargo recap matches the Fuels recap / Fuel Enquiry visual
  // language (gradient screen, glass back button, white logo, badge
  // strip). All click handlers, navigation targets and conditional
  // button visibility are unchanged.
  const renderSolidsHeader = () => (
    <div className="hms-screen-header">
      <button
        className="hms-back-circle"
        onClick={handleBackClick}
        title="Back to Trade Operations"
        type="button"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
      </button>

      <div className="hms-header-logo">
        <img src={logo} alt="IMT Bergbau AG" className="hms-header-logo-img" />
        <div className="hms-header-title-content">
          <div className="hms-header-name-row">
            {renderRecapNameDisplay('hms-header-name-display')}
          </div>
          <div className="hms-header-info-row">
            <span className="hms-stage-badge">{recapType} Recap</span>
            {tradeRecap?.transaction_number && (
              <span className="hms-recap-no-badge">
                Recap no. {tradeRecap.transaction_number}
              </span>
            )}
            {tradeRecap?.created_at && (
              <CreatedByLine
                className="hms-header-created-by"
                name={tradeRecap.created_by_name}
                when={tradeRecap.created_at}
              />
            )}
            {renderAutosaveProblem()}
          </div>
        </div>
      </div>

      <div className="hms-header-actions">
        <PinToDeskButton entityType="trade_recap" objectId={isNewRecap ? null : tradeRecap?.id} />
        <button
          className="hms-btn hms-btn-secondary"
          onClick={handleSubmit}
          type="button"
        >
          <FontAwesomeIcon icon={faSave} /> Save Draft
        </button>
        {tradeRecap?.associated_delivery_id && (
          <button
            className="hms-btn hms-btn-primary"
            onClick={() => history.push(`/deliveries/${tradeRecap.associated_delivery_id}`)}
            type="button"
          >
            View Delivery
          </button>
        )}
        {tradeRecap?.associated_offer_or_bid && (
          <button
            className="hms-btn hms-btn-success"
            onClick={() => {
              const dealType = tradeRecap.recap_type === 'SALE' ? 'sale' : 'purchase';
              history.push(`/offer-generator?offerId=${tradeRecap.associated_offer_or_bid}&type=existing&dealType=${dealType}`);
            }}
            type="button"
          >
            View Deal
          </button>
        )}
      </div>
    </div>
  );

  // Defer the form render in edit mode while we wait for the recap to
  // arrive, so we don't briefly paint the wrong commodity flavour. The
  // flicker only happens when ``location.state.commodityType`` was NOT
  // passed (e.g. relationship viewer, deep link, copy-pasted URL,
  // notification action_url) -- in that case the initial state seeds
  // to 'solids', then the API response flips it to 'fuels' a few
  // hundred ms later. Skip the gate entirely for create mode and for
  // navigations that did pass location.state, so the existing happy
  // path stays instant.
  const hasLocationStateHint = !!location.state?.commodityType;
  const isEditModeStillLoading = (
    offerId
    && offerId !== 'new'
    && !tradeRecap
    && !hasLocationStateHint
  );
  if (offerId && offerId !== 'new' && recapLoadProblem) {
    return (
      <Layout
        showLeftNavSidebar={true}
        showRightSidebar={false}
        chatHeaderText="HAMSTIR Recap Builder"
        isTradeRecap={true}
      >
        <RecordLoadProblem
          problem={recapLoadProblem}
          recordName="recap"
          areaName="trade recaps"
          backLabel="Back to Trade Ops"
          onBack={() => goToTradeOps(history, tradeOpsDesk, {
            selectedDate: location.state?.selectedDate,
            activeTab: location.state?.activeTab || 'recaps',
          })}
          onRetry={() => setRecapLoadAttempt((n) => n + 1)}
        />
      </Layout>
    );
  }
  if (isEditModeStillLoading) {
    return (
      <Layout
        showLeftNavSidebar={true}
        showRightSidebar={false}
        chatHeaderText="HAMSTIR Recap Builder"
        isTradeRecap={true}
      >
        <div
          className="trade-recap-loading"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            color: '#64748b',
            fontSize: '0.95rem',
          }}
        >
          Loading recap…
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      showLeftNavSidebar={true}
      showRightSidebar={false}
      chatHeaderText="HAMSTIR Recap Builder"
      isTradeRecap={true}
      recapType={recapType}
      persistRecapId={tradeRecap?.id || null}
    >
      <div className="trade-recap hms-form-screen">
        {/* IMT Screen Header
         * Sprint 7C/D: both fuels and solids now use the shared
         * ``.hms-form-screen`` chrome (HMSScreenHeader.css) with the
         * sticky numbered stepper below. The two render helpers are
         * kept separate so the action-button mix can differ (cargo
         * keeps the "View Deal" link to its bid/offer). */}
        {commodityType === 'fuels' ? renderFuelsHeader() : renderSolidsHeader()}

        {/* Sprint 7D: solids stepper mirrors the fuels one — same
         * `<HMSStepper>` component, same scroll-to-anchor behaviour.
         * `buildStepperSections()` decides which set of steps to use
         * based on commodity. */}
        <div className="hms-stepper-bar">
          <HMSStepper
            sections={buildStepperSections()}
            activeId={activeStep}
            onStepClick={(id) => {
              setActiveStep(id);
              const el = document.getElementById(`hms-anchor-${id}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
        </div>

        {/* Below the steps, not above them: the steps are how a trader
         * navigates the recap and must stay the first thing under the title.
         * Rendered here rather than inside the fuels header so the solids
         * recap gets it too — an enquiry edit reaches either. */}
        {renderEnquirySyncNote()}

        <form className="hms-stacked-form" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
          <div className="trade-recap-main-content">
              {commodityType === 'fuels' ? (
                /* Sprint 7C: fuels recap uses the shared Section/Field
                 * wrappers (matches FuelEnquiryForm + FuelOfferGenerator
                 * visuals). renderFuelsLayout returns Deal Details +
                 * FuelsSpecBlock + Contract & Partners + Commercial
                 * Terms + Deal Makers as a single fragment. */
                renderFuelsLayout()
              ) : (
                <>
                  {/* ============================================== */}
                  {/* DEAL DETAILS SECTION (Cargo / Solids)          */}
                  {/* Sprint 7D: ported to shared <Section>/<Field>  */}
                  {/* wrappers so the Cargo recap matches the Fuels  */}
                  {/* recap + Fuel Enquiry visual language. Field    */}
                  {/* names, state keys and handlers are unchanged.  */}
                  {/* ============================================== */}
                  <Section
                    title="Deal Details"
                    stepNumber={1}
                    anchorId="hms-anchor-deal-details"
                    headerAction={renderCompletionIndicator('dealDetails')}
                  >
                    <div className="hms-grid hms-grid-2">
                      <Field label="Entity">
                        <select
                          name="entity"
                          value={formFields.entity || ''}
                          onChange={handleChange}
                        >
                          <option value="">Select Entity</option>
                          {hmsEntities && hmsEntities.length > 0 ? hmsEntities.map((entity, index) => {
                            const entityName = typeof entity === 'object' && 'label' in entity ? entity.label :
                                             (entity.hms_entity_name ? entity.hms_entity_name : entity);
                            return (
                              <option key={index} value={entityName}>
                                {entityName}
                              </option>
                            );
                          }) : <option value="" disabled>No entities available</option>}
                        </select>
                      </Field>

                      <Field label="Origin">
                        <select
                          id="origin"
                          name="origin"
                          value={formFields.origin || ''}
                          onChange={(e) => {
                            setFormFields(prev => ({ ...prev, origin: e.target.value }));
                            setHasUnsavedChanges(true);
                          }}
                        >
                          <option value="">Select origin country</option>
                          {dropdownOptions.countries && dropdownOptions.countries.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <Field label="Product">
                      {hasRecapLines ? (
                        <DealProductLines
                          lines={formFields.products}
                          onChange={applyRecapLines}
                          keys={recapLineKeys(recapType)}
                          unit={formFields[recapType === 'Sale' ? 'sale_contract_quantity_unit' : 'purchase_contract_quantity_unit']}
                          currency={formFields[recapType === 'Sale' ? 'sale_contract_price_currency' : 'purchase_contract_price_currency']}
                        />
                      ) : (
                        <>
                          <RecapProductSearch
                            label=""
                            fieldName="stock_item_variant"
                            displayFieldName="product"
                            value={formFields.product}
                            variantId={formFields.stock_item_variant}
                            onChange={handleRecapProductChange}
                            onCreateProduct={openProductCreatorForSolidsProduct}
                          />
                          <HMSButton variant="ghost" size="compact" className="hms-deal-lines-start" onClick={startRecapLines}>
                            + Add another product
                          </HMSButton>
                        </>
                      )}
                    </Field>

                    <div className="hms-grid hms-grid-2">
                      <Field label="Laycan">
                        <DateRangePicker
                          id="laycan"
                          name="laycan"
                          value={formFields.laycan || ''}
                          onChange={(e) => {
                            handleChange(e);
                            setFormFields(prev => ({
                              ...prev,
                              _laycanManuallySet: true
                            }));
                          }}
                          format="YYYY-MM-DD"
                          placeholder="Select laycan date range"
                          style={{ width: '100%' }}
                        />
                      </Field>

                      <Field label="Laycan Comments">
                        <textarea
                          id="laycan_comments"
                          name="laycan_comments"
                          value={formFields.laycan_comments || ''}
                          onChange={handleChange}
                          placeholder="Enter additional laycan details or comments"
                          rows={3}
                        />
                      </Field>
                    </div>
                  </Section>

                  {/* ============================================== */}
                  {/* CONTRACT DETAILS SECTION (Solids — Sale or Purchase) */}
                  {/* ============================================== */}
                  {renderAdditionalFields()}
                </>
              )}

              {/* Bunkers Phase 1: Tonnage Approval panel removed from
                  the fuels recap surface. The recap is only spawned
                  AFTER an approved tonnage on the enquiry side
                  (``stage_service`` gates the recapped transition on
                  it), so re-rendering the same panel here was
                  duplicative. Solids recaps never had the panel. */}
              {/* Bunkers Financial Summary (Phase 1, May 2026): 8-cell
                  read-only KPI block driven by per-product TradeRecapProduct
                  rows + ThirdPartyCost rollups on the source FuelEnquiry.
                  All values are @property's on TradeRecap (server-side),
                  so the FE never recomputes. Wrapped in <Section> for the
                  shared dark-blue header treatment. Renders nothing for
                  Solids (the panel itself gates on commodityType). */}
              {commodityType === 'fuels' && (
                <Section title="Bunkers Financial Summary" anchorId="hms-anchor-summary">
                  <FuelsFinancialSummaryPanel
                    commodityType={commodityType}
                    recapType={tradeRecap?.recap_type}
                    purchase_third_party_costs={tradeRecap?.purchase_third_party_costs}
                    sale_third_party_costs={tradeRecap?.sale_third_party_costs}
                    product_financials={tradeRecap?.product_financials}
                    total_cos={tradeRecap?.total_cos}
                    total_revenue={tradeRecap?.total_revenue}
                    total_on_charged_cost={tradeRecap?.total_on_charged_cost}
                    gross_profit_margin={tradeRecap?.gross_profit_margin}
                    net_profit={tradeRecap?.net_profit}
                  />
                </Section>
              )}

              {/* Bunkers FUNDING (Phase 1, May 2026): PURCHASE-only block
                  beneath the Financial Summary. Funding Days is derived;
                  Funding Amount + Interest Rate are trader inputs.
                  Interest Expense + Net Profit After Interest are
                  read-only @property's that recompute server-side after
                  save. The panel itself gates on recap_type === 'PURCHASE'
                  so SALE recaps render nothing even though we mount it
                  unconditionally for fuels. */}
              {commodityType === 'fuels' && (tradeRecap?.recap_type === 'PURCHASE') && (
                <Section title="Funding" anchorId="hms-anchor-funding">
                  <FuelsFundingPanel
                    recapType={tradeRecap?.recap_type}
                    commodityType={commodityType}
                    effective_funding_days={tradeRecap?.effective_funding_days}
                    funding_days_override={formFields.funding_days_override}
                    funding_amount={formFields.funding_amount}
                    interest_rate_pct={formFields.interest_rate_pct}
                    net_profit={tradeRecap?.net_profit}
                    onChange={(field, value) => {
                      setFormFields((prev) => ({ ...prev, [field]: value }));
                      setHasUnsavedChanges(true);
                    }}
                  />
                </Section>
              )}
          </div>
        </form>

        {/* Approved outgoing invoices for this deal, viewable as PDF only.
            On SALE recaps only: the invoices reachable here hang off the
            delivery's sales-recap allocations, so a purchase leg has none by
            construction. Gated on recap visibility, which is why a customer
            trader can see it here and nowhere else in Invoicing. */}
        {tradeRecap?.id && (tradeRecap?.recap_type || '').toUpperCase() === 'SALE' && (
          <RecapInvoicesPanel recapId={tradeRecap.id} />
        )}

        {/* Marcel 2026-09-18: the Customer Confirmation and the deal's BDNs
            belong on the sales recap alongside the invoices, so a trader can
            see the whole paper trail of the deal in one place.

            SALE recaps only, following the invoices panel above, and for
            EVERYONE who can open the recap -- not a customer-trader feature.
            Deliberately NOT folded into DealDocumentsPanel: that panel is
            hidden from customer traders by ``hideDealDocsAndDelivery``, and
            putting these inside it would turn a server-side rule back into a
            screen-only one. Each panel carries its own server gate: the recap
            for the BDNs, the enquiry's own queryset for the confirmation. */}
        {tradeRecap?.id && (tradeRecap?.recap_type || '').toUpperCase() === 'SALE'
          && tradeRecap?.source_fuel_enquiry_id && (
          <RecapCustomerConfirmationPanel
            enquiryId={tradeRecap.source_fuel_enquiry_id}
            enquiryDisplayId={tradeRecap.source_fuel_enquiry_display_id}
          />
        )}

        {tradeRecap?.id && (tradeRecap?.recap_type || '').toUpperCase() === 'SALE' && (
          <RecapBDNsPanel recapId={tradeRecap.id} />
        )}

        {/* Solids recap keeps the inline Deal Documents card directly
            below the form (legacy behaviour). The Fuels recap moves it
            into a FAB-triggered slide-out drawer rendered later in the
            tree so the recap mirrors the delivery screen's docs UX. */}
        {commodityType !== 'fuels' && tradeRecap?.id && !hideDealDocsAndDelivery && (
          <DealDocumentsPanel
            tradeRecapId={tradeRecap.id}
            title="Recap Documents"
            employeeId={parseInt(localStorage.getItem('user_id'), 10) || null}
            /* ``effectiveCommodityType``, not the raw state: it prefers
               the recap's own saved commodity, so a recap opened by
               direct link or refresh doesn't fall through to 'solids'
               and hide the Bunkers document types. */
            commodityType={effectiveCommodityType}
          />
        )}

        {/* IMT Footer */}
        <Footer />
      </div>
      {showBackConfirmation && (
        <TradeRecapBackModal
          message="Unsaved recap data will be lost, are you sure you want to go back?"
          onConfirm={handleConfirmBack}
          onCancel={handleCancelBack}
          onDraft={handleDraftAndLeave}
        />
      )}
      
      {/* Hamster Chat Integration */}
      <HamsterFloatingButton />
      
      {/* Back to Top Button */}
      <BackToTopButton />

      {/* Bunkers Phase 1 (May 2026): floating actions menu carrying the
          recap's primary actions (Save Draft + Upload Docs). Mirrors
          the reusable FAB used on LegalContractDetail. Fuels-only --
          Solids recaps keep the legacy header Save Draft button. */}
      {commodityType === 'fuels' && (
        <HMSFloatingActionMenu
          label="Actions"
          items={[
            {
              key: 'save-draft',
              label: 'Save Draft',
              icon: faSave,
              color: 'blue',
              onClick: () => handleSubmit(),
              title: 'Save the current recap state without advancing the workflow',
            },
            {
              key: 'upload-docs',
              label: 'Upload Docs',
              icon: faUpload,
              color: 'green',
              onClick: () => setDocsDrawerOpen(true),
              hidden: hideDealDocsAndDelivery,
              disabled: !tradeRecap?.id,
              title: tradeRecap?.id
                ? 'Attach a document (recap email, contract draft, quality cert, ...) to this recap'
                : 'Save the recap once before uploading documents',
            },
            {
              // Only surfaces once this recap has been assigned to a delivery.
              key: 'view-delivery',
              label: 'View Delivery',
              icon: faTruck,
              color: 'purple',
              hidden: !tradeRecap?.associated_delivery_id || hideDealDocsAndDelivery,
              onClick: () => history.push(`/deliveries/${tradeRecap.associated_delivery_id}`),
              title: 'Open the delivery this recap is assigned to',
            },
          ]}
        />
      )}

      {/* FAB-driven slide-out drawer. Reuses ``.document-panel-overlay``
          + ``.document-panel`` from DocumentPanel.css so the recap
          surface matches the delivery screen's documents drawer
          exactly (same backdrop, slide-in-from-right, gradient header).
          The body is the existing ``DealDocumentsPanel`` which already
          carries the type picker, file input, list, download and
          delete actions for recap docs. */}
      {commodityType === 'fuels' && docsDrawerOpen && tradeRecap?.id && !hideDealDocsAndDelivery && (
        <div
          className="document-panel-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDocsDrawerOpen(false);
          }}
        >
          <div className="document-panel">
            <div className="document-panel-header">
              <h2>Recap Documents</h2>
              <button
                type="button"
                className="close-button"
                onClick={() => setDocsDrawerOpen(false)}
                aria-label="Close panel"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="document-panel-content">
              <DealDocumentsPanel
                tradeRecapId={tradeRecap.id}
                title="Recap Documents"
                employeeId={parseInt(localStorage.getItem('user_id'), 10) || null}
                partnerName={formFields.sale_buyer || formFields.purchase_seller || ''}
                commodityType={effectiveCommodityType}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sprint D — Inline product creator. Trade recap covers both legs;
          we default to purchase_price and let the auto-fill logic populate
          sale fields too if the user enters a sale price. The new variant
          becomes searchable by sale_price for the sale leg too because
          handleRecapProductChange auto-fills both currency/quantity sets. */}
      {productCreatorState.isOpen && (
        <InlineProductCreator
          isOpen={true}
          onClose={closeProductCreator}
          onProductCreated={handleInlineProductCreated}
          initialSearchTerm={productCreatorState.searchTerm}
          initialPriceField="purchase_price"
          initialUnitPrice={formFields.purchase_contract_price_value || formFields.sale_contract_price_value || ''}
          initialCurrency={formFields.purchase_contract_price_currency || formFields.sale_contract_price_currency || 'USD'}
          initialQuantity={formFields.purchase_contract_quantity || formFields.sale_contract_quantity || ''}
          initialCategory={categoryForCreatorTarget(productCreatorState.target)}
        />
      )}

      {/* Inline CRM partner creator — only available while authoring a
          NEW recap (isNewRecap gate). The new profile lands as a Pending
          stub and is surfaced on every RecapPartnerSearch on this draft
          via pendingCreatedPartners until a CRM approver completes the
          full record. */}
      <InlineCrmCreator
        isOpen={crmCreatorState.isOpen}
        onClose={closeCrmCreator}
        onPartnerCreated={handleInlineCrmPartnerCreated}
        initialLegalEntityName={crmCreatorState.searchTerm}
        initialPartnerType={crmCreatorState.role === 'buyer' ? 'Buyer (Debtor)' : 'Seller (Creditor)'}
        title={crmCreatorState.role === 'buyer' ? 'Create Buyer (CRM)' : 'Create Seller (CRM)'}
      />
    </Layout>
  );
}

export default withAuth(TradeRecap);